
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { logoutAsync } from '../store/slices/authSlice';
import { fetchComponentBatches, createComponentBatchAsync } from '../store/slices/componentBatchSlice';
import { fetchFillingBatches, createFillingBatchAsync } from '../store/slices/fillingBatchSlice';
import { fetchDoughBatches, createDoughBatchAsync } from '../store/slices/doughBatchSlice';
import { ingredientTraceCodeApiService } from '../services/ingredientTraceCodeApiService';
import { doughBatchApiService } from '../services/doughBatchApiService';
import { componentBatchApiService } from '../services/componentBatchApiService';
import { fillingBatchApiService } from '../services/fillingBatchApiService';
import { batchRecordApiService } from '../services/batchRecordApiService';
import { batchPanApiService } from '../services/batchPanApiService';
import { batchRecordFillingBatchApiService } from '../services/batchRecordFillingBatchApiService';
import { batchRecordDoughBatchApiService } from '../services/batchRecordDoughBatchApiService';
import bakingProductionLogApiService from '../services/bakingProductionLogApiService';
import { BakingProductionLog } from '../types/bakingProductionLog';
import { BatchRecord } from '../types/batchRecord';
import { IncompletePan } from '../types/batchPan';
import { Recipe, Ingredient } from '../types/recipe';
import { ComponentBatch } from '../types/componentBatch';
import { FillingBatch } from '../types/fillingBatch';
import { ComponentIngredient, LotNumberOption } from '../types/componentRecord';
import { FillingIngredient, FillingComponentBatch as FillingComponentBatchLocal } from '../types/fillingRecord';
import { AvailableComponentBatch } from '../types/fillingBatchComponentBatch';
import { DoughRecipe, DoughRecipeIngredient, CreateDoughBatchRequest, DoughIngredientInput, DoughBatch } from '../types/doughBatch';
import { supplierApiService } from '../services/supplierApiService';
import { ingredientReceivingApiService } from '../services/ingredientReceivingApiService';
import { createIngredientReceivingEntry } from '../store/slices/ingredientReceivingSlice';
import { Supplier } from '../types/supplier';
import { ShipmentEntry, QuantityUnit, TemperatureUnit, PackageSize, StorageType } from '../types/shipment';
import { ingredientPackageSizeApiService } from '../services/ingredientPackageSizeApiService';
import { DeliveryLineItem, EligibleBatch } from '../types/deliveryLog';
import {
  fetchEligibleBatchesAsync,
  bulkCreateDeliveryLogsAsync,
  fetchIncompleteDeliveryGroupsAsync,
  completeDeliveryGroupAsync,
} from '../store/slices/deliveryLogsSlice';
import { BatchIngredientDetailsModal } from '../features/product-tracking/components/BatchIngredientDetailsModal';
import { BatchIngredientWithSupplier } from '../types/batchIngredientWithSupplier';
import temperatureEquipmentApiService from '../services/temperatureEquipmentApiService';
import temperatureLogApiService from '../services/temperatureLogApiService';
import { computePanQuantity, formatPanQuantityDisplay } from '../utils/panQuantity';
import { TemperatureEquipment } from '../types/temperatureEquipment';
import { TemperatureLog } from '../types/temperatureLog';
import sanitationApiService from '../services/sanitationApiService';
import { SanitationEquipment, SanitationRecord, CleaningFrequency } from '../types/sanitationRecord';

// Wizard step type
type WizardStep =
  | 'initial'              // Step 1: Big CREATE NEW BATCH button
  | 'batchType'            // Step 2: COMPONENT, FILLING, DOUGH, or PRODUCT button
  | 'selectComponent'      // Step 3a: Select component type
  | 'selectFilling'        // Step 3b: Select filling type
  | 'selectDough'          // Step 3c: Select dough recipe type
  | 'selectProduct'        // Step 3d: Select product type (for product batches)
  | 'ingredientList'       // Step 4a: Show ingredients as buttons (component)
  | 'fillingIngredientList' // Step 4b: Show ingredients + component slots (filling)
  | 'doughIngredientList'  // Step 4c: Show dough ingredients with quantities
  | 'selectFillingBatches' // Step 4d: Multi-select filling batches for product
  | 'selectDoughBatches'   // Step 5d: Multi-select dough batches for product
  | 'bakingDetails'        // Step 6d: Enter baking details (ovenTemp, startTime) for product
  | 'selectTraceCodes'     // Step 5a: Select trace codes for ingredient
  | 'selectComponentBatch' // Step 5b: Select component batch for filling
  | 'timesProduced'        // Step 6: Input # of times produced
  | 'creating'             // Step 7: Loading state during creation
  | 'summary'              // Step 8: Show summary card
  // Shipment wizard steps
  | 'shipmentSelectSupplier'    // Shipment Step 1: Select supplier
  | 'shipmentSelectIngredient'  // Shipment Step 2: Select ingredient from supplier
  | 'shipmentLotNumber'         // Shipment Step 3: Enter lot number
  | 'shipmentQuantity'          // Shipment Step 4: Enter quantity + unit
  | 'shipmentCondition'         // Shipment Step 5: Select condition
  | 'shipmentTemperature'       // Shipment Step 6: Enter temperature
  | 'shipmentCorrectiveAction'  // Shipment Step 7: Enter corrective action (optional)
  | 'shipmentSaving'            // Shipment saving state
  | 'shipmentSummary'           // Shipment success summary
  // In Progress wizard steps
  | 'inProgressBaking'          // In Progress: Baking step
  | 'inProgressCooling1'        // In Progress: Cooling 1 step
  | 'inProgressCooling2'        // In Progress: Cooling 2 step
  | 'inProgressFreezing'        // In Progress: Freezing step
  | 'inProgressSignOff'         // In Progress: Sign-off step
  | 'inProgressSaving'          // In Progress: Saving state
  // Delivery wizard steps
  | 'deliveryDateTime'          // Delivery Step 1: Date + time
  | 'deliveryCustomer'          // Delivery Step 2: Customer name + address
  | 'deliverySelectProduct'     // Delivery Step 3a: Select product
  | 'deliverySelectBatch'       // Delivery Step 3b: Select batch
  | 'deliveryQuantity'          // Delivery Step 3c: Enter quantity
  | 'deliveryReview'            // Delivery Step 4: Review + save
  | 'deliverySaving'            // Delivery saving state
  // Delivery completion wizard steps
  | 'inProgressDeliveryTemp'    // Delivery completion Step 5a: Temperature
  | 'inProgressDeliveryInitials' // Delivery completion Step 5b: Initials + corrective
  | 'inProgressDeliverySaving' // Delivery completion saving state
  // Temperature Log wizard steps
  | 'tempLogSelectType'
  | 'tempLogSelectEquipment'
  | 'tempLogEnterTemp'
  | 'tempLogSaving'
  | 'tempLogSummary'
  // Sanitation Cleaning wizard steps
  | 'sanitationSelectFrequency'
  | 'sanitationSelectEquipment'
  | 'sanitationReview'
  | 'sanitationSaving'
  | 'sanitationSummary';

// Shipment lot entry for the wizard
interface ShipmentLotEntry {
  id: string;
  lotNumber: string;
  quantity: number;
  unit: QuantityUnit;
  conditionUponArrival: string;
  receivingTemperature: number;
  temperatureUnit: TemperatureUnit;
  correctiveAction: string;
  // Package size fields
  packageSizeId: string | null;
  packageCount: number;
  manualPackageSize: number | null;
  manualPackageUnit: string | null;
  manualPackageLabel: string;
}

// Wizard state interface
interface WizardState {
  step: WizardStep;
  batchType: 'component' | 'filling' | 'dough' | 'product' | null;
  // Component batch state
  selectedComponent: Recipe | null;
  selectedIngredient: Ingredient | null;
  ingredientTraceCodes: Map<string, string[]>;
  availableTraceCodes: LotNumberOption[];
  loadingTraceCodes: boolean;
  timesProduced: number;
  createdBatch: ComponentBatch | FillingBatch | DoughBatch | BatchRecord | null;
  error: string | null;
  // Filling batch state
  selectedFilling: Recipe | null;
  selectedFillingIngredient: Ingredient | null;
  selectedComponentSlot: Ingredient | null;
  fillingIngredientTraceCodes: Map<string, string[]>;
  fillingComponentBatches: Map<string, string>;
  availableComponentBatchesForSlot: AvailableComponentBatch[];
  loadingComponentBatches: boolean;
  // Dough batch state
  selectedDoughRecipe: DoughRecipe | null;
  doughRecipeIngredients: DoughRecipeIngredient[];
  doughIngredientTraceCodes: Map<string, string[]>;
  selectedDoughIngredient: DoughRecipeIngredient | null;
  // Product batch state
  selectedProduct: Recipe | null;
  selectedFillingBatchIds: Set<string>;
  selectedDoughBatchIds: Set<string>;
  // Baking details for product batch
  ovenTemp: number;
  startTime: string;
  // Shipment wizard state
  shipmentSuppliers: Supplier[];
  shipmentLoadingSuppliers: boolean;
  shipmentSelectedSupplier: Supplier | null;
  shipmentSelectedIngredient: string | null;
  shipmentIngredientSearch: string;
  shipmentEntries: Map<string, Map<string, ShipmentLotEntry[]>>; // Map<supplierId, Map<ingredientName, lots>>
  shipmentCurrentLot: ShipmentLotEntry;
  shipmentSavedCount: number;
  // Package size state
  shipmentAvailablePackageSizes: PackageSize[];
  shipmentLoadingPackageSizes: boolean;
  shipmentShowNewPackageForm: boolean;
  // Storage type tracking state
  shipmentIngredientStorageType: StorageType;
  shipmentSupplierIngredientId: string | null;
  shipmentFrozenSolidConfirmed: boolean | null;
  shipmentCorrectiveActionRequired: boolean;
  // In Progress wizard state
  inProgressBatchId: string | null;
  inProgressBatch: BatchRecord | null;
  inProgressPanId: string | null;
  inProgressPan: IncompletePan | null;
  inProgressStep: 'baking' | 'cooling1' | 'cooling2' | 'freezing' | null;
  processingActualTemp: number | null;
  processingActualTimeHours: number | null;
  processingActualTimeMinutes: number | null;
  processingCcpAchieved: boolean | null;
  processingCorrectiveAction: string;
  processingFinishedDozens: number | null;
  processingFinishedIndividualBuns: number | null;
  signOffProductionName: string;
  signOffProductionDate: string;
  signOffSupervisorName: string;
  signOffSupervisorDate: string;
  // Delivery wizard state
  deliveryDate: string;
  deliveryTime: string;
  deliveryCustomerName: string;
  deliveryCustomerAddress: string;
  deliveryLineItems: DeliveryLineItem[];
  deliverySelectedProduct: Recipe | null;
  deliveryEligibleBatches: EligibleBatch[];
  deliveryLoadingBatches: boolean;
  deliverySelectedBatchId: string | null;
  deliveryQuantity: number;
  // Delivery completion wizard state
  inProgressDeliveryGroupId: string | null;
  inProgressDeliveryCustomer: string;
  deliveryTransportationTemp: number | null;
  deliveryTemperatureUnit: TemperatureUnit;
  deliveryInitials: string;
  deliveryCorrectiveAction: string;
  // Temperature Log wizard state
  tempLogEquipmentType: 'freezer' | 'oven' | 'fridge' | null;
  tempLogEquipmentList: TemperatureEquipment[];
  tempLogLoadingEquipment: boolean;
  tempLogSelectedEquipment: TemperatureEquipment | null;
  tempLogActualTemp: number | null;
  tempLogCorrectiveAction: string;
  tempLogCreatedLog: TemperatureLog | null;
  // Sanitation Cleaning wizard state
  sanitationFrequency: CleaningFrequency | null;
  sanitationEquipmentList: SanitationEquipment[];
  sanitationLoadingEquipment: boolean;
  sanitationSelectedEquipmentIds: Set<string>;
  sanitationCreatedRecords: SanitationRecord[];
  sanitationError: string | null;
}

// Initial wizard state
const initialWizardState: WizardState = {
  step: 'initial',
  batchType: null,
  // Component batch state
  selectedComponent: null,
  selectedIngredient: null,
  ingredientTraceCodes: new Map(),
  availableTraceCodes: [],
  loadingTraceCodes: false,
  timesProduced: 1,
  createdBatch: null,
  error: null,
  // Filling batch state
  selectedFilling: null,
  selectedFillingIngredient: null,
  selectedComponentSlot: null,
  fillingIngredientTraceCodes: new Map(),
  fillingComponentBatches: new Map(),
  availableComponentBatchesForSlot: [],
  loadingComponentBatches: false,
  // Dough batch state
  selectedDoughRecipe: null,
  doughRecipeIngredients: [],
  doughIngredientTraceCodes: new Map(),
  selectedDoughIngredient: null,
  // Product batch state
  selectedProduct: null,
  selectedFillingBatchIds: new Set(),
  selectedDoughBatchIds: new Set(),
  // Baking details for product batch
  ovenTemp: 350,
  startTime: '',
  // Shipment wizard state
  shipmentSuppliers: [],
  shipmentLoadingSuppliers: false,
  shipmentSelectedSupplier: null,
  shipmentSelectedIngredient: null,
  shipmentIngredientSearch: '',
  shipmentEntries: new Map(),
  shipmentCurrentLot: {
    id: '',
    lotNumber: '',
    quantity: 0,
    unit: 'lbs' as QuantityUnit,
    conditionUponArrival: 'Good',
    receivingTemperature: 40,
    temperatureUnit: 'F' as TemperatureUnit,
    correctiveAction: '',
    packageSizeId: null,
    packageCount: 1,
    manualPackageSize: null,
    manualPackageUnit: null,
    manualPackageLabel: ''
  },
  shipmentSavedCount: 0,
  // Package size state
  shipmentAvailablePackageSizes: [],
  shipmentLoadingPackageSizes: false,
  shipmentShowNewPackageForm: false,
  // Storage type tracking state
  shipmentIngredientStorageType: 'non_refrigerated',
  shipmentSupplierIngredientId: null,
  shipmentFrozenSolidConfirmed: null,
  shipmentCorrectiveActionRequired: false,
  // In Progress wizard state
  inProgressBatchId: null,
  inProgressBatch: null,
  inProgressPanId: null,
  inProgressPan: null,
  inProgressStep: null,
  processingActualTemp: null,
  processingActualTimeHours: null,
  processingActualTimeMinutes: null,
  processingCcpAchieved: null,
  processingCorrectiveAction: '',
  processingFinishedDozens: null,
  processingFinishedIndividualBuns: null,
  signOffProductionName: '',
  signOffProductionDate: '',
  signOffSupervisorName: '',
  signOffSupervisorDate: '',
  // Delivery wizard state
  deliveryDate: '',
  deliveryTime: '',
  deliveryCustomerName: '',
  deliveryCustomerAddress: '',
  deliveryLineItems: [],
  deliverySelectedProduct: null,
  deliveryEligibleBatches: [],
  deliveryLoadingBatches: false,
  deliverySelectedBatchId: null,
  deliveryQuantity: 0,
  // Delivery completion wizard state
  inProgressDeliveryGroupId: null,
  inProgressDeliveryCustomer: '',
  deliveryTransportationTemp: null,
  deliveryTemperatureUnit: 'F' as TemperatureUnit,
  deliveryInitials: '',
  deliveryCorrectiveAction: '',
  // Temperature Log wizard state
  tempLogEquipmentType: null,
  tempLogEquipmentList: [],
  tempLogLoadingEquipment: false,
  tempLogSelectedEquipment: null,
  tempLogActualTemp: null,
  tempLogCorrectiveAction: '',
  tempLogCreatedLog: null,
  // Sanitation Cleaning wizard state
  sanitationFrequency: null,
  sanitationEquipmentList: [],
  sanitationLoadingEquipment: false,
  sanitationSelectedEquipmentIds: new Set(),
  sanitationCreatedRecords: [],
  sanitationError: null
};

// Convert total minutes to { hours, minutes }
const minutesToHrMin = (totalMinutes: number | null | undefined) => ({
  hours: totalMinutes != null ? Math.floor(totalMinutes / 60) : null,
  minutes: totalMinutes != null ? totalMinutes % 60 : null,
});

// Convert hours + minutes to total minutes (returns null if both are null)
const hrMinToMinutes = (hours: number | null, minutes: number | null): number | null => {
  if (hours == null && minutes == null) return null;
  return (hours || 0) * 60 + (minutes || 0);
};

export const Workflow: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // Redux state
  const components = useAppSelector(state => state.recipes.components);
  const fillings = useAppSelector(state => state.recipes.fillings);
  const componentBatches = useAppSelector(state => state.componentBatches.items);
  const componentBatchesLoading = useAppSelector(state => state.componentBatches.loading);
  const fillingBatches = useAppSelector(state => state.fillingBatches.items);
  const fillingBatchesLoading = useAppSelector(state => state.fillingBatches.loading);
  const doughBatches = useAppSelector(state => state.doughBatches.items);
  const doughBatchesLoading = useAppSelector(state => state.doughBatches.loading);
  const currentUser = useAppSelector(state => state.auth.user);
  const incompleteDeliveryGroups = useAppSelector(state => state.deliveryLogs.incompleteDeliveryGroups);

  // Wizard state
  const [wizardState, setWizardState] = useState<WizardState>(initialWizardState);
  const wizardContainerRef = useRef<HTMLDivElement>(null);

  // Local state for dough recipes
  const [doughRecipes, setDoughRecipes] = useState<DoughRecipe[]>([]);

  // Local state for batch records (products)
  const [batchRecords, setBatchRecords] = useState<BatchRecord[]>([]);
  const [batchRecordsLoading, setBatchRecordsLoading] = useState(false);

  // Local state for incomplete pans (In Progress section)
  const [incompletePans, setIncompletePans] = useState<IncompletePan[]>([]);
  const [incompletePansLoading, setIncompletePansLoading] = useState(false);

  // Pan delete confirmation state
  const [panToDelete, setPanToDelete] = useState<IncompletePan | null>(null);

  // Collapsible state for recent batches sections (default collapsed)
  const [componentBatchesExpanded, setComponentBatchesExpanded] = useState(false);
  const [fillingBatchesExpanded, setFillingBatchesExpanded] = useState(false);
  const [doughBatchesExpanded, setDoughBatchesExpanded] = useState(false);
  const [productBatchesExpanded, setProductBatchesExpanded] = useState(false);
  // In Progress section (default expanded)
  const [inProgressExpanded, setInProgressExpanded] = useState(true);
  // In Progress Deliveries section (default expanded)
  const [inProgressDeliveriesExpanded, setInProgressDeliveriesExpanded] = useState(true);

  // Batch ingredient details modal state
  const [ingredientModalOpen, setIngredientModalOpen] = useState(false);
  const [selectedBatchForModal, setSelectedBatchForModal] = useState<{
    type: 'component' | 'filling' | 'dough' | 'product';
    id: string;
    productName: string;
    batchLotNumber: string;
    dateOfProduction: string;
  } | null>(null);
  const [modalIngredients, setModalIngredients] = useState<BatchIngredientWithSupplier[]>([]);
  const [modalLoading, setModalLoading] = useState(false);

  // Logout handler for team users (no sidebar)
  const handleLogout = async () => {
    try {
      await dispatch(logoutAsync()).unwrap();
      navigate('/login');
    } catch (error) {
      // Error handled by toast in authSlice
    }
  };

  // Fetch component, filling, and dough batches on mount
  useEffect(() => {
    dispatch(fetchComponentBatches());
    dispatch(fetchFillingBatches());
    dispatch(fetchDoughBatches());
    dispatch(fetchIncompleteDeliveryGroupsAsync());

    // Fetch dough recipes
    const loadDoughRecipes = async () => {
      try {
        const recipes = await doughBatchApiService.fetchDoughRecipes();
        setDoughRecipes(recipes);
      } catch (error) {
        console.error('Error fetching dough recipes:', error);
      }
    };
    loadDoughRecipes();

    // Fetch batch records (products)
    const loadBatchRecords = async () => {
      setBatchRecordsLoading(true);
      try {
        const records = await batchRecordApiService.fetchBatchRecords();
        setBatchRecords(records);
      } catch (error) {
        console.error('Error fetching batch records:', error);
      } finally {
        setBatchRecordsLoading(false);
      }
    };
    loadBatchRecords();

    // Fetch incomplete pans (for In Progress section)
    const loadIncompletePans = async () => {
      setIncompletePansLoading(true);
      try {
        const pans = await batchPanApiService.fetchIncompletePans();
        setIncompletePans(pans);
      } catch (error) {
        console.error('Error fetching incomplete pans:', error);
      } finally {
        setIncompletePansLoading(false);
      }
    };
    loadIncompletePans();
  }, [dispatch]);

  // Add CSS animation for spinner on mount
  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'workflow-spinner-style';
    style.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    if (!document.getElementById('workflow-spinner-style')) {
      document.head.appendChild(style);
    }
    return () => {
      const existing = document.getElementById('workflow-spinner-style');
      if (existing) {
        document.head.removeChild(existing);
      }
    };
  }, []);

  // Auto-scroll wizard container into view on step changes
  useEffect(() => {
    if (wizardState.step !== 'initial' && wizardContainerRef.current) {
      wizardContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [wizardState.step]);

  // Auto-populate startTime when entering bakingDetails step
  useEffect(() => {
    if (wizardState.step === 'bakingDetails' && wizardState.startTime === '') {
      // Format: YYYY-MM-DDTHH:mm (required by datetime-local input)
      // Use local time components, not UTC (toISOString returns UTC)
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const formatted = `${year}-${month}-${day}T${hours}:${minutes}`;
      setWizardState(prev => ({ ...prev, startTime: formatted }));
    }
  }, [wizardState.step]);

  // Load package sizes when entering shipment quantity step
  useEffect(() => {
    if (wizardState.step !== 'shipmentQuantity') return;

    const loadPackageSizes = async () => {
      if (!wizardState.shipmentSelectedSupplier || !wizardState.shipmentSelectedIngredient) return;

      setWizardState(prev => ({ ...prev, shipmentLoadingPackageSizes: true }));
      try {
        const { packageSizes, supplierIngredient } = await ingredientPackageSizeApiService.getPackageSizesForIngredient(
          wizardState.shipmentSelectedSupplier!.id,
          wizardState.shipmentSelectedIngredient!
        );
        setWizardState(prev => ({
          ...prev,
          shipmentAvailablePackageSizes: packageSizes,
          shipmentLoadingPackageSizes: false,
          shipmentShowNewPackageForm: packageSizes.length === 0,
          shipmentIngredientStorageType: supplierIngredient.storageType || 'non_refrigerated',
          shipmentSupplierIngredientId: supplierIngredient.id
        }));
      } catch (error) {
        console.error('Error loading package sizes:', error);
        setWizardState(prev => ({
          ...prev,
          shipmentLoadingPackageSizes: false,
          shipmentShowNewPackageForm: true
        }));
      }
    };

    loadPackageSizes();
  }, [wizardState.step, wizardState.shipmentSelectedSupplier?.id, wizardState.shipmentSelectedIngredient]);

  // Reset wizard to initial state
  const resetWizard = () => {
    setWizardState(initialWizardState);
  };

  // ========== IN PROGRESS HELPER FUNCTIONS ==========

  /**
   * Determines if a batch record is incomplete (needs processing/sign-off)
   * A batch is incomplete if productionStaffName is null/empty
   */
  const isIncompleteBatch = (batch: BatchRecord): boolean => {
    return !batch.productionStaffName || batch.productionStaffName.trim() === '';
  };

  /**
   * Gets the current processing step from a pan's stage
   */
  const getCurrentStepForPan = (pan: IncompletePan): 'baking' | 'cooling1' | 'cooling2' | 'freezing' => {
    if (pan.stage === 'baking') return 'baking';
    if (pan.stage === 'cooling1') return 'cooling1';
    if (pan.stage === 'cooling2') return 'cooling2';
    return 'freezing';
  };

  /**
   * Gets display label for current step
   */
  const getStepLabel = (step: 'baking' | 'cooling1' | 'cooling2' | 'freezing'): string => {
    switch (step) {
      case 'baking': return 'Baking';
      case 'cooling1': return 'Cooling 1';
      case 'cooling2': return 'Cooling 2';
      case 'freezing': return 'Freezing';
    }
  };

  /**
   * Gets badge background color for current step
   */
  const getStepBadgeColor = (step: 'baking' | 'cooling1' | 'cooling2' | 'freezing'): string => {
    switch (step) {
      case 'baking': return '#FF9800';   // Orange
      case 'cooling1': return '#FFEB3B'; // Yellow
      case 'cooling2': return '#FFF176'; // Light yellow
      case 'freezing': return '#4FC3F7'; // Icy blue
      default: return '#9C27B0';
    }
  };

  /**
   * Gets badge text color for current step (yellow needs dark text for contrast)
   */
  const getStepBadgeTextColor = (step: 'baking' | 'cooling1' | 'cooling2' | 'freezing'): string => {
    return (step === 'cooling1' || step === 'cooling2') ? '#333' : 'white';
  };

  // ========== IN PROGRESS WIZARD HANDLERS ==========

  /**
   * Starts the in-progress wizard for a specific pan
   */
  const handleStartInProgressWizardForPan = (pan: IncompletePan) => {
    const currentStep = getCurrentStepForPan(pan);
    const stepMapping: Record<string, WizardStep> = {
      'baking': 'inProgressBaking',
      'cooling1': 'inProgressCooling1',
      'cooling2': 'inProgressCooling2',
      'freezing': 'inProgressFreezing'
    };

    // Get existing values based on current step
    const getExistingValues = () => {
      if (currentStep === 'baking') {
        return {
          processingActualTemp: pan.bakingActualTemp ?? null,
          processingActualTimeHours: minutesToHrMin(pan.bakingActualTime).hours,
          processingActualTimeMinutes: minutesToHrMin(pan.bakingActualTime).minutes,
          processingCcpAchieved: pan.bakingCcpAchieved ?? null,
          processingCorrectiveAction: pan.bakingCorrectiveAction || '',
          processingFinishedDozens: pan.finishedDozens ?? null,
          processingFinishedIndividualBuns: pan.finishedIndividualBuns ?? null
        };
      } else if (currentStep === 'cooling1') {
        return {
          processingActualTemp: pan.cooling1ActualTemp ?? null,
          processingActualTimeHours: minutesToHrMin(pan.cooling1ActualTime).hours,
          processingActualTimeMinutes: minutesToHrMin(pan.cooling1ActualTime).minutes,
          processingCcpAchieved: pan.cooling1CcpAchieved ?? null,
          processingCorrectiveAction: pan.cooling1CorrectiveAction || '',
          processingFinishedDozens: null,
          processingFinishedIndividualBuns: null
        };
      } else if (currentStep === 'cooling2') {
        return {
          processingActualTemp: pan.cooling2ActualTemp ?? null,
          processingActualTimeHours: minutesToHrMin(pan.cooling2ActualTime).hours,
          processingActualTimeMinutes: minutesToHrMin(pan.cooling2ActualTime).minutes,
          processingCcpAchieved: pan.cooling2CcpAchieved ?? null,
          processingCorrectiveAction: pan.cooling2CorrectiveAction || '',
          processingFinishedDozens: null,
          processingFinishedIndividualBuns: null
        };
      } else if (currentStep === 'freezing') {
        return {
          processingActualTemp: pan.freezingActualTemp ?? null,
          processingActualTimeHours: minutesToHrMin(pan.freezingActualTime).hours,
          processingActualTimeMinutes: minutesToHrMin(pan.freezingActualTime).minutes,
          processingCcpAchieved: pan.freezingCcpAchieved ?? null,
          processingCorrectiveAction: pan.freezingCorrectiveAction || '',
          processingFinishedDozens: null,
          processingFinishedIndividualBuns: null
        };
      }
      return {
        processingActualTemp: null,
        processingActualTimeHours: null,
        processingActualTimeMinutes: null,
        processingCcpAchieved: null,
        processingCorrectiveAction: '',
        processingFinishedDozens: null,
        processingFinishedIndividualBuns: null
      };
    };

    const existingValues = getExistingValues();

    setWizardState(prev => ({
      ...prev,
      step: stepMapping[currentStep],
      inProgressBatchId: pan.batchRecordId,
      inProgressBatch: null,
      inProgressPanId: pan.id,
      inProgressPan: pan,
      inProgressStep: currentStep,
      processingActualTemp: existingValues.processingActualTemp,
      processingActualTimeHours: existingValues.processingActualTimeHours,
      processingActualTimeMinutes: existingValues.processingActualTimeMinutes,
      processingCcpAchieved: existingValues.processingCcpAchieved,
      processingCorrectiveAction: existingValues.processingCorrectiveAction,
      processingFinishedDozens: existingValues.processingFinishedDozens,
      processingFinishedIndividualBuns: existingValues.processingFinishedIndividualBuns
    }));
  };

  /**
   * Creates a new pan for a batch record (Add Another Pan)
   */
  const handleAddAnotherPan = async (batchRecordId: string) => {
    try {
      await batchPanApiService.createPan(batchRecordId);
      const updatedPans = await batchPanApiService.fetchIncompletePans();
      setIncompletePans(updatedPans);
    } catch (error) {
      console.error('Error creating new pan:', error);
    }
  };

  /**
   * Deletes a pan after confirmation
   */
  const handleDeletePan = async () => {
    if (!panToDelete) return;
    try {
      await batchPanApiService.deletePan(panToDelete.id);
      const updatedPans = await batchPanApiService.fetchIncompletePans();
      setIncompletePans(updatedPans);
    } catch (error: any) {
      const message = error?.message || '';
      if (message.includes('only pan')) {
        alert('Cannot delete the only pan for this batch');
      } else {
        console.error('Error deleting pan:', error);
      }
    } finally {
      setPanToDelete(null);
    }
  };

  /**
   * Saves current processing step and advances to next step (saves to pan)
   */
  const handleSaveProcessingStep = async () => {
    if (!wizardState.inProgressPan || !wizardState.inProgressStep || !wizardState.inProgressPanId) return;

    const pan = wizardState.inProgressPan;
    const step = wizardState.inProgressStep;

    // Build pan update data
    const panUpdate: Record<string, unknown> = {};

    if (step === 'baking') {
      panUpdate.bakingActualTemp = wizardState.processingActualTemp;
      panUpdate.bakingActualTime = hrMinToMinutes(wizardState.processingActualTimeHours, wizardState.processingActualTimeMinutes);
      panUpdate.bakingCcpAchieved = wizardState.processingCcpAchieved;
      panUpdate.bakingCorrectiveAction = wizardState.processingCorrectiveAction || null;
      panUpdate.bakingCheckedBy = currentUser?.name || null;
      panUpdate.finishedDozens = wizardState.processingFinishedDozens;
      panUpdate.finishedIndividualBuns = wizardState.processingFinishedIndividualBuns;
      panUpdate.stage = 'cooling1';
    } else if (step === 'cooling1') {
      panUpdate.cooling1ActualTemp = wizardState.processingActualTemp;
      panUpdate.cooling1ActualTime = hrMinToMinutes(wizardState.processingActualTimeHours, wizardState.processingActualTimeMinutes);
      panUpdate.cooling1CcpAchieved = wizardState.processingCcpAchieved;
      panUpdate.cooling1CorrectiveAction = wizardState.processingCorrectiveAction || null;
      panUpdate.cooling1CheckedBy = currentUser?.name || null;
      panUpdate.stage = 'cooling2';
    } else if (step === 'cooling2') {
      panUpdate.cooling2ActualTemp = wizardState.processingActualTemp;
      panUpdate.cooling2ActualTime = hrMinToMinutes(wizardState.processingActualTimeHours, wizardState.processingActualTimeMinutes);
      panUpdate.cooling2CcpAchieved = wizardState.processingCcpAchieved;
      panUpdate.cooling2CorrectiveAction = wizardState.processingCorrectiveAction || null;
      panUpdate.cooling2CheckedBy = currentUser?.name || null;
      panUpdate.stage = 'freezing';
    } else if (step === 'freezing') {
      panUpdate.freezingActualTemp = wizardState.processingActualTemp;
      panUpdate.freezingActualTime = hrMinToMinutes(wizardState.processingActualTimeHours, wizardState.processingActualTimeMinutes);
      panUpdate.freezingCcpAchieved = wizardState.processingCcpAchieved;
      panUpdate.freezingCorrectiveAction = wizardState.processingCorrectiveAction || null;
      panUpdate.freezingCheckedBy = currentUser?.name || null;
      panUpdate.stage = 'complete';
    }

    setWizardState(prev => ({ ...prev, step: 'inProgressSaving' }));

    try {
      // Save to pan in database
      await batchPanApiService.updatePan(wizardState.inProgressPanId, panUpdate);

      // Refresh incomplete pans
      const updatedPans = await batchPanApiService.fetchIncompletePans();
      setIncompletePans(updatedPans);

      // If pan is now complete (freezing was saved), return to main view
      if (step === 'freezing') {
        resetWizard();
        return;
      }

      // Find the updated pan and advance to next step
      const refreshedPan = updatedPans.find(p => p.id === pan.id);
      if (refreshedPan) {
        const nextStep = getCurrentStepForPan(refreshedPan);
        const stepMapping: Record<string, WizardStep> = {
          'baking': 'inProgressBaking',
          'cooling1': 'inProgressCooling1',
          'cooling2': 'inProgressCooling2',
          'freezing': 'inProgressFreezing'
        };

        setWizardState(prev => ({
          ...prev,
          step: stepMapping[nextStep],
          inProgressPan: refreshedPan,
          inProgressStep: nextStep,
          processingActualTemp: null,
          processingActualTimeHours: null,
          processingActualTimeMinutes: null,
          processingCcpAchieved: null,
          processingCorrectiveAction: '',
          processingFinishedDozens: null,
          processingFinishedIndividualBuns: null
        }));
      } else {
        // Pan no longer incomplete (shouldn't happen for baking/cooling saves)
        resetWizard();
      }
    } catch (error) {
      console.error('Error saving processing step:', error);
      const stepMapping: Record<string, WizardStep> = {
        'baking': 'inProgressBaking',
        'cooling1': 'inProgressCooling1',
        'cooling2': 'inProgressCooling2',
        'freezing': 'inProgressFreezing'
      };
      setWizardState(prev => ({
        ...prev,
        step: stepMapping[prev.inProgressStep || 'baking'],
        error: 'Failed to save. Please try again.'
      }));
    }
  };

  /**
   * Saves current processing step and exits wizard without advancing (saves to pan)
   */
  const handleSaveAndExit = async () => {
    if (!wizardState.inProgressPan || !wizardState.inProgressStep || !wizardState.inProgressPanId) return;

    const step = wizardState.inProgressStep;

    // Build pan update data (save current fields but don't advance stage)
    const panUpdate: Record<string, unknown> = {};

    if (step === 'baking') {
      panUpdate.bakingActualTemp = wizardState.processingActualTemp;
      panUpdate.bakingActualTime = hrMinToMinutes(wizardState.processingActualTimeHours, wizardState.processingActualTimeMinutes);
      panUpdate.bakingCcpAchieved = wizardState.processingCcpAchieved;
      panUpdate.bakingCorrectiveAction = wizardState.processingCorrectiveAction || null;
      panUpdate.bakingCheckedBy = currentUser?.name || null;
      panUpdate.finishedDozens = wizardState.processingFinishedDozens;
      panUpdate.finishedIndividualBuns = wizardState.processingFinishedIndividualBuns;
    } else if (step === 'cooling1') {
      panUpdate.cooling1ActualTemp = wizardState.processingActualTemp;
      panUpdate.cooling1ActualTime = hrMinToMinutes(wizardState.processingActualTimeHours, wizardState.processingActualTimeMinutes);
      panUpdate.cooling1CcpAchieved = wizardState.processingCcpAchieved;
      panUpdate.cooling1CorrectiveAction = wizardState.processingCorrectiveAction || null;
      panUpdate.cooling1CheckedBy = currentUser?.name || null;
    } else if (step === 'cooling2') {
      panUpdate.cooling2ActualTemp = wizardState.processingActualTemp;
      panUpdate.cooling2ActualTime = hrMinToMinutes(wizardState.processingActualTimeHours, wizardState.processingActualTimeMinutes);
      panUpdate.cooling2CcpAchieved = wizardState.processingCcpAchieved;
      panUpdate.cooling2CorrectiveAction = wizardState.processingCorrectiveAction || null;
      panUpdate.cooling2CheckedBy = currentUser?.name || null;
    } else if (step === 'freezing') {
      panUpdate.freezingActualTemp = wizardState.processingActualTemp;
      panUpdate.freezingActualTime = hrMinToMinutes(wizardState.processingActualTimeHours, wizardState.processingActualTimeMinutes);
      panUpdate.freezingCcpAchieved = wizardState.processingCcpAchieved;
      panUpdate.freezingCorrectiveAction = wizardState.processingCorrectiveAction || null;
      panUpdate.freezingCheckedBy = currentUser?.name || null;
    }

    setWizardState(prev => ({ ...prev, step: 'inProgressSaving' }));

    try {
      // Save to pan in database
      await batchPanApiService.updatePan(wizardState.inProgressPanId, panUpdate);

      // Refresh incomplete pans
      const updatedPans = await batchPanApiService.fetchIncompletePans();
      setIncompletePans(updatedPans);

      // Reset wizard state to return to main view
      resetWizard();
    } catch (error) {
      console.error('Error saving and exiting:', error);
      const stepMapping: Record<string, WizardStep> = {
        'baking': 'inProgressBaking',
        'cooling1': 'inProgressCooling1',
        'cooling2': 'inProgressCooling2',
        'freezing': 'inProgressFreezing'
      };
      setWizardState(prev => ({
        ...prev,
        step: stepMapping[prev.inProgressStep || 'baking'],
        error: 'Failed to save. Please try again.'
      }));
    }
  };

  /**
   * Saves sign-off and completes the batch
   */
  const handleSaveSignOff = async () => {
    if (!wizardState.inProgressBatch) return;

    const batch = wizardState.inProgressBatch;

    const updatedBatch: BatchRecord = {
      ...batch,
      productionStaffName: wizardState.signOffProductionName || undefined,
      productionStaffDate: wizardState.signOffProductionDate || undefined,
      supervisorQaName: wizardState.signOffSupervisorName || undefined,
      supervisorQaDate: wizardState.signOffSupervisorDate || undefined
    };

    setWizardState(prev => ({ ...prev, step: 'inProgressSaving' }));

    try {
      await batchRecordApiService.updateBatchRecord(updatedBatch);

      // Refresh batch records
      const records = await batchRecordApiService.fetchBatchRecords();
      setBatchRecords(records);

      // Reset wizard to initial state
      resetWizard();
    } catch (error) {
      console.error('Error saving sign-off:', error);
      setWizardState(prev => ({
        ...prev,
        step: 'inProgressSignOff',
        error: 'Failed to save sign-off. Please try again.'
      }));
    }
  };

  /**
   * Exits the in-progress wizard without saving partial data
   */
  const handleExitInProgressWizard = () => {
    resetWizard();
  };

  /**
   * Back navigation for in-progress wizard (uses pan data)
   */
  const handleInProgressBack = () => {
    const step = wizardState.inProgressStep;
    const pan = wizardState.inProgressPan;

    if (!pan) {
      resetWizard();
      return;
    }

    // Determine if we can go back based on what's already filled in the pan
    if (step === 'cooling1' && pan.bakingActualTemp !== undefined && pan.bakingActualTemp !== null) {
      setWizardState(prev => ({
        ...prev,
        step: 'inProgressBaking',
        inProgressStep: 'baking',
        processingActualTemp: pan.bakingActualTemp ?? null,
        processingActualTimeHours: minutesToHrMin(pan.bakingActualTime).hours,
        processingActualTimeMinutes: minutesToHrMin(pan.bakingActualTime).minutes,
        processingCcpAchieved: pan.bakingCcpAchieved ?? null,
        processingCorrectiveAction: pan.bakingCorrectiveAction || '',
        processingFinishedDozens: pan.finishedDozens ?? null,
        processingFinishedIndividualBuns: pan.finishedIndividualBuns ?? null
      }));
    } else if (step === 'cooling2' && pan.cooling1ActualTemp !== undefined && pan.cooling1ActualTemp !== null) {
      setWizardState(prev => ({
        ...prev,
        step: 'inProgressCooling1',
        inProgressStep: 'cooling1',
        processingActualTemp: pan.cooling1ActualTemp ?? null,
        processingActualTimeHours: minutesToHrMin(pan.cooling1ActualTime).hours,
        processingActualTimeMinutes: minutesToHrMin(pan.cooling1ActualTime).minutes,
        processingCcpAchieved: pan.cooling1CcpAchieved ?? null,
        processingCorrectiveAction: pan.cooling1CorrectiveAction || ''
      }));
    } else if (step === 'freezing' && pan.cooling2ActualTemp !== undefined && pan.cooling2ActualTemp !== null) {
      setWizardState(prev => ({
        ...prev,
        step: 'inProgressCooling2',
        inProgressStep: 'cooling2',
        processingActualTemp: pan.cooling2ActualTemp ?? null,
        processingActualTimeHours: minutesToHrMin(pan.cooling2ActualTime).hours,
        processingActualTimeMinutes: minutesToHrMin(pan.cooling2ActualTime).minutes,
        processingCcpAchieved: pan.cooling2CcpAchieved ?? null,
        processingCorrectiveAction: pan.cooling2CorrectiveAction || ''
      }));
    } else {
      // Exit wizard if at first applicable step
      resetWizard();
    }
  };

  // Handle batch card click to show ingredient details
  const handleBatchCardClick = async (
    type: 'component' | 'filling' | 'dough' | 'product',
    batch: ComponentBatch | FillingBatch | DoughBatch | BatchRecord
  ) => {
    // Get lot number based on batch type
    const batchLotNumber = type === 'dough'
      ? (batch as DoughBatch).batchNumber
      : (batch as ComponentBatch | FillingBatch | BatchRecord).batchLotNumber;

    setSelectedBatchForModal({
      type,
      id: batch.id,
      productName: batch.productName,
      batchLotNumber,
      dateOfProduction: batch.dateOfProduction || ''
    });
    setIngredientModalOpen(true);
    setModalLoading(true);
    setModalIngredients([]);

    try {
      let endpoint = '';
      switch (type) {
        case 'component':
          endpoint = `/api/component-batch-ingredients/with-supplier/${batch.id}`;
          break;
        case 'filling':
          endpoint = `/api/filling-batch-ingredients/with-supplier/${batch.id}`;
          break;
        case 'dough':
          endpoint = `/api/dough-batch-ingredients/with-supplier/${batch.id}`;
          break;
        case 'product':
          endpoint = `/api/batch-record-ingredients/with-supplier/${batch.id}`;
          break;
      }

      const response = await fetch(endpoint);
      const data = await response.json();

      if (data.success) {
        setModalIngredients(data.ingredients || []);
      } else {
        console.error('Failed to fetch ingredients:', data.error);
      }
    } catch (error) {
      console.error('Error fetching batch ingredients:', error);
    } finally {
      setModalLoading(false);
    }
  };

  // Re-fetch ingredients for the currently open modal
  const handleRefreshModalIngredients = async () => {
    if (!selectedBatchForModal) return;
    setModalLoading(true);

    try {
      let endpoint = '';
      switch (selectedBatchForModal.type) {
        case 'component':
          endpoint = `/api/component-batch-ingredients/with-supplier/${selectedBatchForModal.id}`;
          break;
        case 'filling':
          endpoint = `/api/filling-batch-ingredients/with-supplier/${selectedBatchForModal.id}`;
          break;
        case 'dough':
          endpoint = `/api/dough-batch-ingredients/with-supplier/${selectedBatchForModal.id}`;
          break;
        case 'product':
          endpoint = `/api/batch-record-ingredients/with-supplier/${selectedBatchForModal.id}`;
          break;
      }

      const response = await fetch(endpoint);
      const data = await response.json();

      if (data.success) {
        setModalIngredients(data.ingredients || []);
      } else {
        console.error('Failed to fetch ingredients:', data.error);
      }
    } catch (error) {
      console.error('Error refreshing batch ingredients:', error);
    } finally {
      setModalLoading(false);
    }
  };

  // Duplicate batch: close modal and set up wizard at the correct step
  const handleDuplicateBatch = () => {
    if (!selectedBatchForModal) return;

    const { type, productName } = selectedBatchForModal;

    // Warn if wizard is already in progress
    if (wizardState.step !== 'initial') {
      if (!window.confirm('A batch wizard is already in progress. Starting a duplicate will discard your current progress. Continue?')) {
        return;
      }
    }

    // Close modal
    setIngredientModalOpen(false);
    setSelectedBatchForModal(null);
    setModalIngredients([]);

    if (type === 'component') {
      const recipe = components.find(c => c.name === productName);
      if (!recipe) {
        alert(`Could not find component recipe "${productName}".`);
        return;
      }
      setWizardState(prev => ({
        ...prev,
        step: 'ingredientList',
        batchType: 'component',
        selectedComponent: recipe,
        ingredientTraceCodes: new Map()
      }));
    } else if (type === 'filling') {
      const recipe = fillings.find(f => f.name === productName);
      if (!recipe) {
        alert(`Could not find filling recipe "${productName}".`);
        return;
      }
      setWizardState(prev => ({
        ...prev,
        step: 'fillingIngredientList',
        batchType: 'filling',
        selectedFilling: recipe,
        fillingIngredientTraceCodes: new Map(),
        fillingComponentBatches: new Map()
      }));
    } else if (type === 'dough') {
      const recipe = doughRecipes.find(d => d.name === productName);
      if (!recipe) {
        alert(`Could not find dough recipe "${productName}".`);
        return;
      }
      setWizardState(prev => ({
        ...prev,
        batchType: 'dough'
      }));
      handleDoughSelect(recipe);
    } else {
      // product
      const recipe = fillings.find(f => f.name === productName);
      if (!recipe) {
        alert(`Could not find product recipe "${productName}".`);
        return;
      }
      setWizardState(prev => ({
        ...prev,
        step: 'selectFillingBatches',
        batchType: 'product',
        selectedProduct: recipe,
        selectedFillingBatchIds: new Set(),
        selectedDoughBatchIds: new Set()
      }));
    }
  };

  // Handle back navigation
  const handleBack = () => {
    switch (wizardState.step) {
      case 'batchType':
        setWizardState(prev => ({ ...prev, step: 'initial', batchType: null }));
        break;
      case 'selectComponent':
        setWizardState(prev => ({ ...prev, step: 'batchType' }));
        break;
      case 'selectFilling':
        setWizardState(prev => ({ ...prev, step: 'batchType' }));
        break;
      case 'selectDough':
        setWizardState(prev => ({ ...prev, step: 'batchType' }));
        break;
      case 'selectProduct':
        setWizardState(prev => ({ ...prev, step: 'batchType' }));
        break;
      case 'ingredientList':
        setWizardState(prev => ({
          ...prev,
          step: 'selectComponent',
          selectedComponent: null,
          ingredientTraceCodes: new Map()
        }));
        break;
      case 'fillingIngredientList':
        setWizardState(prev => ({
          ...prev,
          step: 'selectFilling',
          selectedFilling: null,
          fillingIngredientTraceCodes: new Map(),
          fillingComponentBatches: new Map()
        }));
        break;
      case 'doughIngredientList':
        setWizardState(prev => ({
          ...prev,
          step: 'selectDough',
          selectedDoughRecipe: null,
          doughRecipeIngredients: [],
          doughIngredientTraceCodes: new Map()
        }));
        break;
      case 'selectFillingBatches':
        setWizardState(prev => ({
          ...prev,
          step: 'selectProduct',
          selectedProduct: null,
          selectedFillingBatchIds: new Set(),
          selectedDoughBatchIds: new Set()
        }));
        break;
      case 'selectDoughBatches':
        setWizardState(prev => ({
          ...prev,
          step: 'selectFillingBatches'
        }));
        break;
      case 'bakingDetails':
        setWizardState(prev => ({
          ...prev,
          step: 'selectDoughBatches'
        }));
        break;
      case 'selectTraceCodes':
        if (wizardState.batchType === 'filling') {
          setWizardState(prev => ({
            ...prev,
            step: 'fillingIngredientList',
            selectedFillingIngredient: null,
            availableTraceCodes: []
          }));
        } else if (wizardState.batchType === 'dough') {
          setWizardState(prev => ({
            ...prev,
            step: 'doughIngredientList',
            selectedDoughIngredient: null,
            availableTraceCodes: []
          }));
        } else {
          setWizardState(prev => ({
            ...prev,
            step: 'ingredientList',
            selectedIngredient: null,
            availableTraceCodes: []
          }));
        }
        break;
      case 'selectComponentBatch':
        setWizardState(prev => ({
          ...prev,
          step: 'fillingIngredientList',
          selectedComponentSlot: null,
          availableComponentBatchesForSlot: []
        }));
        break;
      case 'timesProduced':
        if (wizardState.batchType === 'filling') {
          setWizardState(prev => ({ ...prev, step: 'fillingIngredientList' }));
        } else if (wizardState.batchType === 'dough') {
          setWizardState(prev => ({ ...prev, step: 'doughIngredientList' }));
        } else if (wizardState.batchType === 'product') {
          setWizardState(prev => ({ ...prev, step: 'bakingDetails' }));
        } else {
          setWizardState(prev => ({ ...prev, step: 'ingredientList' }));
        }
        break;
      // Shipment wizard back handling
      case 'shipmentSelectSupplier':
        setWizardState(prev => ({
          ...prev,
          step: 'initial',
          shipmentSuppliers: [],
          shipmentLoadingSuppliers: false,
          shipmentSelectedSupplier: null,
          shipmentSelectedIngredient: null,
          shipmentIngredientSearch: '',
          shipmentEntries: new Map(),
          shipmentSavedCount: 0
        }));
        break;
      case 'shipmentSelectIngredient':
        setWizardState(prev => ({
          ...prev,
          step: 'shipmentSelectSupplier',
          shipmentSelectedIngredient: null,
          shipmentIngredientSearch: ''
        }));
        break;
      case 'shipmentLotNumber':
        // Preserve shipmentCurrentLot data when navigating back - it will be reset
        // if user selects a different ingredient via handleShipmentIngredientSelect
        setWizardState(prev => ({
          ...prev,
          step: 'shipmentSelectIngredient',
          shipmentSelectedIngredient: null,
          shipmentIngredientSearch: ''
        }));
        break;
      case 'shipmentQuantity':
        setWizardState(prev => ({ ...prev, step: 'shipmentLotNumber' }));
        break;
      case 'shipmentCondition':
        setWizardState(prev => ({ ...prev, step: 'shipmentQuantity' }));
        break;
      case 'shipmentTemperature':
        setWizardState(prev => ({ ...prev, step: 'shipmentCondition' }));
        break;
      case 'shipmentCorrectiveAction':
        setWizardState(prev => ({ ...prev, step: 'shipmentTemperature' }));
        break;
      // In Progress wizard back handling
      case 'inProgressBaking':
      case 'inProgressCooling1':
      case 'inProgressCooling2':
      case 'inProgressFreezing':
      case 'inProgressSignOff':
        handleInProgressBack();
        break;
      // Delivery wizard back handling
      case 'deliveryDateTime':
        setWizardState(prev => ({
          ...prev,
          step: 'initial',
          deliveryDate: '',
          deliveryTime: '',
          deliveryCustomerName: '',
          deliveryCustomerAddress: '',
          deliveryLineItems: [],
          deliverySelectedProduct: null,
          deliveryEligibleBatches: [],
          deliverySelectedBatchId: null,
          deliveryQuantity: 0
        }));
        break;
      case 'deliveryCustomer':
        setWizardState(prev => ({ ...prev, step: 'deliveryDateTime' }));
        break;
      case 'deliverySelectProduct':
        setWizardState(prev => ({ ...prev, step: 'deliveryCustomer' }));
        break;
      case 'deliverySelectBatch':
        setWizardState(prev => ({
          ...prev,
          step: 'deliverySelectProduct',
          deliverySelectedProduct: null,
          deliveryEligibleBatches: [],
          deliveryLoadingBatches: false
        }));
        break;
      case 'deliveryQuantity':
        setWizardState(prev => ({
          ...prev,
          step: 'deliverySelectBatch',
          deliverySelectedBatchId: null,
          deliveryQuantity: 0
        }));
        break;
      case 'deliveryReview':
        // If there are line items, go back to product selection to add more
        // If no line items, go back to customer step
        if (wizardState.deliveryLineItems.length > 0) {
          setWizardState(prev => ({ ...prev, step: 'deliverySelectProduct' }));
        } else {
          setWizardState(prev => ({ ...prev, step: 'deliveryCustomer' }));
        }
        break;
      // Delivery completion wizard back handling
      case 'inProgressDeliveryTemp':
        setWizardState(prev => ({
          ...prev,
          step: 'initial',
          inProgressDeliveryGroupId: null,
          inProgressDeliveryCustomer: '',
          deliveryTransportationTemp: null,
          deliveryTemperatureUnit: 'F',
          deliveryInitials: '',
          deliveryCorrectiveAction: ''
        }));
        break;
      case 'inProgressDeliveryInitials':
        setWizardState(prev => ({ ...prev, step: 'inProgressDeliveryTemp' }));
        break;
      // Temperature Log wizard back handling
      case 'tempLogSelectType':
        setWizardState(prev => ({
          ...prev,
          step: 'initial',
          tempLogEquipmentType: null,
          tempLogEquipmentList: [],
          tempLogSelectedEquipment: null,
          tempLogActualTemp: null,
          tempLogCorrectiveAction: '',
          tempLogCreatedLog: null
        }));
        break;
      case 'tempLogSelectEquipment':
        setWizardState(prev => ({
          ...prev,
          step: 'tempLogSelectType',
          tempLogEquipmentType: null,
          tempLogEquipmentList: [],
          tempLogSelectedEquipment: null
        }));
        break;
      case 'tempLogEnterTemp':
        setWizardState(prev => ({
          ...prev,
          step: 'tempLogSelectEquipment',
          tempLogSelectedEquipment: null,
          tempLogActualTemp: null,
          tempLogCorrectiveAction: ''
        }));
        break;
      // Sanitation Cleaning wizard back handling
      case 'sanitationSelectFrequency':
        setWizardState(prev => ({
          ...prev,
          step: 'initial',
          sanitationFrequency: null,
          sanitationEquipmentList: [],
          sanitationSelectedEquipmentIds: new Set(),
          sanitationCreatedRecords: [],
          sanitationError: null
        }));
        break;
      case 'sanitationSelectEquipment':
        setWizardState(prev => ({
          ...prev,
          step: 'sanitationSelectFrequency',
          sanitationFrequency: null,
          sanitationEquipmentList: [],
          sanitationSelectedEquipmentIds: new Set()
        }));
        break;
      case 'sanitationReview':
        setWizardState(prev => ({
          ...prev,
          step: 'sanitationSelectEquipment'
        }));
        break;
      default:
        break;
    }
  };

  // Handle component selection
  const handleComponentSelect = (component: Recipe) => {
    setWizardState(prev => ({
      ...prev,
      step: 'ingredientList',
      selectedComponent: component,
      ingredientTraceCodes: new Map()
    }));
  };

  // Handle ingredient selection - fetch trace codes
  const handleIngredientSelect = async (ingredient: Ingredient) => {
    setWizardState(prev => ({
      ...prev,
      step: 'selectTraceCodes',
      selectedIngredient: ingredient,
      loadingTraceCodes: true,
      availableTraceCodes: [],
      error: null
    }));

    try {
      const traceCodes = await ingredientTraceCodeApiService.fetchTraceCodesByIngredientName(
        ingredient.name
      );
      setWizardState(prev => ({
        ...prev,
        loadingTraceCodes: false,
        availableTraceCodes: [...traceCodes]
      }));
    } catch (error) {
      console.error('Error fetching trace codes:', error);
      setWizardState(prev => ({
        ...prev,
        loadingTraceCodes: false,
        availableTraceCodes: [],
        error: 'Failed to load trace codes'
      }));
    }
  };

  // Handle trace code toggle (multi-select)
  const handleTraceCodeToggle = (lotNumber: string) => {
    const ingredientName = wizardState.selectedIngredient?.name;
    if (!ingredientName) return;

    setWizardState(prev => {
      const newMap = new Map(prev.ingredientTraceCodes);
      const currentLots = newMap.get(ingredientName) || [];

      if (currentLots.includes(lotNumber)) {
        // Remove lot number
        newMap.set(ingredientName, currentLots.filter(l => l !== lotNumber));
      } else {
        // Add lot number
        newMap.set(ingredientName, [...currentLots, lotNumber]);
      }

      return { ...prev, ingredientTraceCodes: newMap };
    });
  };

  // Check if all ingredients have trace codes assigned
  const allIngredientsComplete = (): boolean => {
    if (!wizardState.selectedComponent) return false;
    const ingredients = wizardState.selectedComponent.ingredients;
    if (ingredients.length === 0) return false;

    return ingredients.every(ing => {
      const lots = wizardState.ingredientTraceCodes.get(ing.name);
      return lots && lots.length > 0;
    });
  };

  // Handle done selecting trace codes - return to ingredient list
  const handleDoneSelectingTraceCodes = () => {
    setWizardState(prev => ({
      ...prev,
      step: 'ingredientList',
      selectedIngredient: null,
      availableTraceCodes: []
    }));
  };

  // Handle batch creation
  const handleCreateBatch = async () => {
    if (!wizardState.selectedComponent) return;

    setWizardState(prev => ({ ...prev, step: 'creating', error: null }));

    const component = wizardState.selectedComponent;
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const productCode = component.productCode || '';

    // Get next sequential lot number from backend
    const batchLotNumber = await componentBatchApiService.getNextLotNumber(productCode, today);

    // Create the batch object
    const batch: ComponentBatch = {
      id: `temp-${uuidv4()}`,
      productId: component.id,
      productName: component.name,
      dateOfProduction: today,
      batchLotNumber,
      preparedBy: currentUser?.name || '',
      verifiedBy: '',
      timesProduced: wizardState.timesProduced,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    // Convert trace codes map to ingredients array
    const ingredients: ComponentIngredient[] = Array.from(
      wizardState.ingredientTraceCodes.entries()
    ).map(([ingredientName, lotNumbers]) => {
      const recipeIngredient = wizardState.selectedComponent?.ingredients.find(
        ing => ing.name === ingredientName
      );

      return {
        id: `temp-${uuidv4()}`,
        ingredientName,
        lotNumbers,
        quantity: recipeIngredient?.amount,
        unit: recipeIngredient?.unit
      };
    });

    try {
      const result = await dispatch(
        createComponentBatchAsync({ batch, ingredients })
      ).unwrap();

      setWizardState(prev => ({
        ...prev,
        step: 'summary',
        createdBatch: result,
        error: null
      }));
    } catch (error) {
      console.error('Error creating batch:', error);
      setWizardState(prev => ({
        ...prev,
        step: 'timesProduced',
        error: 'Failed to create batch. Please try again.'
      }));
    }
  };

  // ========== FILLING BATCH HANDLERS ==========

  // Handle filling selection
  const handleFillingSelect = (filling: Recipe) => {
    setWizardState(prev => ({
      ...prev,
      step: 'fillingIngredientList',
      selectedFilling: filling,
      fillingIngredientTraceCodes: new Map(),
      fillingComponentBatches: new Map()
    }));
  };

  // Handle filling ingredient selection (for raw ingredients)
  const handleFillingIngredientSelect = async (ingredient: Ingredient) => {
    setWizardState(prev => ({
      ...prev,
      step: 'selectTraceCodes',
      selectedFillingIngredient: ingredient,
      loadingTraceCodes: true,
      availableTraceCodes: [],
      error: null
    }));

    try {
      const traceCodes = await ingredientTraceCodeApiService.fetchTraceCodesByIngredientName(
        ingredient.name
      );
      setWizardState(prev => ({
        ...prev,
        loadingTraceCodes: false,
        availableTraceCodes: [...traceCodes]
      }));
    } catch (error) {
      console.error('Error fetching trace codes:', error);
      setWizardState(prev => ({
        ...prev,
        loadingTraceCodes: false,
        availableTraceCodes: [],
        error: 'Failed to load trace codes'
      }));
    }
  };

  // Handle component slot selection (for component references in fillings)
  const handleComponentSlotSelect = (ingredient: Ingredient) => {
    // Filter component batches by matching product name to ingredient name
    // and map to AvailableComponentBatch format
    const availableBatches: AvailableComponentBatch[] = componentBatches
      .filter(batch => batch.productName === ingredient.name)
      .map(batch => ({
        id: batch.id,
        componentId: batch.productId,
        componentName: batch.productName,
        batchLotNumber: batch.batchLotNumber,
        dateOfProduction: batch.dateOfProduction,
        preparedBy: batch.preparedBy || '',
        verifiedBy: batch.verifiedBy || ''
      }));

    setWizardState(prev => ({
      ...prev,
      step: 'selectComponentBatch',
      selectedComponentSlot: ingredient,
      loadingComponentBatches: false,
      availableComponentBatchesForSlot: availableBatches,
      error: null
    }));
  };

  // Handle trace code toggle for filling ingredients
  const handleFillingTraceCodeToggle = (lotNumber: string) => {
    const ingredientName = wizardState.selectedFillingIngredient?.name;
    if (!ingredientName) return;

    setWizardState(prev => {
      const newMap = new Map(prev.fillingIngredientTraceCodes);
      const currentLots = newMap.get(ingredientName) || [];

      if (currentLots.includes(lotNumber)) {
        newMap.set(ingredientName, currentLots.filter(l => l !== lotNumber));
      } else {
        newMap.set(ingredientName, [...currentLots, lotNumber]);
      }

      return { ...prev, fillingIngredientTraceCodes: newMap };
    });
  };

  // Handle component batch selection for filling
  const handleComponentBatchSelect = (batchId: string) => {
    const slotName = wizardState.selectedComponentSlot?.name;
    if (!slotName) return;

    setWizardState(prev => {
      const newMap = new Map(prev.fillingComponentBatches);
      newMap.set(slotName, batchId);
      return { ...prev, fillingComponentBatches: newMap };
    });
  };

  // Handle done selecting component batch - return to filling ingredient list
  const handleDoneSelectingComponentBatch = () => {
    setWizardState(prev => ({
      ...prev,
      step: 'fillingIngredientList',
      selectedComponentSlot: null,
      availableComponentBatchesForSlot: []
    }));
  };

  // Check if all filling ingredients are complete
  const allFillingIngredientsComplete = (): boolean => {
    if (!wizardState.selectedFilling) return false;
    const ingredients = wizardState.selectedFilling.ingredients;
    if (ingredients.length === 0) return false;

    // Check raw ingredients (type === 'ingredient') have trace codes
    const rawIngredients = ingredients.filter(ing => ing.type === 'ingredient');
    const rawComplete = rawIngredients.every(ing => {
      const lots = wizardState.fillingIngredientTraceCodes.get(ing.name);
      return lots && lots.length > 0;
    });

    // Check component slots (type === 'component') have batches selected
    const componentSlots = ingredients.filter(ing => ing.type === 'component');
    const componentsComplete = componentSlots.every(ing => {
      return wizardState.fillingComponentBatches.has(ing.name);
    });

    return rawComplete && componentsComplete;
  };

  // Handle filling batch creation
  const handleCreateFillingBatch = async () => {
    if (!wizardState.selectedFilling) return;

    setWizardState(prev => ({ ...prev, step: 'creating', error: null }));

    const filling = wizardState.selectedFilling;
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const productCode = filling.productCode || '';

    // Get next sequential lot number from backend
    const batchLotNumber = await fillingBatchApiService.getNextLotNumber(productCode, today);

    // Create the batch object
    const batch: FillingBatch = {
      id: `temp-${uuidv4()}`,
      productId: filling.id,
      productName: filling.name,
      dateOfProduction: today,
      batchLotNumber,
      preparedBy: currentUser?.name || '',
      verifiedBy: '',
      timesProduced: wizardState.timesProduced,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    // Convert trace codes map to ingredients array (raw ingredients only)
    const ingredients: FillingIngredient[] = Array.from(
      wizardState.fillingIngredientTraceCodes.entries()
    ).map(([ingredientName, lotNumbers]) => {
      const recipeIngredient = wizardState.selectedFilling?.ingredients.find(
        ing => ing.name === ingredientName
      );

      return {
        id: `temp-${uuidv4()}`,
        ingredientName,
        lotNumbers,
        quantity: recipeIngredient?.amount,
        unit: recipeIngredient?.unit
      };
    });

    // Convert component batches map to component batches array
    const componentBatches: FillingComponentBatchLocal[] = Array.from(
      wizardState.fillingComponentBatches.entries()
    ).map(([componentName, componentBatchId]) => {
      return {
        id: `temp-${uuidv4()}`,
        componentName,
        componentBatchId
      };
    });

    try {
      const result = await dispatch(
        createFillingBatchAsync({ batch, ingredients, componentBatches })
      ).unwrap();

      setWizardState(prev => ({
        ...prev,
        step: 'summary',
        createdBatch: result,
        error: null
      }));
    } catch (error) {
      console.error('Error creating filling batch:', error);
      setWizardState(prev => ({
        ...prev,
        step: 'timesProduced',
        error: 'Failed to create batch. Please try again.'
      }));
    }
  };

  // ========== DOUGH BATCH HANDLERS ==========

  // Handle dough recipe selection
  const handleDoughSelect = async (recipe: DoughRecipe) => {
    setWizardState(prev => ({
      ...prev,
      step: 'doughIngredientList',
      selectedDoughRecipe: recipe,
      doughRecipeIngredients: [],
      doughIngredientTraceCodes: new Map(),
      loadingTraceCodes: true,
      error: null
    }));

    try {
      const ingredients = await doughBatchApiService.fetchDoughRecipe(recipe.name);
      setWizardState(prev => ({
        ...prev,
        doughRecipeIngredients: [...ingredients],
        loadingTraceCodes: false
      }));
    } catch (error) {
      console.error('Error fetching dough recipe ingredients:', error);
      setWizardState(prev => ({
        ...prev,
        loadingTraceCodes: false,
        error: 'Failed to load recipe ingredients'
      }));
    }
  };

  // Handle dough ingredient selection - fetch trace codes
  const handleDoughIngredientSelect = async (ingredient: DoughRecipeIngredient) => {
    setWizardState(prev => ({
      ...prev,
      step: 'selectTraceCodes',
      selectedDoughIngredient: ingredient,
      loadingTraceCodes: true,
      availableTraceCodes: [],
      error: null
    }));

    try {
      const traceCodes = await ingredientTraceCodeApiService.fetchTraceCodesByIngredientName(
        ingredient.ingredientName
      );
      setWizardState(prev => ({
        ...prev,
        loadingTraceCodes: false,
        availableTraceCodes: [...traceCodes]
      }));
    } catch (error) {
      console.error('Error fetching trace codes:', error);
      setWizardState(prev => ({
        ...prev,
        loadingTraceCodes: false,
        availableTraceCodes: [],
        error: 'Failed to load trace codes'
      }));
    }
  };

  // Handle trace code toggle for dough ingredients
  const handleDoughTraceCodeToggle = (lotNumber: string) => {
    const ingredientName = wizardState.selectedDoughIngredient?.ingredientName;
    if (!ingredientName) return;

    setWizardState(prev => {
      const newMap = new Map(prev.doughIngredientTraceCodes);
      const currentLots = newMap.get(ingredientName) || [];

      if (currentLots.includes(lotNumber)) {
        newMap.set(ingredientName, currentLots.filter(l => l !== lotNumber));
      } else {
        newMap.set(ingredientName, [...currentLots, lotNumber]);
      }

      return { ...prev, doughIngredientTraceCodes: newMap };
    });
  };

  // Check if all dough ingredients have trace codes assigned
  const allDoughIngredientsComplete = (): boolean => {
    if (!wizardState.selectedDoughRecipe) return false;
    const ingredients = wizardState.doughRecipeIngredients;
    if (ingredients.length === 0) return false;

    return ingredients.every(ing => {
      const lots = wizardState.doughIngredientTraceCodes.get(ing.ingredientName);
      return lots && lots.length > 0;
    });
  };

  // Handle done selecting trace codes for dough - return to ingredient list
  const handleDoneSelectingDoughTraceCodes = () => {
    setWizardState(prev => ({
      ...prev,
      step: 'doughIngredientList',
      selectedDoughIngredient: null,
      availableTraceCodes: []
    }));
  };

  // Handle dough batch creation
  const handleCreateDoughBatch = async () => {
    if (!wizardState.selectedDoughRecipe) return;

    setWizardState(prev => ({ ...prev, step: 'creating', error: null }));

    const recipe = wizardState.selectedDoughRecipe;
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

    // Convert trace codes map to ingredients array
    const ingredients: DoughIngredientInput[] = Array.from(
      wizardState.doughIngredientTraceCodes.entries()
    ).map(([ingredientName, lotNumbers]) => {
      const recipeIngredient = wizardState.doughRecipeIngredients.find(
        ing => ing.ingredientName === ingredientName
      );

      return {
        ingredientName,
        lotNumbers,
        quantity: recipeIngredient?.quantity,
        unit: recipeIngredient?.unit
      };
    });

    // Create the batch request
    const batchRequest: CreateDoughBatchRequest = {
      productName: recipe.name,
      dateOfProduction: today,
      preparedBy: currentUser?.name || '',
      timesProduced: wizardState.timesProduced,
      ingredients
    };

    try {
      const result = await dispatch(createDoughBatchAsync(batchRequest)).unwrap();

      setWizardState(prev => ({
        ...prev,
        step: 'summary',
        createdBatch: result.doughBatch,
        error: null
      }));
    } catch (error) {
      console.error('Error creating dough batch:', error);
      setWizardState(prev => ({
        ...prev,
        step: 'timesProduced',
        error: 'Failed to create batch. Please try again.'
      }));
    }
  };

  // ========== PRODUCT BATCH HANDLERS ==========

  // Handle product selection
  const handleProductSelect = (product: Recipe) => {
    setWizardState(prev => ({
      ...prev,
      step: 'selectFillingBatches',
      selectedProduct: product,
      selectedFillingBatchIds: new Set(),
      selectedDoughBatchIds: new Set()
    }));
  };

  // Handle filling batch toggle for product
  const handleFillingBatchToggle = (batchId: string) => {
    setWizardState(prev => {
      const newSet = new Set(prev.selectedFillingBatchIds);
      if (newSet.has(batchId)) {
        newSet.delete(batchId);
      } else {
        newSet.add(batchId);
      }
      return { ...prev, selectedFillingBatchIds: newSet };
    });
  };

  // Handle dough batch toggle for product
  const handleDoughBatchToggle = (batchId: string) => {
    setWizardState(prev => {
      const newSet = new Set(prev.selectedDoughBatchIds);
      if (newSet.has(batchId)) {
        newSet.delete(batchId);
      } else {
        newSet.add(batchId);
      }
      return { ...prev, selectedDoughBatchIds: newSet };
    });
  };

  // Generate product batch lot number
  const generateProductBatchLotNumber = (productCode: string): string => {
    const today = new Date();
    const dateStr = today.toISOString().split('T')[0].replace(/-/g, '');
    return `${productCode || 'PROD'}-${dateStr}-P`;
  };

  // Handle product batch creation
  const handleCreateProductBatch = async () => {
    if (!wizardState.selectedProduct) return;

    setWizardState(prev => ({ ...prev, step: 'creating', error: null }));

    const product = wizardState.selectedProduct;
    const productCode = product.productCode || '';

    // Format date for lot number generation (YYYYMMDD)
    const startDate = wizardState.startTime
      ? new Date(wizardState.startTime).toISOString().split('T')[0].replace(/-/g, '')
      : new Date().toISOString().split('T')[0].replace(/-/g, '');

    try {
      // Step 1: Generate lot number via API
      const lotNumber = await bakingProductionLogApiService.generateLotNumber(productCode, startDate);

      // Step 2: Create baking production log (cascade creates batch_record and trace_code)
      const bakingLog: BakingProductionLog = {
        id: '',
        fillingId: product.id,
        lotNumber,
        startTime: wizardState.startTime || new Date().toISOString(),
        ovenTemp: wizardState.ovenTemp,
        staffInitials: currentUser?.name || '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      const { batchRecordId } = await bakingProductionLogApiService.createBakingProductionLog(bakingLog);

      // Step 3: Create filling batch links
      const fillingBatchPromises = Array.from(wizardState.selectedFillingBatchIds).map(
        fillingBatchId =>
          batchRecordFillingBatchApiService.createBatchRecordFillingBatch({
            batchRecordId,
            fillingBatchId
          })
      );
      await Promise.all(fillingBatchPromises);

      // Step 4: Create dough batch links
      const doughBatchPromises = Array.from(wizardState.selectedDoughBatchIds).map(
        doughBatchId =>
          batchRecordDoughBatchApiService.createBatchRecordDoughBatch({
            batchRecordId,
            doughBatchId
          })
      );
      await Promise.all(doughBatchPromises);

      // Step 5: Create Pan #1 for this batch
      await batchPanApiService.createPan(batchRecordId);

      // Step 6: Fetch the created batch record and refresh lists
      const [createdBatchRecord, updatedRecords, updatedPans] = await Promise.all([
        batchRecordApiService.fetchBatchRecordById(batchRecordId),
        batchRecordApiService.fetchBatchRecords(),
        batchPanApiService.fetchIncompletePans()
      ]);
      setBatchRecords(updatedRecords);
      setIncompletePans(updatedPans);

      setWizardState(prev => ({
        ...prev,
        step: 'summary',
        createdBatch: createdBatchRecord,
        error: null
      }));
    } catch (error) {
      console.error('Error creating product batch:', error);
      setWizardState(prev => ({
        ...prev,
        step: 'bakingDetails',
        error: 'Failed to create batch. Please try again.'
      }));
    }
  };

  // Shipment wizard handlers
  const handleNewShipmentClick = async () => {
    setWizardState(prev => ({
      ...prev,
      step: 'shipmentSelectSupplier',
      shipmentLoadingSuppliers: true,
      shipmentSuppliers: [],
      shipmentSelectedSupplier: null,
      shipmentSelectedIngredient: null,
      shipmentIngredientSearch: '',
      shipmentEntries: new Map(),
      shipmentSavedCount: 0
    }));

    try {
      const suppliers = await supplierApiService.fetchSuppliers();
      setWizardState(prev => ({
        ...prev,
        shipmentSuppliers: suppliers,
        shipmentLoadingSuppliers: false
      }));
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      setWizardState(prev => ({
        ...prev,
        shipmentLoadingSuppliers: false,
        error: 'Failed to load suppliers'
      }));
    }
  };

  const handleShipmentSupplierSelect = (supplier: Supplier) => {
    setWizardState(prev => ({
      ...prev,
      shipmentSelectedSupplier: supplier,
      shipmentSelectedIngredient: null,
      shipmentIngredientSearch: '',
      step: 'shipmentSelectIngredient'
    }));
  };

  const handleShipmentIngredientSelect = (ingredientName: string) => {
    setWizardState(prev => ({
      ...prev,
      shipmentSelectedIngredient: ingredientName,
      step: 'shipmentLotNumber',
      shipmentCurrentLot: {
        id: '',
        lotNumber: '',
        quantity: 0,
        unit: 'lbs' as QuantityUnit,
        conditionUponArrival: 'Good',
        receivingTemperature: 40,
        temperatureUnit: 'F' as TemperatureUnit,
        correctiveAction: '',
        packageSizeId: null,
        packageCount: 1,
        manualPackageSize: null,
        manualPackageUnit: null,
        manualPackageLabel: ''
      },
      shipmentAvailablePackageSizes: [],
      shipmentShowNewPackageForm: false
    }));
  };

  const handleAddShipmentLot = () => {
    const { shipmentSelectedSupplier, shipmentSelectedIngredient, shipmentCurrentLot } = wizardState;
    if (!shipmentSelectedSupplier || !shipmentSelectedIngredient || !shipmentCurrentLot.lotNumber) return;

    setWizardState(prev => {
      const newEntries = new Map(prev.shipmentEntries);

      // Get or create supplier map
      let supplierMap = newEntries.get(shipmentSelectedSupplier.id);
      if (!supplierMap) {
        supplierMap = new Map<string, ShipmentLotEntry[]>();
        newEntries.set(shipmentSelectedSupplier.id, supplierMap);
      }

      // Get or create ingredient lots array
      const ingredientLots = supplierMap.get(shipmentSelectedIngredient) || [];

      // Check if we're editing an existing lot
      if (shipmentCurrentLot.id && ingredientLots.some(lot => lot.id === shipmentCurrentLot.id)) {
        // Update existing lot in place
        const updatedLots = ingredientLots.map(lot =>
          lot.id === shipmentCurrentLot.id ? { ...shipmentCurrentLot } : lot
        );
        supplierMap.set(shipmentSelectedIngredient, updatedLots);
      } else {
        // Append new lot with fresh id
        const newLot: ShipmentLotEntry = {
          ...shipmentCurrentLot,
          id: uuidv4()
        };
        supplierMap.set(shipmentSelectedIngredient, [...ingredientLots, newLot]);
      }

      return {
        ...prev,
        shipmentEntries: newEntries,
        shipmentCurrentLot: {
          id: '',
          lotNumber: '',
          quantity: 0,
          unit: 'lbs' as QuantityUnit,
          conditionUponArrival: 'Good',
          receivingTemperature: 40,
          temperatureUnit: 'F' as TemperatureUnit,
          correctiveAction: '',
          packageSizeId: null,
          packageCount: 1,
          manualPackageSize: null,
          manualPackageUnit: null,
          manualPackageLabel: ''
        },
        shipmentAvailablePackageSizes: [],
        shipmentShowNewPackageForm: false
      };
    });
  };

  // Add lot and return to lot number step for additional lots
  const handleAddShipmentLotAndReturn = () => {
    const { shipmentSelectedSupplier, shipmentSelectedIngredient, shipmentCurrentLot } = wizardState;
    if (!shipmentSelectedSupplier || !shipmentSelectedIngredient || !shipmentCurrentLot.lotNumber) return;

    const newLot: ShipmentLotEntry = {
      ...shipmentCurrentLot,
      id: uuidv4()
    };

    setWizardState(prev => {
      const newEntries = new Map(prev.shipmentEntries);

      // Get or create supplier map
      let supplierMap = newEntries.get(shipmentSelectedSupplier.id);
      if (!supplierMap) {
        supplierMap = new Map<string, ShipmentLotEntry[]>();
        newEntries.set(shipmentSelectedSupplier.id, supplierMap);
      }

      // Get or create ingredient lots array
      const ingredientLots = supplierMap.get(shipmentSelectedIngredient) || [];
      supplierMap.set(shipmentSelectedIngredient, [...ingredientLots, newLot]);

      return {
        ...prev,
        shipmentEntries: newEntries,
        step: 'shipmentLotNumber',  // Return to lot number step for additional lots
        shipmentCurrentLot: {
          id: '',
          lotNumber: '',
          quantity: 0,
          unit: 'lbs' as QuantityUnit,
          conditionUponArrival: 'Good',
          receivingTemperature: 40,
          temperatureUnit: 'F' as TemperatureUnit,
          correctiveAction: '',
          packageSizeId: null,
          packageCount: 1,
          manualPackageSize: null,
          manualPackageUnit: null,
          manualPackageLabel: ''
        },
        shipmentAvailablePackageSizes: [],
        shipmentShowNewPackageForm: false
      };
    });
  };

  const handleRemoveShipmentLot = (lotId: string) => {
    const { shipmentSelectedSupplier, shipmentSelectedIngredient } = wizardState;
    if (!shipmentSelectedSupplier || !shipmentSelectedIngredient) return;

    setWizardState(prev => {
      const newEntries = new Map(prev.shipmentEntries);
      const supplierMap = newEntries.get(shipmentSelectedSupplier.id);

      if (supplierMap) {
        const ingredientLots = supplierMap.get(shipmentSelectedIngredient) || [];
        const filteredLots = ingredientLots.filter(lot => lot.id !== lotId);

        if (filteredLots.length > 0) {
          supplierMap.set(shipmentSelectedIngredient, filteredLots);
        } else {
          supplierMap.delete(shipmentSelectedIngredient);
        }

        if (supplierMap.size === 0) {
          newEntries.delete(shipmentSelectedSupplier.id);
        }
      }

      return {
        ...prev,
        shipmentEntries: newEntries
      };
    });
  };

  const handleEditShipmentLot = (lotId: string) => {
    const { shipmentSelectedSupplier, shipmentSelectedIngredient } = wizardState;
    if (!shipmentSelectedSupplier || !shipmentSelectedIngredient) return;

    const supplierMap = wizardState.shipmentEntries.get(shipmentSelectedSupplier.id);
    if (!supplierMap) return;

    const ingredientLots = supplierMap.get(shipmentSelectedIngredient) || [];
    const lot = ingredientLots.find(l => l.id === lotId);
    if (!lot) return;

    setWizardState(prev => ({
      ...prev,
      shipmentCurrentLot: { ...lot },
      step: 'shipmentQuantity'
    }));
  };

  const handleShipmentDone = async () => {
    // Check if there are any entries to save
    if (wizardState.shipmentEntries.size === 0) {
      setWizardState(prev => ({ ...prev, step: 'initial' }));
      return;
    }

    setWizardState(prev => ({ ...prev, step: 'shipmentSaving' }));

    try {
      const now = new Date();
      const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const receivedBy = currentUser?.name || 'Unknown';
      let savedCount = 0;

      // Iterate through all entries and save them
      for (const [supplierId, ingredientMap] of wizardState.shipmentEntries) {
        const supplier = wizardState.shipmentSuppliers.find(s => s.id === supplierId);
        if (!supplier) continue;

        for (const [ingredientName, lots] of ingredientMap) {
          for (const lot of lots) {
            const entry: ShipmentEntry = {
              id: lot.id,
              supplierName: supplier.supplierName,
              supplierId: supplier.id,
              ingredient: ingredientName,
              lotNumber: lot.lotNumber,
              quantity: lot.quantity,
              unit: lot.unit,
              conditionUponArrival: lot.conditionUponArrival,
              receivingTemperature: lot.receivingTemperature,
              temperatureUnit: lot.temperatureUnit,
              receivedBy: receivedBy,
              correctiveAction: lot.correctiveAction,
              storageType: wizardState.shipmentIngredientStorageType,
              frozenSolidConfirmed: wizardState.shipmentFrozenSolidConfirmed,
              receivingDate: today,
              createdAt: new Date().toISOString()
            };

            await dispatch(createIngredientReceivingEntry(entry)).unwrap();
            savedCount++;
          }
        }
      }

      setWizardState(prev => ({
        ...prev,
        step: 'shipmentSummary',
        shipmentSavedCount: savedCount,
        shipmentEntries: new Map(),
        error: null
      }));
    } catch (error) {
      console.error('Error saving shipment entries:', error);
      setWizardState(prev => ({
        ...prev,
        step: 'shipmentSelectSupplier',
        error: 'Failed to save shipment entries. Please try again.'
      }));
    }
  };

  // ===== DELIVERY WIZARD HANDLERS =====

  const handleNewDeliveryClick = () => {
    const now = new Date();
    const today = now.toISOString().split('T')[0]; // YYYY-MM-DD
    const currentTime = now.toTimeString().slice(0, 5); // HH:MM

    setWizardState(prev => ({
      ...prev,
      step: 'deliveryDateTime',
      deliveryDate: today,
      deliveryTime: currentTime,
      deliveryCustomerName: '',
      deliveryCustomerAddress: '',
      deliveryLineItems: [],
      deliverySelectedProduct: null,
      deliveryEligibleBatches: [],
      deliveryLoadingBatches: false,
      deliverySelectedBatchId: null,
      deliveryQuantity: 0
    }));
  };

  const handleDeliveryDateTimeNext = () => {
    if (!wizardState.deliveryDate || !wizardState.deliveryTime) return;
    setWizardState(prev => ({ ...prev, step: 'deliveryCustomer' }));
  };

  const handleDeliveryCustomerNext = () => {
    if (!wizardState.deliveryCustomerName.trim()) return;
    setWizardState(prev => ({ ...prev, step: 'deliverySelectProduct' }));
  };

  const handleDeliverySelectProduct = async (product: Recipe) => {
    setWizardState(prev => ({
      ...prev,
      deliverySelectedProduct: product,
      deliveryLoadingBatches: true,
      deliveryEligibleBatches: [],
      deliverySelectedBatchId: null,
      deliveryQuantity: 0,
      step: 'deliverySelectBatch'
    }));

    try {
      const result = await dispatch(fetchEligibleBatchesAsync(product.id)).unwrap();
      setWizardState(prev => ({
        ...prev,
        deliveryEligibleBatches: result,
        deliveryLoadingBatches: false
      }));
    } catch (error) {
      console.error('Error fetching eligible batches:', error);
      setWizardState(prev => ({
        ...prev,
        deliveryLoadingBatches: false,
        error: 'Failed to load eligible batches'
      }));
    }
  };

  const handleDeliverySelectBatch = (batchId: string) => {
    setWizardState(prev => ({
      ...prev,
      deliverySelectedBatchId: batchId,
      deliveryQuantity: 1,
      step: 'deliveryQuantity'
    }));
  };

  const handleDeliveryAddLineItem = () => {
    const { deliverySelectedProduct, deliverySelectedBatchId, deliveryQuantity, deliveryEligibleBatches } = wizardState;
    if (!deliverySelectedProduct || !deliverySelectedBatchId || deliveryQuantity <= 0) return;

    const selectedBatch = deliveryEligibleBatches.find(b => b.id === deliverySelectedBatchId);
    if (!selectedBatch) return;

    const newLineItem: DeliveryLineItem = {
      id: uuidv4(),
      fillingId: deliverySelectedProduct.id,
      fillingName: deliverySelectedProduct.name,
      batchRecordId: selectedBatch.id,
      batchLotNumber: selectedBatch.batchLotNumber,
      quantity: deliveryQuantity
    };

    setWizardState(prev => ({
      ...prev,
      deliveryLineItems: [...prev.deliveryLineItems, newLineItem],
      deliverySelectedProduct: null,
      deliverySelectedBatchId: null,
      deliveryQuantity: 0,
      deliveryEligibleBatches: [],
      step: 'deliveryReview'
    }));
  };

  const handleDeliveryRemoveLineItem = (itemId: string) => {
    setWizardState(prev => ({
      ...prev,
      deliveryLineItems: prev.deliveryLineItems.filter(item => item.id !== itemId)
    }));
  };

  const handleDeliverySave = async () => {
    const { deliveryDate, deliveryTime, deliveryCustomerName, deliveryCustomerAddress, deliveryLineItems } = wizardState;
    if (deliveryLineItems.length === 0) return;

    setWizardState(prev => ({ ...prev, step: 'deliverySaving' }));

    try {
      const customerDestination = deliveryCustomerAddress
        ? `${deliveryCustomerName}, ${deliveryCustomerAddress}`
        : deliveryCustomerName;

      await dispatch(bulkCreateDeliveryLogsAsync({
        deliveryDate,
        deliveryTime,
        customerDestination,
        lineItems: deliveryLineItems.map(item => ({
          batchRecordId: item.batchRecordId,
          fillingId: item.fillingId,
          quantity: item.quantity
        }))
      })).unwrap();

      // Reset wizard and return to initial
      setWizardState(prev => ({
        ...prev,
        step: 'initial',
        deliveryDate: '',
        deliveryTime: '',
        deliveryCustomerName: '',
        deliveryCustomerAddress: '',
        deliveryLineItems: [],
        deliverySelectedProduct: null,
        deliveryEligibleBatches: [],
        deliveryLoadingBatches: false,
        deliverySelectedBatchId: null,
        deliveryQuantity: 0,
        error: null
      }));
    } catch (error) {
      console.error('Error saving delivery:', error);
      setWizardState(prev => ({
        ...prev,
        step: 'deliveryReview',
        error: 'Failed to save delivery. Please try again.'
      }));
    }
  };

  // ===== DELIVERY COMPLETION WIZARD HANDLERS =====

  const handleStartInProgressDeliveryWizard = (group: { deliveryGroupId: string; customerDestination: string }) => {
    setWizardState(prev => ({
      ...prev,
      step: 'inProgressDeliveryTemp',
      inProgressDeliveryGroupId: group.deliveryGroupId,
      inProgressDeliveryCustomer: group.customerDestination,
      deliveryTransportationTemp: null,
      deliveryTemperatureUnit: 'F',
      deliveryInitials: currentUser?.name || '',
      deliveryCorrectiveAction: ''
    }));
  };

  const handleDeliveryTempNext = () => {
    if (wizardState.deliveryTransportationTemp === null) return;
    setWizardState(prev => ({ ...prev, step: 'inProgressDeliveryInitials' }));
  };

  const handleCompleteDelivery = async () => {
    const { inProgressDeliveryGroupId, deliveryTransportationTemp, deliveryTemperatureUnit, deliveryInitials, deliveryCorrectiveAction } = wizardState;
    if (!inProgressDeliveryGroupId || deliveryTransportationTemp === null || !deliveryInitials.trim()) return;

    setWizardState(prev => ({ ...prev, step: 'inProgressDeliverySaving' }));

    try {
      await dispatch(completeDeliveryGroupAsync({
        groupId: inProgressDeliveryGroupId,
        data: {
          transportationTemp: deliveryTransportationTemp,
          temperatureUnit: deliveryTemperatureUnit,
          initials: deliveryInitials,
          correctiveAction: deliveryCorrectiveAction || undefined
        }
      })).unwrap();

      // Refetch incomplete delivery groups to update the list
      dispatch(fetchIncompleteDeliveryGroupsAsync());

      // Reset wizard and return to initial
      setWizardState(prev => ({
        ...prev,
        step: 'initial',
        inProgressDeliveryGroupId: null,
        inProgressDeliveryCustomer: '',
        deliveryTransportationTemp: null,
        deliveryTemperatureUnit: 'F',
        deliveryInitials: '',
        deliveryCorrectiveAction: '',
        error: null
      }));
    } catch (error) {
      console.error('Error completing delivery:', error);
      setWizardState(prev => ({
        ...prev,
        step: 'inProgressDeliveryInitials',
        error: 'Failed to complete delivery. Please try again.'
      }));
    }
  };

  // ===== Temperature Log Wizard Handlers =====
  const handleNewTempLogClick = () => {
    setWizardState(prev => ({
      ...prev,
      step: 'tempLogSelectType',
      tempLogEquipmentType: null,
      tempLogEquipmentList: [],
      tempLogLoadingEquipment: false,
      tempLogSelectedEquipment: null,
      tempLogActualTemp: null,
      tempLogCorrectiveAction: '',
      tempLogCreatedLog: null
    }));
  };

  const handleTempLogTypeSelect = async (type: 'freezer' | 'oven' | 'fridge') => {
    setWizardState(prev => ({
      ...prev,
      tempLogEquipmentType: type,
      tempLogLoadingEquipment: true,
      tempLogEquipmentList: [],
      step: 'tempLogSelectEquipment'
    }));

    try {
      const equipment = await temperatureEquipmentApiService.fetchByType(type);
      // Filter to only active equipment
      const activeEquipment = equipment.filter(e => e.isActive);
      setWizardState(prev => ({
        ...prev,
        tempLogEquipmentList: activeEquipment,
        tempLogLoadingEquipment: false
      }));
    } catch (error) {
      console.error('Error fetching equipment:', error);
      setWizardState(prev => ({
        ...prev,
        tempLogLoadingEquipment: false,
        error: 'Failed to load equipment'
      }));
    }
  };

  const handleTempLogEquipmentSelect = (equipment: TemperatureEquipment) => {
    setWizardState(prev => ({
      ...prev,
      tempLogSelectedEquipment: equipment,
      tempLogActualTemp: null,
      tempLogCorrectiveAction: '',
      step: 'tempLogEnterTemp'
    }));
  };

  const handleTempLogSubmit = async () => {
    const { tempLogSelectedEquipment, tempLogActualTemp, tempLogCorrectiveAction } = wizardState;
    if (!tempLogSelectedEquipment || tempLogActualTemp === null) return;

    setWizardState(prev => ({ ...prev, step: 'tempLogSaving' }));

    try {
      const now = new Date();
      const date = now.toISOString().split('T')[0];
      const timeChecked = now.toTimeString().slice(0, 5);

      const log = await temperatureLogApiService.createLog({
        equipmentId: tempLogSelectedEquipment.id,
        date,
        timeChecked,
        actualTemp: tempLogActualTemp,
        correctiveAction: tempLogCorrectiveAction,
        recordedBy: currentUser?.name || ''
      });

      setWizardState(prev => ({
        ...prev,
        step: 'tempLogSummary',
        tempLogCreatedLog: log
      }));
    } catch (error) {
      console.error('Error creating temperature log:', error);
      setWizardState(prev => ({
        ...prev,
        step: 'tempLogEnterTemp',
        error: 'Failed to save temperature log. Please try again.'
      }));
    }
  };

  const handleTempLogAnotherSameType = async () => {
    const type = wizardState.tempLogEquipmentType;
    if (!type) return;

    setWizardState(prev => ({
      ...prev,
      step: 'tempLogSelectEquipment',
      tempLogSelectedEquipment: null,
      tempLogActualTemp: null,
      tempLogCorrectiveAction: '',
      tempLogCreatedLog: null,
      tempLogLoadingEquipment: true
    }));

    try {
      const equipment = await temperatureEquipmentApiService.fetchByType(type);
      const activeEquipment = equipment.filter(e => e.isActive);
      setWizardState(prev => ({
        ...prev,
        tempLogEquipmentList: activeEquipment,
        tempLogLoadingEquipment: false
      }));
    } catch (error) {
      console.error('Error fetching equipment:', error);
      setWizardState(prev => ({
        ...prev,
        tempLogLoadingEquipment: false,
        error: 'Failed to load equipment'
      }));
    }
  };

  const handleTempLogDifferentType = () => {
    setWizardState(prev => ({
      ...prev,
      step: 'tempLogSelectType',
      tempLogEquipmentType: null,
      tempLogEquipmentList: [],
      tempLogSelectedEquipment: null,
      tempLogActualTemp: null,
      tempLogCorrectiveAction: '',
      tempLogCreatedLog: null
    }));
  };

  const handleTempLogDone = () => {
    resetWizard();
  };

  // ========== SANITATION CLEANING WIZARD HANDLERS ==========

  const handleNewSanitationClick = () => {
    setWizardState(prev => ({
      ...prev,
      step: 'sanitationSelectFrequency',
      sanitationFrequency: null,
      sanitationEquipmentList: [],
      sanitationLoadingEquipment: false,
      sanitationSelectedEquipmentIds: new Set(),
      sanitationCreatedRecords: [],
      sanitationError: null
    }));
  };

  const handleSanitationFrequencySelect = async (frequency: CleaningFrequency) => {
    setWizardState(prev => ({
      ...prev,
      sanitationFrequency: frequency,
      sanitationLoadingEquipment: true,
      sanitationEquipmentList: [],
      sanitationSelectedEquipmentIds: new Set(),
      step: 'sanitationSelectEquipment'
    }));

    try {
      const equipment = await sanitationApiService.fetchEquipment();
      setWizardState(prev => ({
        ...prev,
        sanitationEquipmentList: equipment,
        sanitationLoadingEquipment: false
      }));
    } catch (error) {
      console.error('Error fetching sanitation equipment:', error);
      setWizardState(prev => ({
        ...prev,
        sanitationLoadingEquipment: false,
        sanitationError: 'Failed to load equipment'
      }));
    }
  };

  const handleSanitationEquipmentToggle = (equipmentId: string) => {
    setWizardState(prev => {
      const newSelected = new Set(prev.sanitationSelectedEquipmentIds);
      if (newSelected.has(equipmentId)) {
        newSelected.delete(equipmentId);
      } else {
        newSelected.add(equipmentId);
      }
      return { ...prev, sanitationSelectedEquipmentIds: newSelected };
    });
  };

  const handleSanitationProceedToReview = () => {
    setWizardState(prev => ({ ...prev, step: 'sanitationReview' }));
  };

  const handleSanitationSubmit = async () => {
    const { sanitationFrequency, sanitationSelectedEquipmentIds } = wizardState;
    if (!sanitationFrequency || sanitationSelectedEquipmentIds.size === 0) return;

    setWizardState(prev => ({ ...prev, step: 'sanitationSaving' }));

    try {
      const cleaningDate = new Date().toISOString().split('T')[0];
      const createdRecords: SanitationRecord[] = [];

      for (const equipmentId of sanitationSelectedEquipmentIds) {
        const record = await sanitationApiService.createRecord({
          equipmentId: equipmentId,
          frequency: sanitationFrequency,
          cleaningDate: cleaningDate,
          cleanedByUserId: currentUser?.id || null,
          verifiedByUserId: null
        });
        createdRecords.push(record);
      }

      setWizardState(prev => ({
        ...prev,
        step: 'sanitationSummary',
        sanitationCreatedRecords: createdRecords
      }));
    } catch (error) {
      console.error('Error creating sanitation records:', error);
      setWizardState(prev => ({
        ...prev,
        step: 'sanitationReview',
        sanitationError: 'Failed to save cleaning records. Please try again.'
      }));
    }
  };

  const handleSanitationLogAnother = () => {
    setWizardState(prev => ({
      ...prev,
      step: 'sanitationSelectFrequency',
      sanitationFrequency: null,
      sanitationEquipmentList: [],
      sanitationLoadingEquipment: false,
      sanitationSelectedEquipmentIds: new Set(),
      sanitationCreatedRecords: [],
      sanitationError: null
    }));
  };

  const handleSanitationDone = () => {
    resetWizard();
  };

  // Step 1: Initial - CREATE NEW BATCH button
  const renderInitialStep = () => (
    <div style={styles.centeredContainer}>
      <button
        onClick={() => setWizardState(prev => ({ ...prev, step: 'batchType' }))}
        style={styles.primaryButton}
      >
        CREATE NEW BATCH
      </button>
    </div>
  );

  // Step 2: Batch Type Selection - COMPONENT, FILLING, or DOUGH
  const renderBatchTypeStep = () => (
    <div style={styles.stepContainer}>
      <button onClick={handleBack} style={styles.backButton}>
        ← Back
      </button>

      <h2 style={styles.stepTitle}>Select Batch Type</h2>

      <div style={styles.buttonGrid}>
        <button
          onClick={() => setWizardState(prev => ({ ...prev, step: 'selectComponent', batchType: 'component' }))}
          style={styles.typeButton}
        >
          COMPONENT
        </button>
        <button
          onClick={() => setWizardState(prev => ({ ...prev, step: 'selectFilling', batchType: 'filling' }))}
          style={styles.fillingTypeButton}
        >
          FILLING
        </button>
        <button
          onClick={() => setWizardState(prev => ({ ...prev, step: 'selectDough', batchType: 'dough' }))}
          style={styles.doughTypeButton}
        >
          DOUGH
        </button>
        <button
          onClick={() => setWizardState(prev => ({ ...prev, step: 'selectProduct', batchType: 'product' }))}
          style={styles.productTypeButton}
        >
          PRODUCT
        </button>
      </div>
    </div>
  );

  // Step 3a: Select Component
  const renderSelectComponentStep = () => (
    <div style={styles.stepContainer}>
      <button onClick={handleBack} style={styles.backButton}>
        ← Back
      </button>

      <h2 style={styles.stepTitle}>Select Component</h2>

      {components.length === 0 ? (
        <p style={styles.emptyMessage}>No components available. Create components first.</p>
      ) : (
        <div style={styles.componentGrid}>
          {components.map(component => (
            <button
              key={component.id}
              onClick={() => handleComponentSelect(component)}
              style={styles.componentButton}
            >
              {component.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  // Step 3b: Select Filling
  const renderSelectFillingStep = () => (
    <div style={styles.stepContainer}>
      <button onClick={handleBack} style={styles.backButton}>
        ← Back
      </button>

      <h2 style={styles.stepTitle}>Select Filling</h2>

      {fillings.length === 0 ? (
        <p style={styles.emptyMessage}>No fillings available. Create fillings first.</p>
      ) : (
        <div style={styles.componentGrid}>
          {fillings.map(filling => (
            <button
              key={filling.id}
              onClick={() => handleFillingSelect(filling)}
              style={styles.fillingButton}
            >
              {filling.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  // Step 3c: Select Dough Recipe
  const renderSelectDoughStep = () => (
    <div style={styles.stepContainer}>
      <button onClick={handleBack} style={styles.backButton}>
        ← Back
      </button>

      <h2 style={styles.stepTitle}>Select Dough Type</h2>

      {doughRecipes.length === 0 ? (
        <p style={styles.emptyMessage}>No dough recipes available.</p>
      ) : (
        <div style={styles.componentGrid}>
          {doughRecipes.map(recipe => (
            <button
              key={recipe.id}
              onClick={() => handleDoughSelect(recipe)}
              style={styles.doughButton}
            >
              {recipe.displayName || recipe.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  // Step 3d: Select Product Type (from fillings)
  const renderSelectProductStep = () => (
    <div style={styles.stepContainer}>
      <button onClick={handleBack} style={styles.backButton}>
        ← Back
      </button>

      <h2 style={styles.stepTitle}>Select Product Type</h2>

      {fillings.length === 0 ? (
        <p style={styles.emptyMessage}>No products available. Create fillings first.</p>
      ) : (
        <div style={styles.componentGrid}>
          {fillings.map(filling => (
            <button
              key={filling.id}
              onClick={() => handleProductSelect(filling)}
              style={styles.productButton}
            >
              {filling.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  // Step 4d: Select Filling Batches (multi-select for product)
  const renderSelectFillingBatchesStep = () => {
    const selectedProductName = wizardState.selectedProduct?.name;
    const sortedFillingBatches = [...fillingBatches]
      .filter(batch => batch.productName === selectedProductName)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    return (
      <div style={styles.stepContainerScrollable}>
        <button onClick={handleBack} style={styles.backButton}>
          ← Back
        </button>

        <h2 style={styles.stepTitle}>{wizardState.selectedProduct?.name}</h2>
        <p style={styles.stepSubtitle}>Select filling batches used</p>

        <div style={styles.scrollableListArea}>
          {fillingBatchesLoading ? (
            <div style={styles.loadingContainer}>
              <div style={styles.spinner} />
              <p style={styles.loadingText}>Loading filling batches...</p>
            </div>
          ) : sortedFillingBatches.length === 0 ? (
            <p style={styles.emptyMessage}>No "{selectedProductName}" filling batches available.</p>
          ) : (
            <div style={styles.batchSelectionList}>
              {sortedFillingBatches.map(batch => {
                const isSelected = wizardState.selectedFillingBatchIds.has(batch.id);
                return (
                  <label
                    key={batch.id}
                    style={{
                      ...styles.batchSelectionItem,
                      backgroundColor: isSelected ? '#f3e5f5' : 'white',
                      borderColor: isSelected ? '#9C27B0' : '#ddd'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleFillingBatchToggle(batch.id)}
                      style={styles.checkbox}
                    />
                    <div style={styles.batchSelectionInfo}>
                      <span style={styles.batchSelectionName}>{batch.productName}</span>
                      <span style={styles.batchSelectionLot}>{batch.batchLotNumber}</span>
                      <span style={styles.batchSelectionDate}>
                        {batch.dateOfProduction && new Date(batch.dateOfProduction).toLocaleDateString()}
                      </span>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* Continue button */}
        <div style={styles.continueContainer}>
          <button
            onClick={() => setWizardState(prev => ({ ...prev, step: 'selectDoughBatches' }))}
            disabled={wizardState.selectedFillingBatchIds.size === 0}
            style={{
              ...styles.continueButton,
              backgroundColor: wizardState.selectedFillingBatchIds.size > 0 ? '#9C27B0' : '#cccccc',
              cursor: wizardState.selectedFillingBatchIds.size > 0 ? 'pointer' : 'not-allowed'
            }}
          >
            CONTINUE
          </button>
        </div>
      </div>
    );
  };

  // Step 5d: Select Dough Batches (multi-select for product)
  const renderSelectDoughBatchesStep = () => {
    const sortedDoughBatches = [...doughBatches]
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    return (
      <div style={styles.stepContainerScrollable}>
        <button onClick={handleBack} style={styles.backButton}>
          ← Back
        </button>

        <h2 style={styles.stepTitle}>{wizardState.selectedProduct?.name}</h2>
        <p style={styles.stepSubtitle}>Select dough batches used</p>

        <div style={styles.scrollableListArea}>
          {doughBatchesLoading ? (
            <div style={styles.loadingContainer}>
              <div style={styles.spinner} />
              <p style={styles.loadingText}>Loading dough batches...</p>
            </div>
          ) : sortedDoughBatches.length === 0 ? (
            <p style={styles.emptyMessage}>No dough batches available.</p>
          ) : (
            <div style={styles.batchSelectionList}>
              {sortedDoughBatches.map(batch => {
                const isSelected = wizardState.selectedDoughBatchIds.has(batch.id);
                return (
                  <label
                    key={batch.id}
                    style={{
                      ...styles.batchSelectionItem,
                      backgroundColor: isSelected ? '#f3e5f5' : 'white',
                      borderColor: isSelected ? '#9C27B0' : '#ddd'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleDoughBatchToggle(batch.id)}
                      style={styles.checkbox}
                    />
                    <div style={styles.batchSelectionInfo}>
                      <span style={styles.batchSelectionName}>{batch.productName}</span>
                      <span style={styles.batchSelectionLot}>{batch.batchNumber}</span>
                      <span style={styles.batchSelectionDate}>
                        {batch.dateOfProduction && new Date(batch.dateOfProduction).toLocaleDateString()}
                      </span>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* Continue button */}
        <div style={styles.continueContainer}>
          <button
            onClick={() => setWizardState(prev => ({ ...prev, step: 'bakingDetails' }))}
            disabled={wizardState.selectedDoughBatchIds.size === 0}
            style={{
              ...styles.continueButton,
              backgroundColor: wizardState.selectedDoughBatchIds.size > 0 ? '#9C27B0' : '#cccccc',
              cursor: wizardState.selectedDoughBatchIds.size > 0 ? 'pointer' : 'not-allowed'
            }}
          >
            CONTINUE
          </button>
        </div>
      </div>
    );
  };

  // Step 6d: Baking Details for Product batch
  const renderBakingDetailsStep = () => {
    const isValid = wizardState.ovenTemp > 0 && wizardState.startTime !== '';

    return (
      <div style={styles.stepContainer}>
        <button onClick={handleBack} style={styles.backButton}>
          ← Back
        </button>

        <h2 style={styles.stepTitle}>{wizardState.selectedProduct?.name}</h2>
        <p style={styles.stepSubtitle}>Enter baking details</p>

        <div style={styles.formContainer}>
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Oven Temperature (°F) *</label>
            <input
              type="number"
              value={wizardState.ovenTemp}
              onChange={(e) => setWizardState(prev => ({
                ...prev,
                ovenTemp: parseInt(e.target.value) || 0
              }))}
              style={styles.formInput}
              min="0"
              max="1000"
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Start Time *</label>
            <input
              type="datetime-local"
              value={wizardState.startTime}
              onChange={(e) => setWizardState(prev => ({
                ...prev,
                startTime: e.target.value
              }))}
              style={styles.formInput}
            />
          </div>
        </div>

        {/* Create Product Batch button */}
        <div style={styles.continueContainer}>
          <button
            onClick={handleCreateProductBatch}
            disabled={!isValid}
            style={{
              ...styles.continueButton,
              backgroundColor: isValid ? '#9C27B0' : '#cccccc',
              cursor: isValid ? 'pointer' : 'not-allowed'
            }}
          >
            CREATE PRODUCT BATCH
          </button>
        </div>
      </div>
    );
  };

  // Step 4: Ingredient List
  const renderIngredientListStep = () => {
    const ingredients = wizardState.selectedComponent?.ingredients || [];

    return (
      <div style={styles.stepContainerScrollable}>
        <button onClick={handleBack} style={styles.backButton}>
          ← Back
        </button>

        <h2 style={styles.stepTitle}>{wizardState.selectedComponent?.name}</h2>
        <p style={styles.stepSubtitle}>Select trace codes for each ingredient</p>

        <div style={styles.scrollableListArea}>
          {ingredients.length === 0 ? (
            <p style={styles.emptyMessage}>No ingredients in this component.</p>
          ) : (
            <div style={styles.ingredientGrid}>
              {ingredients.map(ingredient => {
                const lots = wizardState.ingredientTraceCodes.get(ingredient.name);
                const isComplete = lots && lots.length > 0;

                return (
                  <button
                    key={ingredient.id}
                    onClick={() => handleIngredientSelect(ingredient)}
                    style={{
                      ...styles.ingredientButton,
                      backgroundColor: isComplete ? '#e8f5e9' : 'white',
                      borderColor: isComplete ? '#28a745' : '#ddd'
                    }}
                  >
                    <span style={styles.ingredientName}>{ingredient.name}</span>
                    {isComplete && (
                      <span style={styles.checkmark}>✓</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Continue button */}
        <div style={styles.continueContainer}>
          <button
            onClick={() => setWizardState(prev => ({ ...prev, step: 'timesProduced' }))}
            disabled={!allIngredientsComplete()}
            style={{
              ...styles.continueButton,
              backgroundColor: allIngredientsComplete() ? '#28a745' : '#cccccc',
              cursor: allIngredientsComplete() ? 'pointer' : 'not-allowed'
            }}
          >
            CONTINUE
          </button>
        </div>
      </div>
    );
  };

  // Step 4b: Filling Ingredient List (with raw ingredients and component slots)
  const renderFillingIngredientListStep = () => {
    const ingredients = wizardState.selectedFilling?.ingredients || [];
    const rawIngredients = ingredients.filter(ing => ing.type === 'ingredient');
    const componentSlots = ingredients.filter(ing => ing.type === 'component');

    return (
      <div style={styles.stepContainerScrollable}>
        <button onClick={handleBack} style={styles.backButton}>
          ← Back
        </button>

        <h2 style={styles.stepTitle}>{wizardState.selectedFilling?.name}</h2>
        <p style={styles.stepSubtitle}>Select trace codes and component batches</p>

        <div style={styles.scrollableListArea}>
          {ingredients.length === 0 ? (
            <p style={styles.emptyMessage}>No ingredients in this filling.</p>
          ) : (
            <div style={styles.fillingIngredientSections}>
              {/* Raw Ingredients Section */}
              {rawIngredients.length > 0 && (
                <div style={styles.ingredientSection}>
                  <h3 style={styles.sectionTitle}>Raw Ingredients</h3>
                  <div style={styles.ingredientGrid}>
                    {rawIngredients.map(ingredient => {
                      const lots = wizardState.fillingIngredientTraceCodes.get(ingredient.name);
                      const isComplete = lots && lots.length > 0;

                      return (
                        <button
                          key={ingredient.id}
                          onClick={() => handleFillingIngredientSelect(ingredient)}
                          style={{
                            ...styles.ingredientButton,
                            backgroundColor: isComplete ? '#e8f5e9' : 'white',
                            borderColor: isComplete ? '#28a745' : '#ddd'
                          }}
                        >
                          <span style={styles.ingredientName}>{ingredient.name}</span>
                          {isComplete && (
                            <span style={styles.checkmark}>✓</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Component Slots Section */}
              {componentSlots.length > 0 && (
                <div style={styles.ingredientSection}>
                  <h3 style={styles.sectionTitleBlue}>Components</h3>
                  <div style={styles.ingredientGrid}>
                    {componentSlots.map(ingredient => {
                      const isComplete = wizardState.fillingComponentBatches.has(ingredient.name);

                      return (
                        <button
                          key={ingredient.id}
                          onClick={() => handleComponentSlotSelect(ingredient)}
                          style={{
                            ...styles.ingredientButton,
                            backgroundColor: isComplete ? '#e3f2fd' : 'white',
                            borderColor: isComplete ? '#2196F3' : '#ddd'
                          }}
                        >
                          <span style={styles.ingredientName}>{ingredient.name}</span>
                          {isComplete && (
                            <span style={styles.checkmarkBlue}>✓</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Continue button */}
        <div style={styles.continueContainer}>
          <button
            onClick={() => setWizardState(prev => ({ ...prev, step: 'timesProduced' }))}
            disabled={!allFillingIngredientsComplete()}
            style={{
              ...styles.continueButton,
              backgroundColor: allFillingIngredientsComplete() ? '#28a745' : '#cccccc',
              cursor: allFillingIngredientsComplete() ? 'pointer' : 'not-allowed'
            }}
          >
            CONTINUE
          </button>
        </div>
      </div>
    );
  };

  // Step 4c: Dough Ingredient List
  const renderDoughIngredientListStep = () => {
    const ingredients = wizardState.doughRecipeIngredients;

    return (
      <div style={styles.stepContainerScrollable}>
        <button onClick={handleBack} style={styles.backButton}>
          ← Back
        </button>

        <h2 style={styles.stepTitle}>
          {wizardState.selectedDoughRecipe?.displayName || wizardState.selectedDoughRecipe?.name}
        </h2>
        <p style={styles.stepSubtitle}>Select trace codes for each ingredient</p>

        <div style={styles.scrollableListArea}>
          {wizardState.loadingTraceCodes ? (
            <div style={styles.loadingContainer}>
              <div style={styles.spinner} />
              <p style={styles.loadingText}>Loading ingredients...</p>
            </div>
          ) : wizardState.error ? (
            <div style={styles.errorContainer}>
              <p style={styles.errorText}>{wizardState.error}</p>
            </div>
          ) : ingredients.length === 0 ? (
            <p style={styles.emptyMessage}>No ingredients in this recipe.</p>
          ) : (
            <div style={styles.ingredientGrid}>
              {ingredients.map(ingredient => {
                const lots = wizardState.doughIngredientTraceCodes.get(ingredient.ingredientName);
                const isComplete = lots && lots.length > 0;

                return (
                  <button
                    key={ingredient.id}
                    onClick={() => handleDoughIngredientSelect(ingredient)}
                    style={{
                      ...styles.doughIngredientButton,
                      backgroundColor: isComplete ? '#f5f0e6' : 'white',
                      borderColor: isComplete ? '#8B4513' : '#ddd'
                    }}
                  >
                    <span style={styles.ingredientName}>{ingredient.ingredientName}</span>
                    <span style={styles.ingredientQuantity}>
                      {ingredient.quantity} {ingredient.unit}
                    </span>
                    {isComplete && (
                      <span style={styles.checkmarkBrown}>✓</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Continue button */}
        <div style={styles.continueContainer}>
          <button
            onClick={() => setWizardState(prev => ({ ...prev, step: 'timesProduced' }))}
            disabled={!allDoughIngredientsComplete()}
            style={{
              ...styles.continueButton,
              backgroundColor: allDoughIngredientsComplete() ? '#8B4513' : '#cccccc',
              cursor: allDoughIngredientsComplete() ? 'pointer' : 'not-allowed'
            }}
          >
            CONTINUE
          </button>
        </div>
      </div>
    );
  };

  // Step 5: Trace Code Selection (shared by component, filling, and dough flows)
  const renderTraceCodeSelectionStep = () => {
    const isFilling = wizardState.batchType === 'filling';
    const isDough = wizardState.batchType === 'dough';
    const ingredientName = isDough
      ? wizardState.selectedDoughIngredient?.ingredientName || ''
      : isFilling
        ? wizardState.selectedFillingIngredient?.name || ''
        : wizardState.selectedIngredient?.name || '';
    const currentlySelected = isDough
      ? wizardState.doughIngredientTraceCodes.get(ingredientName) || []
      : isFilling
        ? wizardState.fillingIngredientTraceCodes.get(ingredientName) || []
        : wizardState.ingredientTraceCodes.get(ingredientName) || [];

    const handleToggle = isDough
      ? handleDoughTraceCodeToggle
      : isFilling
        ? handleFillingTraceCodeToggle
        : handleTraceCodeToggle;
    const handleDone = isDough
      ? handleDoneSelectingDoughTraceCodes
      : isFilling
        ? () => setWizardState(prev => ({
            ...prev,
            step: 'fillingIngredientList',
            selectedFillingIngredient: null,
            availableTraceCodes: []
          }))
        : handleDoneSelectingTraceCodes;

    return (
      <div style={styles.stepContainerScrollable}>
        <button onClick={handleDone} style={styles.backButton}>
          ← Back
        </button>

        <h2 style={styles.stepTitle}>{ingredientName}</h2>
        <p style={styles.stepSubtitle}>Select lot numbers used</p>

        <div style={styles.scrollableListArea}>
          {wizardState.loadingTraceCodes ? (
            <div style={styles.loadingContainer}>
              <div style={styles.spinner} />
              <p style={styles.loadingText}>Loading trace codes...</p>
            </div>
          ) : wizardState.error ? (
            <div style={styles.errorContainer}>
              <p style={styles.errorText}>{wizardState.error}</p>
            </div>
          ) : wizardState.availableTraceCodes.length === 0 ? (
            <div style={styles.emptyContainer}>
              <p style={styles.emptyMessage}>No trace codes available for this ingredient</p>
            </div>
          ) : (
            <div style={styles.traceCodeList}>
              {wizardState.availableTraceCodes.map((lot, index) => (
                <label
                  key={`${lot.lotNumber}-${index}`}
                  style={{
                    ...styles.traceCodeItem,
                    backgroundColor: currentlySelected.includes(lot.lotNumber) ? '#e3f2fd' : 'white'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={currentlySelected.includes(lot.lotNumber)}
                    onChange={() => handleToggle(lot.lotNumber)}
                    style={styles.checkbox}
                  />
                  <span style={styles.lotNumber}>{lot.lotNumber}</span>
                  {lot.receivingDate && (
                    <span style={styles.receivingDate}>
                      {(() => {
                        const d = new Date(lot.receivingDate);
                        return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()).toLocaleDateString();
                      })()}
                    </span>
                  )}
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Done button */}
        <div style={styles.continueContainer}>
          <button
            onClick={handleDone}
            style={styles.doneButton}
          >
            DONE
          </button>
        </div>
      </div>
    );
  };

  // Step 5b: Select Component Batch (for filling component slots)
  const renderSelectComponentBatchStep = () => {
    const slotName = wizardState.selectedComponentSlot?.name || '';
    const currentlySelected = wizardState.fillingComponentBatches.get(slotName) || '';

    return (
      <div style={styles.stepContainerScrollable}>
        <button onClick={handleBack} style={styles.backButton}>
          ← Back
        </button>

        <h2 style={styles.stepTitle}>{slotName}</h2>
        <p style={styles.stepSubtitle}>Select a component batch</p>

        <div style={styles.scrollableListArea}>
          {wizardState.loadingComponentBatches ? (
            <div style={styles.loadingContainer}>
              <div style={styles.spinner} />
              <p style={styles.loadingText}>Loading component batches...</p>
            </div>
          ) : wizardState.error ? (
            <div style={styles.errorContainer}>
              <p style={styles.errorText}>{wizardState.error}</p>
            </div>
          ) : wizardState.availableComponentBatchesForSlot.length === 0 ? (
            <div style={styles.emptyContainer}>
              <p style={styles.emptyMessage}>No component batches available for {slotName}</p>
            </div>
          ) : (
            <div style={styles.componentBatchList}>
              {wizardState.availableComponentBatchesForSlot.map(batch => (
                <label
                  key={batch.id}
                  style={{
                    ...styles.componentBatchItem,
                    backgroundColor: currentlySelected === batch.id ? '#e3f2fd' : 'white',
                    borderColor: currentlySelected === batch.id ? '#2196F3' : '#ddd'
                  }}
                >
                  <input
                    type="radio"
                    name="componentBatch"
                    checked={currentlySelected === batch.id}
                    onChange={() => handleComponentBatchSelect(batch.id)}
                    style={styles.radio}
                  />
                  <div style={styles.componentBatchInfo}>
                    <span style={styles.componentBatchLot}>{batch.batchLotNumber}</span>
                    <span style={styles.componentBatchMeta}>
                      {batch.dateOfProduction && new Date(batch.dateOfProduction).toLocaleDateString()}
                      {batch.preparedBy && ` • ${batch.preparedBy}`}
                    </span>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Done button */}
        <div style={styles.continueContainer}>
          <button
            onClick={handleDoneSelectingComponentBatch}
            style={styles.doneButton}
          >
            DONE
          </button>
        </div>
      </div>
    );
  };

  // Step 7: Times Produced Input (shared by component, filling, dough, and product flows)
  const renderTimesProducedStep = () => {
    const isFilling = wizardState.batchType === 'filling';
    const isDough = wizardState.batchType === 'dough';
    const isProduct = wizardState.batchType === 'product';
    const handleCreate = isProduct
      ? handleCreateProductBatch
      : isDough
        ? handleCreateDoughBatch
        : isFilling
          ? handleCreateFillingBatch
          : handleCreateBatch;

    return (
      <div style={styles.stepContainer}>
        <button onClick={handleBack} style={styles.backButton}>
          ← Back
        </button>

        <h2 style={styles.stepTitle}>How many times produced?</h2>

        {wizardState.error && (
          <div style={styles.errorBanner}>
            <span>{wizardState.error}</span>
            <button
              onClick={() => setWizardState(prev => ({ ...prev, error: null }))}
              style={styles.errorDismiss}
            >
              ×
            </button>
          </div>
        )}

        <div style={styles.timesProducedContainer}>
          <div style={styles.timesProducedControls}>
            <button
              onClick={() => setWizardState(prev => ({
                ...prev,
                timesProduced: Math.max(1, prev.timesProduced - 1)
              }))}
              style={styles.incrementButton}
            >
              −
            </button>

            <input
              type="number"
              value={wizardState.timesProduced}
              onChange={e => setWizardState(prev => ({
                ...prev,
                timesProduced: Math.max(1, parseInt(e.target.value) || 1)
              }))}
              style={styles.timesProducedInput}
              min="1"
            />

            <button
              onClick={() => setWizardState(prev => ({
                ...prev,
                timesProduced: prev.timesProduced + 1
              }))}
              style={styles.incrementButton}
            >
              +
            </button>
          </div>

          <button
            onClick={handleCreate}
            style={styles.createBatchButton}
          >
            CREATE BATCH
          </button>
        </div>
      </div>
    );
  };

  // Step 8: Creating State (Loading)
  const renderCreatingStep = () => (
    <div style={styles.centeredContainer}>
      <div style={styles.loadingContainer}>
        <div style={styles.spinnerLarge} />
        <p style={styles.creatingText}>Creating batch...</p>
      </div>
    </div>
  );

  // Step 9: Summary Card
  const renderSummaryStep = () => (
    <div style={styles.stepContainer}>
      <div style={styles.summaryCard}>
        <div style={styles.summaryIconContainer}>
          <span style={styles.summaryIcon}>✓</span>
        </div>

        <h2 style={styles.summaryTitle}>Batch Created!</h2>

        <div style={styles.summaryDetails}>
          <div style={styles.summaryRow}>
            <span style={styles.summaryLabel}>Product:</span>
            <span style={styles.summaryValue}>{wizardState.createdBatch?.productName}</span>
          </div>
          <div style={styles.summaryRow}>
            <span style={styles.summaryLabel}>Lot #:</span>
            <span style={styles.summaryValueMono}>
              {wizardState.createdBatch && 'batchLotNumber' in wizardState.createdBatch
                ? wizardState.createdBatch.batchLotNumber
                : wizardState.createdBatch && 'batchNumber' in wizardState.createdBatch
                  ? wizardState.createdBatch.batchNumber
                  : ''}
            </span>
          </div>
          <div style={styles.summaryRow}>
            <span style={styles.summaryLabel}>Date:</span>
            <span style={styles.summaryValue}>{wizardState.createdBatch?.dateOfProduction}</span>
          </div>
          <div style={styles.summaryRow}>
            <span style={styles.summaryLabel}>Times Produced:</span>
            <span style={styles.summaryValue}>×{wizardState.createdBatch?.timesProduced}</span>
          </div>
        </div>
      </div>

      <div style={styles.continueContainer}>
        <button onClick={resetWizard} style={styles.createAnotherButton}>
          CREATE ANOTHER
        </button>
      </div>
    </div>
  );

  // Render Recent Batches Section
  const renderRecentBatches = () => {
    const sortedComponentBatches = [...componentBatches]
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 10);

    const sortedFillingBatches = [...fillingBatches]
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 10);

    const sortedDoughBatches = [...doughBatches]
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 10);

    const sortedBatchRecords = [...batchRecords]
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 10);

    return (
      <>
        {/* Components Section */}
        <div style={styles.recentBatchesCardCyan}>
          <div
            style={styles.collapsibleHeader}
            onClick={() => setComponentBatchesExpanded(!componentBatchesExpanded)}
          >
            <h2 style={styles.recentBatchesTitleWhite}>Recent Batches - Components</h2>
            <span style={{ ...styles.chevronWhite, transform: componentBatchesExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
          </div>

          {componentBatchesExpanded && (
            componentBatchesLoading ? (
              <p style={styles.loadingTextWhite}>Loading batches...</p>
            ) : sortedComponentBatches.length === 0 ? (
              <p style={styles.emptyMessageWhite}>No component batches created yet.</p>
            ) : (
              <div style={styles.recentBatchesGrid}>
                {sortedComponentBatches.map(batch => (
                  <div
                    key={batch.id}
                    style={{ ...styles.batchCard, cursor: 'pointer' }}
                    onClick={() => handleBatchCardClick('component', batch)}
                  >
                    <h4 style={styles.batchCardTitle}>{batch.productName}</h4>
                    <p style={styles.batchCardDetail}>
                      <span style={styles.batchCardLabel}>Lot:</span>
                      <span style={styles.batchCardValueMono}>{batch.batchLotNumber}</span>
                    </p>
                    <p style={styles.batchCardDetail}>
                      <span style={styles.batchCardLabel}>Date:</span>
                      <span>{batch.dateOfProduction?.split('T')[0]}</span>
                    </p>
                    <p style={styles.batchCardDetail}>
                      <span style={styles.batchCardLabel}>Multiplier:</span>
                      <span>×{batch.timesProduced}</span>
                    </p>
                  </div>
                ))}
              </div>
            )
          )}
        </div>

        {/* Fillings Section */}
        <div style={styles.recentBatchesCardOrange}>
          <div
            style={styles.collapsibleHeader}
            onClick={() => setFillingBatchesExpanded(!fillingBatchesExpanded)}
          >
            <h2 style={styles.recentBatchesTitleWhite}>Recent Batches - Fillings</h2>
            <span style={{ ...styles.chevronWhite, transform: fillingBatchesExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
          </div>

          {fillingBatchesExpanded && (
            fillingBatchesLoading ? (
              <p style={styles.loadingTextWhite}>Loading batches...</p>
            ) : sortedFillingBatches.length === 0 ? (
              <p style={styles.emptyMessageWhite}>No filling batches created yet.</p>
            ) : (
              <div style={styles.recentBatchesGrid}>
                {sortedFillingBatches.map(batch => (
                  <div
                    key={batch.id}
                    style={{ ...styles.fillingBatchCard, cursor: 'pointer' }}
                    onClick={() => handleBatchCardClick('filling', batch)}
                  >
                    <h4 style={styles.batchCardTitle}>{batch.productName}</h4>
                    <p style={styles.batchCardDetail}>
                      <span style={styles.batchCardLabel}>Lot:</span>
                      <span style={styles.batchCardValueMono}>{batch.batchLotNumber}</span>
                    </p>
                    <p style={styles.batchCardDetail}>
                      <span style={styles.batchCardLabel}>Date:</span>
                      <span>{batch.dateOfProduction?.split('T')[0]}</span>
                    </p>
                    <p style={styles.batchCardDetail}>
                      <span style={styles.batchCardLabel}>Multiplier:</span>
                      <span>×{batch.timesProduced}</span>
                    </p>
                  </div>
                ))}
              </div>
            )
          )}
        </div>

        {/* Dough Section */}
        <div style={styles.recentBatchesCardBrown}>
          <div
            style={styles.collapsibleHeader}
            onClick={() => setDoughBatchesExpanded(!doughBatchesExpanded)}
          >
            <h2 style={styles.recentBatchesTitleWhite}>Recent Batches - Dough</h2>
            <span style={{ ...styles.chevronWhite, transform: doughBatchesExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
          </div>

          {doughBatchesExpanded && (
            doughBatchesLoading ? (
              <p style={styles.loadingTextWhite}>Loading batches...</p>
            ) : sortedDoughBatches.length === 0 ? (
              <p style={styles.emptyMessageWhite}>No dough batches created yet.</p>
            ) : (
              <div style={styles.recentBatchesGrid}>
                {sortedDoughBatches.map(batch => (
                  <div
                    key={batch.id}
                    style={{ ...styles.doughBatchCard, cursor: 'pointer' }}
                    onClick={() => handleBatchCardClick('dough', batch)}
                  >
                    <h4 style={styles.batchCardTitle}>{batch.productName}</h4>
                    <p style={styles.batchCardDetail}>
                      <span style={styles.batchCardLabel}>Lot:</span>
                      <span style={styles.batchCardValueMono}>{batch.batchNumber}</span>
                    </p>
                    <p style={styles.batchCardDetail}>
                      <span style={styles.batchCardLabel}>Date:</span>
                      <span>{batch.dateOfProduction?.split('T')[0]}</span>
                    </p>
                    <p style={styles.batchCardDetail}>
                      <span style={styles.batchCardLabel}>Multiplier:</span>
                      <span>×{batch.timesProduced}</span>
                    </p>
                  </div>
                ))}
              </div>
            )
          )}
        </div>

        {/* Products Section */}
        <div style={styles.recentBatchesCardPurple}>
          <div
            style={styles.collapsibleHeader}
            onClick={() => setProductBatchesExpanded(!productBatchesExpanded)}
          >
            <h2 style={styles.recentBatchesTitleWhite}>Recent Batches - Products</h2>
            <span style={{ ...styles.chevronWhite, transform: productBatchesExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
          </div>

          {productBatchesExpanded && (
            batchRecordsLoading ? (
              <p style={styles.loadingTextWhite}>Loading batches...</p>
            ) : sortedBatchRecords.length === 0 ? (
              <p style={styles.emptyMessageWhite}>No product batches created yet.</p>
            ) : (
              <div style={styles.recentBatchesGrid}>
                {sortedBatchRecords.map(batch => (
                  <div
                    key={batch.id}
                    style={{ ...styles.productBatchCard, cursor: 'pointer' }}
                    onClick={() => handleBatchCardClick('product', batch)}
                  >
                    <h4 style={styles.batchCardTitle}>{batch.productName}</h4>
                    <p style={styles.batchCardDetail}>
                      <span style={styles.batchCardLabel}>Lot:</span>
                      <span style={styles.batchCardValueMono}>{batch.batchLotNumber}</span>
                    </p>
                    <p style={styles.batchCardDetail}>
                      <span style={styles.batchCardLabel}>Date:</span>
                      <span>{batch.dateOfProduction?.split('T')[0]}</span>
                    </p>
                    <p style={styles.batchCardDetail}>
                      <span style={styles.batchCardLabel}>Multiplier:</span>
                      <span>×{batch.timesProduced}</span>
                    </p>
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      </>
    );
  };

  // Shipment Step 1: Select Supplier
  const renderShipmentSelectSupplierStep = () => {
    // Check which suppliers have entries recorded
    const getSuppliersWithEntries = () => {
      const supplierIds = new Set<string>();
      wizardState.shipmentEntries.forEach((_, supplierId) => {
        supplierIds.add(supplierId);
      });
      return supplierIds;
    };

    const suppliersWithEntries = getSuppliersWithEntries();

    return (
      <div style={styles.stepContainer}>
        <button onClick={handleBack} style={styles.backButton}>
          ← Back
        </button>

        <h2 style={styles.stepTitle}>Select Supplier</h2>

        {wizardState.error && (
          <div style={{
            backgroundColor: '#ffebee',
            border: '1px solid #f44336',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '16px',
            color: '#c62828'
          }}>
            {wizardState.error}
          </div>
        )}

        {wizardState.shipmentLoadingSuppliers ? (
          <div style={styles.centeredContainer}>
            <div style={styles.spinner}></div>
            <p style={styles.loadingText}>Loading suppliers...</p>
          </div>
        ) : wizardState.shipmentSuppliers.length === 0 ? (
          <p style={styles.emptyMessage}>No suppliers available.</p>
        ) : (
          <div style={styles.componentGrid}>
            {wizardState.shipmentSuppliers.map(supplier => (
              <button
                key={supplier.id}
                onClick={() => handleShipmentSupplierSelect(supplier)}
                style={{
                  ...styles.shipmentSupplierButton,
                  ...(suppliersWithEntries.has(supplier.id) ? { borderColor: '#009688', borderWidth: '3px' } : {})
                }}
              >
                {suppliersWithEntries.has(supplier.id) && (
                  <span style={styles.checkmark}>✓</span>
                )}
                {supplier.supplierName}
              </button>
            ))}
          </div>
        )}

        <div style={styles.doneButtonContainer}>
          <button
            onClick={handleShipmentDone}
            style={styles.shipmentPrimaryButton}
          >
            DONE
          </button>
        </div>
      </div>
    );
  };

  // Shipment Step 2: Select Ingredient
  const renderShipmentSelectIngredientStep = () => {
    const supplier = wizardState.shipmentSelectedSupplier;
    if (!supplier) return null;

    // Filter ingredients based on search
    const filteredIngredients = supplier.ingredientsSupplied.filter(ingredient =>
      ingredient.toLowerCase().includes(wizardState.shipmentIngredientSearch.toLowerCase())
    );

    // Check which ingredients have lots recorded for this supplier
    const getIngredientsWithLots = () => {
      const ingredientNames = new Set<string>();
      const supplierEntries = wizardState.shipmentEntries.get(supplier.id);
      if (supplierEntries) {
        supplierEntries.forEach((lots, ingredientName) => {
          if (lots.length > 0) {
            ingredientNames.add(ingredientName);
          }
        });
      }
      return ingredientNames;
    };

    const ingredientsWithLots = getIngredientsWithLots();

    return (
      <div style={styles.stepContainer}>
        <button onClick={handleBack} style={styles.backButton}>
          ← Back
        </button>

        <h2 style={styles.stepTitle}>{supplier.supplierName}</h2>

        {/* Search input */}
        <div style={styles.shipmentSearchContainer}>
          <input
            type="text"
            placeholder="Search ingredients..."
            value={wizardState.shipmentIngredientSearch}
            onChange={(e) => setWizardState(prev => ({ ...prev, shipmentIngredientSearch: e.target.value }))}
            style={styles.shipmentSearchInput}
          />
        </div>

        {filteredIngredients.length === 0 ? (
          <p style={styles.emptyMessage}>No ingredients found.</p>
        ) : (
          <div style={styles.componentGrid}>
            {filteredIngredients.map(ingredient => (
              <button
                key={ingredient}
                onClick={() => handleShipmentIngredientSelect(ingredient)}
                style={{
                  ...styles.shipmentSupplierButton,
                  ...(ingredientsWithLots.has(ingredient) ? { borderColor: '#009688', borderWidth: '3px' } : {})
                }}
              >
                {ingredientsWithLots.has(ingredient) && (
                  <span style={styles.checkmark}>✓</span>
                )}
                {ingredient}
              </button>
            ))}
          </div>
        )}

        {/* Done button to return to supplier selection */}
        <div style={styles.doneButtonContainer}>
          <button
            onClick={() => setWizardState(prev => ({ ...prev, step: 'shipmentSelectSupplier', shipmentSelectedIngredient: null, shipmentIngredientSearch: '' }))}
            style={styles.shipmentPrimaryButton}
          >
            DONE
          </button>
        </div>
      </div>
    );
  };

  // Shipment Step 3: Lot Number
  const renderShipmentLotNumberStep = () => {
    const supplier = wizardState.shipmentSelectedSupplier;
    const ingredientName = wizardState.shipmentSelectedIngredient;
    if (!supplier || !ingredientName) return null;

    // Get existing lots for this supplier/ingredient
    const existingLots: ShipmentLotEntry[] =
      wizardState.shipmentEntries.get(supplier.id)?.get(ingredientName) || [];

    const handleNext = () => {
      if (wizardState.shipmentCurrentLot.lotNumber.trim()) {
        setWizardState(prev => ({ ...prev, step: 'shipmentQuantity' }));
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleNext();
      }
    };

    return (
      <div style={styles.stepContainer}>
        <button onClick={handleBack} style={styles.backButton}>
          ← Back
        </button>

        <h2 style={styles.stepTitle}>{ingredientName}</h2>
        <p style={styles.shipmentSubtitle}>{supplier.supplierName}</p>

        {/* Existing lots as chips */}
        {existingLots.length > 0 && (
          <div style={styles.shipmentExistingLots}>
            <p style={styles.shipmentExistingLotsTitle}>Lots Added:</p>
            <div style={styles.shipmentLotChipsContainer}>
              {existingLots.map(lot => (
                <div key={lot.id} style={{ ...styles.shipmentLotChip, cursor: 'pointer' }} onClick={() => handleEditShipmentLot(lot.id)}>
                  <span>{lot.lotNumber} ({lot.quantity} {lot.unit})</span>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleRemoveShipmentLot(lot.id); }}
                    style={styles.shipmentLotRemoveButton}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Lot Number Input */}
        <div style={styles.shipmentSingleFieldContainer}>
          <label style={styles.shipmentFieldLabel}>Enter Lot Number</label>
          <input
            type="text"
            value={wizardState.shipmentCurrentLot.lotNumber}
            onChange={(e) => setWizardState(prev => ({
              ...prev,
              shipmentCurrentLot: { ...prev.shipmentCurrentLot, lotNumber: e.target.value }
            }))}
            onKeyDown={handleKeyDown}
            style={styles.shipmentLargeInput}
            placeholder="Enter lot number"
            autoFocus
          />
        </div>

        {/* Button row */}
        <div style={styles.shipmentButtonRow}>
          <button
            onClick={handleNext}
            disabled={!wizardState.shipmentCurrentLot.lotNumber.trim()}
            style={{
              ...styles.shipmentPrimaryButton,
              opacity: wizardState.shipmentCurrentLot.lotNumber.trim() ? 1 : 0.5,
              cursor: wizardState.shipmentCurrentLot.lotNumber.trim() ? 'pointer' : 'not-allowed'
            }}
          >
            NEXT
          </button>
          <button
            onClick={() => setWizardState(prev => ({ ...prev, step: 'shipmentSelectIngredient' }))}
            style={styles.shipmentSecondaryButton}
          >
            BACK TO INGREDIENTS
          </button>
        </div>
      </div>
    );
  };

  // Shipment Step 4: Quantity with Package Sizes
  const renderShipmentQuantityStep = () => {
    const unitOptions: QuantityUnit[] = ['lbs', 'kg', 'oz', 'g', 'gallons', 'liters', 'L', 'mL'];
    const { shipmentSelectedSupplier, shipmentSelectedIngredient, shipmentCurrentLot, shipmentAvailablePackageSizes, shipmentLoadingPackageSizes, shipmentShowNewPackageForm } = wizardState;

    const handlePackageSizeSelect = (sizeId: string) => {
      const selectedSize = shipmentAvailablePackageSizes.find(s => s.id === sizeId);
      if (selectedSize) {
        const totalQuantity = shipmentCurrentLot.packageCount * selectedSize.packageSize;
        setWizardState(prev => ({
          ...prev,
          shipmentCurrentLot: {
            ...prev.shipmentCurrentLot,
            packageSizeId: sizeId,
            quantity: totalQuantity,
            unit: selectedSize.packageUnit as QuantityUnit,
            manualPackageSize: null,
            manualPackageUnit: null,
            manualPackageLabel: ''
          },
          shipmentShowNewPackageForm: false
        }));
      }
    };

    const handlePackageCountChange = (count: number) => {
      const selectedSize = shipmentAvailablePackageSizes.find(s => s.id === shipmentCurrentLot.packageSizeId);
      let totalQuantity = count;

      if (selectedSize) {
        totalQuantity = count * selectedSize.packageSize;
      } else if (shipmentCurrentLot.manualPackageSize) {
        totalQuantity = count * shipmentCurrentLot.manualPackageSize;
      }

      setWizardState(prev => ({
        ...prev,
        shipmentCurrentLot: {
          ...prev.shipmentCurrentLot,
          packageCount: count,
          quantity: totalQuantity
        }
      }));
    };

    const handleManualSizeChange = (size: number) => {
      const totalQuantity = shipmentCurrentLot.packageCount * size;
      setWizardState(prev => ({
        ...prev,
        shipmentCurrentLot: {
          ...prev.shipmentCurrentLot,
          manualPackageSize: size,
          quantity: totalQuantity,
          packageSizeId: null
        }
      }));
    };

    const handleManualUnitChange = (unit: string) => {
      setWizardState(prev => ({
        ...prev,
        shipmentCurrentLot: {
          ...prev.shipmentCurrentLot,
          manualPackageUnit: unit,
          unit: unit as QuantityUnit,
          packageSizeId: null
        }
      }));
    };

    const isEditing = !!shipmentCurrentLot.id;

    const handleNext = async () => {
      if (isEditing) {
        // Edit mode: update the existing lot and return to lot number step
        handleAddShipmentLot();
        setWizardState(prev => ({ ...prev, step: 'shipmentLotNumber' }));
        return;
      }

      // If using manual entry with a new size, save it first
      if (shipmentShowNewPackageForm && shipmentCurrentLot.manualPackageSize && shipmentCurrentLot.manualPackageUnit && shipmentSelectedSupplier && shipmentSelectedIngredient) {
        try {
          await ingredientPackageSizeApiService.createPackageSize(
            shipmentSelectedSupplier.id,
            shipmentSelectedIngredient,
            shipmentCurrentLot.manualPackageSize,
            shipmentCurrentLot.manualPackageUnit,
            shipmentCurrentLot.manualPackageLabel || undefined
          );
        } catch (error) {
          console.error('Error saving package size:', error);
          // Continue anyway - the receiving log is more important
        }
      }
      // Fire-and-forget: save storage type
      if (wizardState.shipmentSupplierIngredientId) {
        ingredientPackageSizeApiService.updateStorageType(
          wizardState.shipmentSupplierIngredientId,
          wizardState.shipmentIngredientStorageType
        ).catch(error => console.error('Error saving storage type:', error));
      }
      setWizardState(prev => ({ ...prev, step: 'shipmentCondition' }));
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && canProceed) {
        handleNext();
      }
    };

    // Determine if we can proceed - need valid quantity AND either selected package size OR manual entry
    // For manual entry, fall back to shipmentCurrentLot.unit if manualPackageUnit not explicitly set (matching UI behavior)
    const effectiveManualUnit = shipmentCurrentLot.manualPackageUnit || shipmentCurrentLot.unit;
    const hasValidPackageSize = shipmentCurrentLot.packageSizeId !== null ||
      (shipmentCurrentLot.manualPackageSize !== null && shipmentCurrentLot.manualPackageSize > 0 &&
       effectiveManualUnit && effectiveManualUnit.trim() !== '');
    const canProceed = shipmentCurrentLot.quantity > 0 && shipmentCurrentLot.packageCount > 0 && hasValidPackageSize;

    // Get selected package size for display
    const selectedPackageSize = shipmentAvailablePackageSizes.find(s => s.id === shipmentCurrentLot.packageSizeId);
    const effectiveSize = selectedPackageSize?.packageSize || shipmentCurrentLot.manualPackageSize || 0;
    const effectiveUnit = selectedPackageSize?.packageUnit || shipmentCurrentLot.manualPackageUnit || shipmentCurrentLot.unit;

    return (
      <div style={styles.stepContainer}>
        <button onClick={handleBack} style={styles.backButton}>
          ← Back
        </button>

        <h2 style={styles.stepTitle}>Quantity</h2>
        <p style={styles.shipmentSubtitle}>Lot: {shipmentCurrentLot.lotNumber}</p>

        {shipmentLoadingPackageSizes ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>Loading package sizes...</div>
        ) : (
          <div style={styles.shipmentSingleFieldContainer}>
            {/* Package Size Selection */}
            {!shipmentShowNewPackageForm && shipmentAvailablePackageSizes.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Package Size</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                  {shipmentAvailablePackageSizes.map(size => (
                    <button
                      key={size.id}
                      onClick={() => handlePackageSizeSelect(size.id)}
                      style={{
                        padding: '12px 20px',
                        fontSize: '1.1rem',
                        border: shipmentCurrentLot.packageSizeId === size.id ? '3px solid #009688' : '2px solid #ddd',
                        borderRadius: '8px',
                        background: shipmentCurrentLot.packageSizeId === size.id ? '#e0f2f1' : 'white',
                        cursor: 'pointer'
                      }}
                    >
                      {size.packageSize}{size.packageUnit} {size.label ? `(${size.label})` : ''}
                    </button>
                  ))}
                  <button
                    onClick={() => setWizardState(prev => ({
                      ...prev,
                      shipmentShowNewPackageForm: true,
                      shipmentCurrentLot: {
                        ...prev.shipmentCurrentLot,
                        packageSizeId: null,
                        manualPackageUnit: prev.shipmentCurrentLot.manualPackageUnit || prev.shipmentCurrentLot.unit
                      }
                    }))}
                    style={{
                      padding: '12px 20px',
                      fontSize: '1.1rem',
                      border: '2px dashed #009688',
                      borderRadius: '8px',
                      background: 'white',
                      cursor: 'pointer',
                      color: '#009688'
                    }}
                  >
                    + New Size
                  </button>
                </div>
              </div>
            )}

            {/* Manual Package Size Entry Form */}
            {shipmentShowNewPackageForm && (
              <div style={{ marginBottom: '20px', padding: '15px', background: '#f5f5f5', borderRadius: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <label style={{ fontWeight: 'bold' }}>New Package Size</label>
                  {shipmentAvailablePackageSizes.length > 0 && (
                    <button
                      onClick={() => setWizardState(prev => ({ ...prev, shipmentShowNewPackageForm: false }))}
                      style={{ background: 'none', border: 'none', color: '#009688', cursor: 'pointer' }}
                    >
                      Cancel
                    </button>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                  <input
                    type="number"
                    value={shipmentCurrentLot.manualPackageSize || ''}
                    onChange={(e) => handleManualSizeChange(parseFloat(e.target.value) || 0)}
                    onKeyDown={handleKeyDown}
                    style={{ ...styles.shipmentLargeInput, flex: 1 }}
                    placeholder="Size (e.g. 50)"
                    autoFocus
                  />
                  <select
                    value={shipmentCurrentLot.manualPackageUnit || shipmentCurrentLot.unit}
                    onChange={(e) => handleManualUnitChange(e.target.value)}
                    style={styles.shipmentLargeSelect}
                  >
                    {unitOptions.map(unit => (
                      <option key={unit} value={unit}>{unit}</option>
                    ))}
                  </select>
                </div>
                <input
                  type="text"
                  value={shipmentCurrentLot.manualPackageLabel}
                  onChange={(e) => setWizardState(prev => ({
                    ...prev,
                    shipmentCurrentLot: { ...prev.shipmentCurrentLot, manualPackageLabel: e.target.value }
                  }))}
                  style={{ ...styles.shipmentLargeInput, marginTop: '10px' }}
                  placeholder="Label (e.g. 'bag', 'jug') - optional"
                />
              </div>
            )}

            {/* Package Count */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>How many packages?</label>
              <input
                type="number"
                value={shipmentCurrentLot.packageCount || ''}
                onChange={(e) => handlePackageCountChange(parseInt(e.target.value) || 0)}
                onKeyDown={handleKeyDown}
                style={styles.shipmentLargeInput}
                placeholder="1"
                min="1"
              />
            </div>

            {/* Storage Type */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Storage Type</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', maxWidth: '500px' }}>
                {([
                  { value: 'non_refrigerated' as StorageType, label: 'Not Refrigerated' },
                  { value: 'refrigerated' as StorageType, label: 'Refrigerated' },
                  { value: 'frozen' as StorageType, label: 'Frozen' },
                ]).map(({ value, label }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setWizardState(prev => ({
                      ...prev,
                      shipmentIngredientStorageType: value,
                      shipmentFrozenSolidConfirmed: null,
                      shipmentCorrectiveActionRequired: false
                    }))}
                    style={{
                      padding: '16px 20px',
                      fontSize: '1.1rem',
                      fontWeight: '500',
                      backgroundColor: wizardState.shipmentIngredientStorageType === value ? '#009688' : 'white',
                      color: wizardState.shipmentIngredientStorageType === value ? 'white' : '#333',
                      border: '3px solid #009688',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Total Calculation Display */}
            {effectiveSize > 0 && shipmentCurrentLot.packageCount > 0 && (
              <div style={{
                padding: '15px',
                background: '#e8f5e9',
                borderRadius: '8px',
                textAlign: 'center',
                fontSize: '1.2rem',
                marginBottom: '20px'
              }}>
                <strong>Total: </strong>
                {shipmentCurrentLot.packageCount} × {effectiveSize}{effectiveUnit} = <strong>{shipmentCurrentLot.quantity} {effectiveUnit}</strong>
              </div>
            )}
          </div>
        )}

        <div style={styles.shipmentButtonRow}>
          <button
            onClick={handleNext}
            disabled={!canProceed}
            style={{
              ...styles.shipmentPrimaryButton,
              opacity: canProceed ? 1 : 0.5,
              cursor: canProceed ? 'pointer' : 'not-allowed'
            }}
          >
            {isEditing ? 'SAVE' : 'NEXT'}
          </button>
        </div>
      </div>
    );
  };

  // Shipment Step 5: Condition
  const renderShipmentConditionStep = () => {
    const conditionOptions = ['Good', 'Acceptable', 'Damaged', 'Rejected'];

    const handleConditionSelect = (condition: string) => {
      setWizardState(prev => ({
        ...prev,
        shipmentCurrentLot: { ...prev.shipmentCurrentLot, conditionUponArrival: condition },
        step: 'shipmentTemperature'
      }));
    };

    return (
      <div style={styles.stepContainer}>
        <button onClick={handleBack} style={styles.backButton}>
          ← Back
        </button>

        <h2 style={styles.stepTitle}>Condition Upon Arrival</h2>
        <p style={styles.shipmentSubtitle}>Lot: {wizardState.shipmentCurrentLot.lotNumber}</p>

        <div style={styles.shipmentConditionGrid}>
          {conditionOptions.map(condition => (
            <button
              key={condition}
              onClick={() => handleConditionSelect(condition)}
              style={{
                ...styles.shipmentConditionButton,
                ...(wizardState.shipmentCurrentLot.conditionUponArrival === condition
                  ? styles.shipmentConditionButtonActive
                  : {})
              }}
            >
              {condition}
            </button>
          ))}
        </div>
      </div>
    );
  };

  // Shipment Step 6: Temperature
  const renderShipmentTemperatureStep = () => {
    const { receivingTemperature, temperatureUnit } = wizardState.shipmentCurrentLot;
    const isFrozen = wizardState.shipmentIngredientStorageType === 'frozen';

    // Validate temperature range: -40 to 200 for F, -40 to 100 for C
    const minTemp = -40;
    const maxTemp = temperatureUnit === 'F' ? 200 : 100;
    const isValidTemperature = !isNaN(receivingTemperature) &&
      receivingTemperature >= minTemp &&
      receivingTemperature <= maxTemp;

    // Convert to Fahrenheit for frozen threshold checks
    const tempF = temperatureUnit === 'C'
      ? (receivingTemperature * 9 / 5) + 32
      : receivingTemperature;

    const frozenNeedsConfirmation = isFrozen && tempF > 0 && tempF <= 10;
    const frozenTooWarm = isFrozen && tempF > 10;

    // Display thresholds in user's selected unit
    const displayTemp = (fahrenheit: number) =>
      temperatureUnit === 'C'
        ? `${Math.round((fahrenheit - 32) * 5 / 9)}°C`
        : `${fahrenheit}°F`;

    // Determine if NEXT is allowed
    const frozenConfirmationNeeded = frozenNeedsConfirmation && wizardState.shipmentFrozenSolidConfirmed === null;
    const canProceedTemp = isValidTemperature && !frozenConfirmationNeeded;

    const handleNext = () => {
      if (!canProceedTemp) return;
      // Set corrective action required flag for frozen items
      const correctiveRequired = isFrozen && (
        frozenTooWarm ||
        (frozenNeedsConfirmation && wizardState.shipmentFrozenSolidConfirmed === false)
      );
      setWizardState(prev => ({
        ...prev,
        step: 'shipmentCorrectiveAction',
        shipmentCorrectiveActionRequired: correctiveRequired
      }));
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && canProceedTemp) {
        handleNext();
      }
    };

    return (
      <div style={styles.stepContainer}>
        <button onClick={handleBack} style={styles.backButton}>
          ← Back
        </button>

        <h2 style={styles.stepTitle}>Receiving Temperature</h2>
        <p style={styles.shipmentSubtitle}>Lot: {wizardState.shipmentCurrentLot.lotNumber}</p>

        <div style={styles.shipmentSingleFieldContainer}>
          <div style={styles.shipmentTemperatureRow}>
            <input
              type="number"
              value={wizardState.shipmentCurrentLot.receivingTemperature || ''}
              onChange={(e) => setWizardState(prev => ({
                ...prev,
                shipmentCurrentLot: { ...prev.shipmentCurrentLot, receivingTemperature: parseFloat(e.target.value) || 0 },
                shipmentFrozenSolidConfirmed: null
              }))}
              onKeyDown={handleKeyDown}
              style={{ ...styles.shipmentLargeInput, flex: 1 }}
              placeholder="40"
              autoFocus
            />
            <div style={styles.shipmentTempToggleLarge}>
              <button
                onClick={() => setWizardState(prev => ({
                  ...prev,
                  shipmentCurrentLot: { ...prev.shipmentCurrentLot, temperatureUnit: 'F' },
                  shipmentFrozenSolidConfirmed: null
                }))}
                style={{
                  ...styles.shipmentTempButtonLarge,
                  ...(wizardState.shipmentCurrentLot.temperatureUnit === 'F' ? styles.shipmentTempButtonActive : {})
                }}
              >
                °F
              </button>
              <button
                onClick={() => setWizardState(prev => ({
                  ...prev,
                  shipmentCurrentLot: { ...prev.shipmentCurrentLot, temperatureUnit: 'C' },
                  shipmentFrozenSolidConfirmed: null
                }))}
                style={{
                  ...styles.shipmentTempButtonLarge,
                  ...(wizardState.shipmentCurrentLot.temperatureUnit === 'C' ? styles.shipmentTempButtonActive : {})
                }}
              >
                °C
              </button>
            </div>
          </div>
          {!isValidTemperature && receivingTemperature !== 0 && (
            <p style={{ color: '#c62828', fontSize: '0.9rem', marginTop: '8px' }}>
              Temperature must be between {minTemp}° and {maxTemp}°{temperatureUnit}
            </p>
          )}

          {/* Frozen: needs confirmation (0-10°F) */}
          {isValidTemperature && frozenNeedsConfirmation && (
            <div style={{
              marginTop: '16px',
              padding: '16px',
              backgroundColor: '#fff8e1',
              border: '2px solid #ff8f00',
              borderRadius: '10px'
            }}>
              <p style={{ margin: '0 0 12px 0', fontWeight: '600', color: '#e65100' }}>
                Temperature is above {displayTemp(0)}. Is the product still frozen solid?
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <button
                  type="button"
                  onClick={() => setWizardState(prev => ({ ...prev, shipmentFrozenSolidConfirmed: true }))}
                  style={{
                    padding: '14px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    backgroundColor: wizardState.shipmentFrozenSolidConfirmed === true ? '#2e7d32' : 'white',
                    color: wizardState.shipmentFrozenSolidConfirmed === true ? 'white' : '#333',
                    border: '2px solid #2e7d32',
                    borderRadius: '10px',
                    cursor: 'pointer'
                  }}
                >
                  Yes, Frozen Solid
                </button>
                <button
                  type="button"
                  onClick={() => setWizardState(prev => ({ ...prev, shipmentFrozenSolidConfirmed: false }))}
                  style={{
                    padding: '14px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    backgroundColor: wizardState.shipmentFrozenSolidConfirmed === false ? '#c62828' : 'white',
                    color: wizardState.shipmentFrozenSolidConfirmed === false ? 'white' : '#333',
                    border: '2px solid #c62828',
                    borderRadius: '10px',
                    cursor: 'pointer'
                  }}
                >
                  No, Not Frozen Solid
                </button>
              </div>
              {wizardState.shipmentFrozenSolidConfirmed === false && (
                <p style={{ margin: '10px 0 0 0', color: '#c62828', fontSize: '0.9rem', fontWeight: '500' }}>
                  Corrective action will be required on the next step.
                </p>
              )}
            </div>
          )}

          {/* Frozen: too warm (>10°F) */}
          {isValidTemperature && frozenTooWarm && (
            <div style={{
              marginTop: '16px',
              padding: '16px',
              backgroundColor: '#ffebee',
              border: '2px solid #c62828',
              borderRadius: '10px'
            }}>
              <p style={{ margin: 0, fontWeight: '600', color: '#c62828' }}>
                Temperature exceeds {displayTemp(10)} for frozen product. Corrective action will be required.
              </p>
            </div>
          )}
        </div>

        <div style={styles.shipmentButtonRow}>
          <button
            onClick={handleNext}
            disabled={!canProceedTemp}
            style={{
              ...styles.shipmentPrimaryButton,
              opacity: canProceedTemp ? 1 : 0.5,
              cursor: canProceedTemp ? 'pointer' : 'not-allowed'
            }}
          >
            NEXT
          </button>
        </div>
      </div>
    );
  };

  // Shipment Step 7: Corrective Action
  const renderShipmentCorrectiveActionStep = () => {
    const isRequired = wizardState.shipmentCorrectiveActionRequired;
    const hasText = wizardState.shipmentCurrentLot.correctiveAction.trim().length > 0;
    const canAdd = !isRequired || hasText;

    const handleAddAndContinue = () => {
      if (canAdd) {
        handleAddShipmentLotAndReturn();
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && canAdd) {
        handleAddAndContinue();
      }
    };

    return (
      <div style={styles.stepContainer}>
        <button onClick={handleBack} style={styles.backButton}>
          ← Back
        </button>

        <h2 style={styles.stepTitle}>Corrective Action</h2>
        <p style={styles.shipmentSubtitle}>Lot: {wizardState.shipmentCurrentLot.lotNumber}</p>

        {isRequired && (
          <div style={{
            padding: '12px 16px',
            backgroundColor: '#ffebee',
            border: '2px solid #c62828',
            borderRadius: '10px',
            marginBottom: '16px'
          }}>
            <p style={{ margin: 0, fontWeight: '600', color: '#c62828' }}>
              Corrective action is required for this frozen product due to temperature.
            </p>
          </div>
        )}

        <div style={styles.shipmentSingleFieldContainer}>
          <label style={styles.shipmentFieldLabel}>
            {isRequired ? 'Required - describe corrective action taken' : 'Optional - enter if needed'}
          </label>
          <input
            type="text"
            value={wizardState.shipmentCurrentLot.correctiveAction}
            onChange={(e) => setWizardState(prev => ({
              ...prev,
              shipmentCurrentLot: { ...prev.shipmentCurrentLot, correctiveAction: e.target.value }
            }))}
            onKeyDown={handleKeyDown}
            style={styles.shipmentLargeInput}
            placeholder={isRequired ? 'Describe corrective action taken' : 'Enter corrective action if needed'}
            autoFocus
          />
        </div>

        <div style={styles.shipmentButtonRow}>
          <button
            onClick={handleAddAndContinue}
            disabled={!canAdd}
            style={{
              ...styles.shipmentPrimaryButton,
              opacity: canAdd ? 1 : 0.5,
              cursor: canAdd ? 'pointer' : 'not-allowed'
            }}
          >
            ADD LOT
          </button>
        </div>
      </div>
    );
  };

  // Shipment Summary Step
  const renderShipmentSummaryStep = () => (
    <div style={styles.centeredContainer}>
      <div style={styles.shipmentSummaryCard}>
        <div style={styles.shipmentSummaryIcon}>✓</div>
        <h2 style={styles.shipmentSummaryTitle}>Shipment Recorded!</h2>
        <p style={styles.shipmentSummaryText}>
          {wizardState.shipmentSavedCount} {wizardState.shipmentSavedCount === 1 ? 'lot' : 'lots'} saved to Ingredient Receiving Log
        </p>
        <button
          onClick={() => {
            // Reset wizard and close
            setWizardState(prev => ({
              ...prev,
              step: 'initial',
              shipmentSelectedSupplier: null,
              shipmentSelectedIngredient: null,
              shipmentIngredientSearch: '',
              shipmentEntries: new Map(),
              shipmentSavedCount: 0,
              shipmentSuppliers: [],
              shipmentCurrentLot: {
                id: '',
                lotNumber: '',
                quantity: 0,
                unit: 'lbs' as QuantityUnit,
                conditionUponArrival: 'Good',
                receivingTemperature: 40,
                temperatureUnit: 'F' as TemperatureUnit,
                correctiveAction: '',
                packageSizeId: null,
                packageCount: 1,
                manualPackageSize: null,
                manualPackageUnit: null,
                manualPackageLabel: ''
              },
              shipmentAvailablePackageSizes: [],
              shipmentShowNewPackageForm: false
            }));
          }}
          style={styles.shipmentPrimaryButton}
        >
          DONE
        </button>
      </div>
    </div>
  );

  // ========== DELIVERY WIZARD RENDER FUNCTIONS ==========

  const renderDeliveryDateTimeStep = () => (
    <div style={styles.stepContainer}>
      <button onClick={handleBack} style={styles.backButton}>
        ← Back
      </button>

      <h2 style={styles.stepTitle}>Delivery Date & Time</h2>

      <div style={styles.formGroup}>
        <label style={styles.inputLabel}>Date</label>
        <input
          type="date"
          value={wizardState.deliveryDate}
          onChange={(e) => setWizardState(prev => ({ ...prev, deliveryDate: e.target.value }))}
          style={styles.textInput}
        />
      </div>

      <div style={styles.formGroup}>
        <label style={styles.inputLabel}>Time</label>
        <input
          type="time"
          value={wizardState.deliveryTime}
          onChange={(e) => setWizardState(prev => ({ ...prev, deliveryTime: e.target.value }))}
          style={styles.textInput}
        />
      </div>

      <div style={styles.buttonContainer}>
        <button
          onClick={handleDeliveryDateTimeNext}
          disabled={!wizardState.deliveryDate || !wizardState.deliveryTime}
          style={{
            ...styles.deliveryPrimaryButton,
            opacity: (!wizardState.deliveryDate || !wizardState.deliveryTime) ? 0.5 : 1
          }}
        >
          Next
        </button>
      </div>
    </div>
  );

  const renderDeliveryCustomerStep = () => (
    <div style={styles.stepContainer}>
      <button onClick={handleBack} style={styles.backButton}>
        ← Back
      </button>

      <h2 style={styles.stepTitle}>Customer Information</h2>

      <div style={styles.formGroup}>
        <label style={styles.inputLabel}>Customer Name *</label>
        <input
          type="text"
          value={wizardState.deliveryCustomerName}
          onChange={(e) => setWizardState(prev => ({ ...prev, deliveryCustomerName: e.target.value }))}
          placeholder="Enter customer name"
          style={styles.textInput}
        />
      </div>

      <div style={styles.formGroup}>
        <label style={styles.inputLabel}>Address (optional)</label>
        <input
          type="text"
          value={wizardState.deliveryCustomerAddress}
          onChange={(e) => setWizardState(prev => ({ ...prev, deliveryCustomerAddress: e.target.value }))}
          placeholder="Enter delivery address"
          style={styles.textInput}
        />
      </div>

      <div style={styles.buttonContainer}>
        <button
          onClick={handleDeliveryCustomerNext}
          disabled={!wizardState.deliveryCustomerName.trim()}
          style={{
            ...styles.deliveryPrimaryButton,
            opacity: !wizardState.deliveryCustomerName.trim() ? 0.5 : 1
          }}
        >
          Next
        </button>
      </div>
    </div>
  );

  const renderDeliverySelectProductStep = () => (
    <div style={styles.stepContainer}>
      <button onClick={handleBack} style={styles.backButton}>
        ← Back
      </button>

      <h2 style={styles.stepTitle}>Select Product</h2>

      {fillings.length === 0 ? (
        <p style={styles.emptyMessage}>No products available.</p>
      ) : (
        <div style={styles.componentGrid}>
          {fillings.map(filling => (
            <button
              key={filling.id}
              onClick={() => handleDeliverySelectProduct(filling)}
              style={styles.deliveryProductButton}
            >
              {filling.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const renderDeliverySelectBatchStep = () => {
    const { deliverySelectedProduct, deliveryEligibleBatches, deliveryLoadingBatches, deliveryLineItems } = wizardState;

    // Filter out batches that are already in line items
    const usedBatchIds = new Set(deliveryLineItems.map(item => item.batchRecordId));
    const availableBatches = deliveryEligibleBatches.filter(batch => !usedBatchIds.has(batch.id));

    return (
      <div style={styles.stepContainer}>
        <button onClick={handleBack} style={styles.backButton}>
          ← Back
        </button>

        <h2 style={styles.stepTitle}>{deliverySelectedProduct?.name}</h2>
        <p style={styles.stepSubtitle}>Select batch for delivery</p>

        {deliveryLoadingBatches ? (
          <div style={styles.loadingContainer}>
            <div style={styles.spinner} />
            <p style={styles.loadingText}>Loading eligible batches...</p>
          </div>
        ) : availableBatches.length === 0 ? (
          <p style={styles.emptyMessage}>No eligible batches available for this product.</p>
        ) : (
          <div style={styles.batchSelectionList}>
            {availableBatches.map(batch => (
              <button
                key={batch.id}
                onClick={() => handleDeliverySelectBatch(batch.id)}
                style={styles.deliveryBatchButton}
              >
                <div style={styles.batchSelectionInfo}>
                  <span style={styles.batchLotNumber}>{batch.batchLotNumber}</span>
                  <span style={styles.batchDate}>Produced: {new Date(batch.dateOfProduction).toLocaleDateString()}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderDeliveryQuantityStep = () => {
    const { deliverySelectedProduct, deliverySelectedBatchId, deliveryEligibleBatches, deliveryQuantity } = wizardState;
    const selectedBatch = deliveryEligibleBatches.find(b => b.id === deliverySelectedBatchId);

    return (
      <div style={styles.stepContainer}>
        <button onClick={handleBack} style={styles.backButton}>
          ← Back
        </button>

        <h2 style={styles.stepTitle}>Enter Quantity</h2>
        <p style={styles.stepSubtitle}>
          {deliverySelectedProduct?.name} - {selectedBatch?.batchLotNumber}
        </p>

        <div style={styles.formGroup}>
          <label style={styles.inputLabel}>Quantity</label>
          <input
            type="number"
            min="1"
            value={deliveryQuantity || ''}
            onChange={(e) => setWizardState(prev => ({ ...prev, deliveryQuantity: parseInt(e.target.value) || 0 }))}
            placeholder="Enter quantity"
            style={styles.textInput}
          />
        </div>

        <div style={styles.buttonContainer}>
          <button
            onClick={handleDeliveryAddLineItem}
            disabled={deliveryQuantity <= 0}
            style={{
              ...styles.deliveryPrimaryButton,
              opacity: deliveryQuantity <= 0 ? 0.5 : 1
            }}
          >
            Add to Delivery
          </button>
        </div>
      </div>
    );
  };

  const renderDeliveryReviewStep = () => {
    const { deliveryDate, deliveryTime, deliveryCustomerName, deliveryCustomerAddress, deliveryLineItems, error } = wizardState;

    return (
      <div style={styles.stepContainer}>
        <button onClick={handleBack} style={styles.backButton}>
          ← Back
        </button>

        <h2 style={styles.stepTitle}>Review Delivery</h2>

        {error && (
          <div style={styles.errorMessage}>
            {error}
          </div>
        )}

        <div style={styles.deliveryReviewSection}>
          <div style={styles.deliveryReviewInfo}>
            <strong>Date:</strong> {new Date(deliveryDate).toLocaleDateString()}
          </div>
          <div style={styles.deliveryReviewInfo}>
            <strong>Time:</strong> {deliveryTime}
          </div>
          <div style={styles.deliveryReviewInfo}>
            <strong>Customer:</strong> {deliveryCustomerName}
            {deliveryCustomerAddress && `, ${deliveryCustomerAddress}`}
          </div>
        </div>

        <h3 style={styles.deliveryReviewSubtitle}>Line Items ({deliveryLineItems.length})</h3>

        {deliveryLineItems.length === 0 ? (
          <p style={styles.emptyMessage}>No items added yet.</p>
        ) : (
          <div style={styles.deliveryLineItemsList}>
            {deliveryLineItems.map(item => (
              <div key={item.id} style={styles.deliveryLineItem}>
                <div style={styles.deliveryLineItemInfo}>
                  <span style={styles.deliveryLineItemName}>{item.fillingName}</span>
                  <span style={styles.deliveryLineItemBatch}>Lot: {item.batchLotNumber}</span>
                  <span style={styles.deliveryLineItemQty}>Qty: {item.quantity}</span>
                </div>
                <button
                  onClick={() => handleDeliveryRemoveLineItem(item.id)}
                  style={styles.deliveryRemoveButton}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        <div style={styles.deliveryReviewButtons}>
          <button
            onClick={() => setWizardState(prev => ({ ...prev, step: 'deliverySelectProduct' }))}
            style={styles.deliverySecondaryButton}
          >
            Add Another Item
          </button>
          <button
            onClick={handleDeliverySave}
            disabled={deliveryLineItems.length === 0}
            style={{
              ...styles.deliveryPrimaryButton,
              opacity: deliveryLineItems.length === 0 ? 0.5 : 1
            }}
          >
            Save Delivery
          </button>
        </div>
      </div>
    );
  };

  // ========== DELIVERY COMPLETION WIZARD RENDER FUNCTIONS ==========

  const renderInProgressDeliveryTempStep = () => (
    <div style={styles.stepContainer}>
      <button onClick={handleBack} style={styles.backButton}>
        ← Back
      </button>

      <h2 style={styles.stepTitle}>Transportation Temperature</h2>
      <p style={styles.stepSubtitle}>{wizardState.inProgressDeliveryCustomer}</p>

      <div style={styles.formGroup}>
        <label style={styles.inputLabel}>Temperature</label>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', justifyContent: 'center' }}>
          <input
            type="number"
            value={wizardState.deliveryTransportationTemp ?? ''}
            onChange={(e) => setWizardState(prev => ({
              ...prev,
              deliveryTransportationTemp: e.target.value ? parseFloat(e.target.value) : null
            }))}
            placeholder="Enter temp"
            style={{ ...styles.textInput, width: '150px', textAlign: 'center' as const }}
          />
          <div style={styles.deliveryTempToggle}>
            <button
              onClick={() => setWizardState(prev => ({ ...prev, deliveryTemperatureUnit: 'F' }))}
              style={{
                ...styles.deliveryTempButton,
                ...(wizardState.deliveryTemperatureUnit === 'F' ? styles.deliveryTempButtonActive : {})
              }}
            >
              °F
            </button>
            <button
              onClick={() => setWizardState(prev => ({ ...prev, deliveryTemperatureUnit: 'C' }))}
              style={{
                ...styles.deliveryTempButton,
                ...(wizardState.deliveryTemperatureUnit === 'C' ? styles.deliveryTempButtonActive : {})
              }}
            >
              °C
            </button>
          </div>
        </div>
      </div>

      <div style={styles.buttonContainer}>
        <button
          onClick={handleDeliveryTempNext}
          disabled={wizardState.deliveryTransportationTemp === null}
          style={{
            ...styles.deliveryPrimaryButton,
            opacity: wizardState.deliveryTransportationTemp === null ? 0.5 : 1
          }}
        >
          Next
        </button>
      </div>
    </div>
  );

  const renderInProgressDeliveryInitialsStep = () => (
    <div style={styles.stepContainer}>
      <button onClick={handleBack} style={styles.backButton}>
        ← Back
      </button>

      <h2 style={styles.stepTitle}>Complete Delivery</h2>
      <p style={styles.stepSubtitle}>{wizardState.inProgressDeliveryCustomer}</p>

      {wizardState.error && (
        <div style={styles.errorMessage}>
          {wizardState.error}
        </div>
      )}

      <div style={styles.deliveryReviewSection}>
        <div style={styles.deliveryReviewInfo}>
          <strong>Temperature:</strong> {wizardState.deliveryTransportationTemp}°{wizardState.deliveryTemperatureUnit}
        </div>
      </div>

      <div style={styles.formGroup}>
        <label style={styles.inputLabel}>Initials *</label>
        <input
          type="text"
          value={wizardState.deliveryInitials}
          onChange={(e) => setWizardState(prev => ({ ...prev, deliveryInitials: e.target.value }))}
          placeholder="Enter your initials"
          style={styles.textInput}
        />
      </div>

      <div style={styles.formGroup}>
        <label style={styles.inputLabel}>Corrective Action (optional)</label>
        <textarea
          value={wizardState.deliveryCorrectiveAction}
          onChange={(e) => setWizardState(prev => ({ ...prev, deliveryCorrectiveAction: e.target.value }))}
          placeholder="Enter any corrective actions taken"
          style={{ ...styles.textInput, minHeight: '80px', resize: 'vertical' as const }}
        />
      </div>

      <div style={styles.buttonContainer}>
        <button
          onClick={handleCompleteDelivery}
          disabled={!wizardState.deliveryInitials.trim()}
          style={{
            ...styles.deliveryPrimaryButton,
            opacity: !wizardState.deliveryInitials.trim() ? 0.5 : 1
          }}
        >
          Complete Delivery
        </button>
      </div>
    </div>
  );

  // ========== TEMPERATURE LOG WIZARD RENDER FUNCTIONS ==========

  const renderTempLogSelectTypeStep = () => (
    <div style={styles.stepContainer}>
      <button onClick={handleBack} style={styles.backButton}>
        ← Back
      </button>

      <h2 style={styles.stepTitle}>Select Equipment Type</h2>

      <div style={styles.buttonGrid}>
        <button
          onClick={() => handleTempLogTypeSelect('freezer')}
          style={styles.tempLogTypeButton}
        >
          <span style={styles.tempLogTypeIcon}>❄️</span>
          FREEZER
        </button>
        <button
          onClick={() => handleTempLogTypeSelect('fridge')}
          style={styles.tempLogTypeButton}
        >
          <span style={styles.tempLogTypeIcon}>🧊</span>
          FRIDGE
        </button>
        <button
          onClick={() => handleTempLogTypeSelect('oven')}
          style={styles.tempLogTypeButton}
        >
          <span style={styles.tempLogTypeIcon}>🔥</span>
          OVEN
        </button>
      </div>
    </div>
  );

  const renderTempLogSelectEquipmentStep = () => {
    const typeLabel = wizardState.tempLogEquipmentType?.toUpperCase() || '';

    return (
      <div style={styles.stepContainer}>
        <button onClick={handleBack} style={styles.backButton}>
          ← Back
        </button>

        <h2 style={styles.stepTitle}>Select {typeLabel}</h2>

        {wizardState.error && (
          <div style={styles.errorMessage}>
            {wizardState.error}
          </div>
        )}

        {wizardState.tempLogLoadingEquipment ? (
          <div style={styles.centeredContainer}>
            <div style={styles.spinner}></div>
            <p style={styles.loadingText}>Loading equipment...</p>
          </div>
        ) : wizardState.tempLogEquipmentList.length === 0 ? (
          <p style={styles.emptyMessage}>No active {typeLabel.toLowerCase()} equipment found.</p>
        ) : (
          <div style={styles.componentGrid}>
            {wizardState.tempLogEquipmentList.map(equipment => (
              <button
                key={equipment.id}
                onClick={() => handleTempLogEquipmentSelect(equipment)}
                style={styles.tempLogEquipmentButton}
              >
                <div style={styles.tempLogEquipmentName}>{equipment.equipmentName}</div>
                <div style={styles.tempLogEquipmentLocation}>{equipment.location}</div>
                <div style={styles.tempLogEquipmentRange}>
                  Range: {equipment.tempRangeMin}°F - {equipment.tempRangeMax}°F
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderTempLogEnterTempStep = () => {
    const equipment = wizardState.tempLogSelectedEquipment;
    if (!equipment) return null;

    const isOutOfRange = wizardState.tempLogActualTemp !== null && (
      wizardState.tempLogActualTemp < equipment.tempRangeMin ||
      wizardState.tempLogActualTemp > equipment.tempRangeMax
    );

    return (
      <div style={styles.stepContainer}>
        <button onClick={handleBack} style={styles.backButton}>
          ← Back
        </button>

        <h2 style={styles.stepTitle}>Log Temperature</h2>
        <p style={styles.stepSubtitle}>{equipment.equipmentName} - {equipment.location}</p>

        {wizardState.error && (
          <div style={styles.errorMessage}>
            {wizardState.error}
          </div>
        )}

        <div style={styles.tempLogInfoCard}>
          <div style={styles.tempLogInfoRow}>
            <span style={styles.tempLogInfoLabel}>Acceptable Range:</span>
            <span style={styles.tempLogInfoValue}>{equipment.tempRangeMin}°F - {equipment.tempRangeMax}°F</span>
          </div>
          <div style={styles.tempLogInfoRow}>
            <span style={styles.tempLogInfoLabel}>Date:</span>
            <span style={styles.tempLogInfoValue}>{new Date().toLocaleDateString()}</span>
          </div>
          <div style={styles.tempLogInfoRow}>
            <span style={styles.tempLogInfoLabel}>Time:</span>
            <span style={styles.tempLogInfoValue}>{new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
          </div>
          <div style={styles.tempLogInfoRow}>
            <span style={styles.tempLogInfoLabel}>Recorded By:</span>
            <span style={styles.tempLogInfoValue}>{currentUser?.name || 'N/A'}</span>
          </div>
        </div>

        <div style={styles.formGroup}>
          <label style={styles.inputLabel}>Actual Temperature (°F) *</label>
          <input
            type="number"
            value={wizardState.tempLogActualTemp ?? ''}
            onChange={(e) => setWizardState(prev => ({
              ...prev,
              tempLogActualTemp: e.target.value ? parseFloat(e.target.value) : null
            }))}
            placeholder="Enter temperature"
            style={{
              ...styles.textInput,
              borderColor: isOutOfRange ? '#f44336' : undefined
            }}
          />
          {isOutOfRange && (
            <div style={styles.tempLogWarning}>
              ⚠️ Temperature is outside acceptable range
            </div>
          )}
        </div>

        <div style={styles.formGroup}>
          <label style={styles.inputLabel}>Corrective Action {isOutOfRange ? '*' : '(optional)'}</label>
          <textarea
            value={wizardState.tempLogCorrectiveAction}
            onChange={(e) => setWizardState(prev => ({ ...prev, tempLogCorrectiveAction: e.target.value }))}
            placeholder="Enter any corrective actions taken"
            style={{ ...styles.textInput, minHeight: '80px', resize: 'vertical' as const }}
          />
        </div>

        <div style={styles.buttonContainer}>
          <button
            onClick={handleTempLogSubmit}
            disabled={wizardState.tempLogActualTemp === null || (isOutOfRange && !wizardState.tempLogCorrectiveAction.trim())}
            style={{
              ...styles.tempLogPrimaryButton,
              opacity: (wizardState.tempLogActualTemp === null || (isOutOfRange && !wizardState.tempLogCorrectiveAction.trim())) ? 0.5 : 1
            }}
          >
            Save Temperature Log
          </button>
        </div>
      </div>
    );
  };

  const renderTempLogSummaryStep = () => {
    const log = wizardState.tempLogCreatedLog;
    const equipment = wizardState.tempLogSelectedEquipment;
    const typeLabel = wizardState.tempLogEquipmentType?.toUpperCase() || '';

    if (!log || !equipment) return null;

    return (
      <div style={styles.stepContainer}>
        <div style={styles.tempLogSummaryCard}>
          <div style={styles.tempLogSummaryIcon}>✓</div>
          <h2 style={styles.tempLogSummaryTitle}>Temperature Logged!</h2>

          <div style={styles.tempLogSummaryDetails}>
            <div style={styles.tempLogSummaryRow}>
              <span style={styles.tempLogSummaryLabel}>Equipment:</span>
              <span>{equipment.equipmentName}</span>
            </div>
            <div style={styles.tempLogSummaryRow}>
              <span style={styles.tempLogSummaryLabel}>Location:</span>
              <span>{equipment.location}</span>
            </div>
            <div style={styles.tempLogSummaryRow}>
              <span style={styles.tempLogSummaryLabel}>Temperature:</span>
              <span>{log.actualTemp}°F</span>
            </div>
            <div style={styles.tempLogSummaryRow}>
              <span style={styles.tempLogSummaryLabel}>Date/Time:</span>
              <span>{log.date} {log.timeChecked}</span>
            </div>
            <div style={styles.tempLogSummaryRow}>
              <span style={styles.tempLogSummaryLabel}>Recorded By:</span>
              <span>{log.recordedBy}</span>
            </div>
            {log.correctiveAction && (
              <div style={styles.tempLogSummaryRow}>
                <span style={styles.tempLogSummaryLabel}>Corrective Action:</span>
                <span>{log.correctiveAction}</span>
              </div>
            )}
          </div>
        </div>

        <div style={styles.tempLogSummaryButtons}>
          <button
            onClick={handleTempLogAnotherSameType}
            style={styles.tempLogPrimaryButton}
          >
            Log Another ({typeLabel})
          </button>
          <button
            onClick={handleTempLogDifferentType}
            style={styles.tempLogSecondaryButton}
          >
            Log Different Type
          </button>
          <button
            onClick={handleTempLogDone}
            style={styles.tempLogOutlineButton}
          >
            Done
          </button>
        </div>
      </div>
    );
  };

  // ========== SANITATION CLEANING WIZARD RENDER FUNCTIONS ==========

  const renderSanitationSelectFrequencyStep = () => (
    <div style={styles.stepContainer}>
      <button onClick={handleBack} style={styles.backButton}>
        ← Back
      </button>

      <h2 style={styles.stepTitle}>Select Cleaning Frequency</h2>

      <div style={styles.buttonGrid}>
        <button
          onClick={() => handleSanitationFrequencySelect('daily')}
          style={styles.sanitationFrequencyButton}
        >
          <span style={styles.sanitationFrequencyIcon}>📅</span>
          DAILY
        </button>
        <button
          onClick={() => handleSanitationFrequencySelect('weekly')}
          style={styles.sanitationFrequencyButton}
        >
          <span style={styles.sanitationFrequencyIcon}>📆</span>
          WEEKLY
        </button>
        <button
          onClick={() => handleSanitationFrequencySelect('monthly')}
          style={styles.sanitationFrequencyButton}
        >
          <span style={styles.sanitationFrequencyIcon}>🗓️</span>
          MONTHLY
        </button>
        <button
          onClick={() => handleSanitationFrequencySelect('quarterly')}
          style={styles.sanitationFrequencyButton}
        >
          <span style={styles.sanitationFrequencyIcon}>📊</span>
          QUARTERLY
        </button>
      </div>
    </div>
  );

  const renderSanitationSelectEquipmentStep = () => {
    const frequencyLabel = wizardState.sanitationFrequency?.toUpperCase() || '';

    return (
      <div style={styles.stepContainer}>
        <button onClick={handleBack} style={styles.backButton}>
          ← Back
        </button>

        <h2 style={styles.stepTitle}>Select Equipment ({frequencyLabel} Cleaning)</h2>
        <p style={styles.stepSubtitle}>Select all equipment that was cleaned</p>

        {wizardState.sanitationError && (
          <div style={styles.errorMessage}>
            {wizardState.sanitationError}
          </div>
        )}

        {wizardState.sanitationLoadingEquipment ? (
          <div style={styles.centeredContainer}>
            <div style={styles.spinner}></div>
            <p style={styles.loadingText}>Loading equipment...</p>
          </div>
        ) : wizardState.sanitationEquipmentList.length === 0 ? (
          <p style={styles.emptyMessage}>No sanitation equipment found. Add equipment in the Sanitation Records page.</p>
        ) : (
          <>
            <div style={styles.sanitationEquipmentGrid}>
              {wizardState.sanitationEquipmentList.map(equipment => {
                const isSelected = wizardState.sanitationSelectedEquipmentIds.has(equipment.id);
                return (
                  <button
                    key={equipment.id}
                    onClick={() => handleSanitationEquipmentToggle(equipment.id)}
                    style={{
                      ...styles.sanitationEquipmentButton,
                      ...(isSelected ? styles.sanitationEquipmentButtonSelected : {})
                    }}
                  >
                    <div style={{
                      ...styles.sanitationCheckbox,
                      ...(isSelected ? styles.sanitationCheckboxSelected : {})
                    }}>
                      {isSelected && '✓'}
                    </div>
                    <div style={styles.sanitationEquipmentName}>{equipment.name}</div>
                  </button>
                );
              })}
            </div>

            <div style={styles.buttonContainer}>
              <button
                onClick={handleSanitationProceedToReview}
                disabled={wizardState.sanitationSelectedEquipmentIds.size === 0}
                style={{
                  ...styles.sanitationPrimaryButton,
                  opacity: wizardState.sanitationSelectedEquipmentIds.size === 0 ? 0.5 : 1
                }}
              >
                Continue ({wizardState.sanitationSelectedEquipmentIds.size} selected)
              </button>
            </div>
          </>
        )}
      </div>
    );
  };

  const renderSanitationReviewStep = () => {
    const frequencyLabel = wizardState.sanitationFrequency?.toUpperCase() || '';
    const selectedEquipment = wizardState.sanitationEquipmentList.filter(
      e => wizardState.sanitationSelectedEquipmentIds.has(e.id)
    );

    return (
      <div style={styles.stepContainer}>
        <button onClick={handleBack} style={styles.backButton}>
          ← Back
        </button>

        <h2 style={styles.stepTitle}>Review Cleaning Log</h2>

        {wizardState.sanitationError && (
          <div style={styles.errorMessage}>
            {wizardState.sanitationError}
          </div>
        )}

        <div style={styles.sanitationReviewCard}>
          <div style={styles.sanitationReviewRow}>
            <span style={styles.sanitationReviewLabel}>Frequency:</span>
            <span style={styles.sanitationReviewValue}>{frequencyLabel}</span>
          </div>
          <div style={styles.sanitationReviewRow}>
            <span style={styles.sanitationReviewLabel}>Date:</span>
            <span style={styles.sanitationReviewValue}>{new Date().toLocaleDateString()}</span>
          </div>
          <div style={styles.sanitationReviewRow}>
            <span style={styles.sanitationReviewLabel}>Cleaned By:</span>
            <span style={styles.sanitationReviewValue}>{currentUser?.name || 'N/A'}</span>
          </div>
          <div style={styles.sanitationReviewRow}>
            <span style={styles.sanitationReviewLabel}>Equipment ({selectedEquipment.length}):</span>
          </div>
          <div style={styles.sanitationEquipmentList}>
            {selectedEquipment.map(equipment => (
              <div key={equipment.id} style={styles.sanitationEquipmentListItem}>
                • {equipment.name}
              </div>
            ))}
          </div>
        </div>

        <div style={styles.buttonContainer}>
          <button
            onClick={handleSanitationSubmit}
            style={styles.sanitationPrimaryButton}
          >
            Confirm & Save
          </button>
        </div>
      </div>
    );
  };

  const renderSanitationSummaryStep = () => {
    const frequencyLabel = wizardState.sanitationFrequency?.toUpperCase() || '';
    const recordCount = wizardState.sanitationCreatedRecords.length;

    return (
      <div style={styles.stepContainer}>
        <div style={styles.sanitationSummaryCard}>
          <div style={styles.sanitationSummaryIcon}>✓</div>
          <h2 style={styles.sanitationSummaryTitle}>Cleaning Logged!</h2>

          <div style={styles.sanitationSummaryDetails}>
            <div style={styles.sanitationSummaryRow}>
              <span style={styles.sanitationSummaryLabel}>Frequency:</span>
              <span>{frequencyLabel}</span>
            </div>
            <div style={styles.sanitationSummaryRow}>
              <span style={styles.sanitationSummaryLabel}>Equipment Cleaned:</span>
              <span>{recordCount} item{recordCount !== 1 ? 's' : ''}</span>
            </div>
            <div style={styles.sanitationSummaryRow}>
              <span style={styles.sanitationSummaryLabel}>Date:</span>
              <span>{new Date().toLocaleDateString()}</span>
            </div>
            <div style={styles.sanitationSummaryRow}>
              <span style={styles.sanitationSummaryLabel}>Cleaned By:</span>
              <span>{currentUser?.name || 'N/A'}</span>
            </div>
          </div>
        </div>

        <div style={styles.sanitationSummaryButtons}>
          <button
            onClick={handleSanitationLogAnother}
            style={styles.sanitationPrimaryButton}
          >
            Log Another Cleaning
          </button>
          <button
            onClick={handleSanitationDone}
            style={styles.sanitationOutlineButton}
          >
            Done
          </button>
        </div>
      </div>
    );
  };

  // ========== IN PROGRESS WIZARD RENDER FUNCTIONS ==========

  /**
   * Renders processing step (Baking, Cooling, or Freezing)
   */
  const renderInProgressProcessingStep = () => {
    const step = wizardState.inProgressStep;
    const pan = wizardState.inProgressPan;

    if (!step || !pan) return null;

    const stepLabels: Record<string, string> = {
      'baking': 'Baking',
      'cooling1': 'Cooling 1',
      'cooling2': 'Cooling 2',
      'freezing': 'Freezing'
    };

    // Get target values from pan (inherited from batch)
    const targetTemp = step === 'baking' ? pan.bakingTargetTemp :
                       step === 'cooling1' ? pan.cooling1TargetTemp :
                       step === 'cooling2' ? pan.cooling2TargetTemp :
                       pan.freezingTargetTemp;
    const targetTempMax = step === 'baking' ? pan.bakingTargetTempMax : undefined;
    const targetTime = step === 'baking' ? pan.bakingTargetTime :
                       step === 'cooling1' ? pan.cooling1TargetTime :
                       step === 'cooling2' ? pan.cooling2TargetTime :
                       pan.freezingTargetTime;

    const canProceed = wizardState.processingActualTemp !== null &&
                       wizardState.processingCcpAchieved !== null;

    return (
      <div style={styles.stepContainer}>
        <button onClick={handleInProgressBack} style={styles.backButton}>
          ← Back
        </button>

        <h2 style={styles.stepTitle}>{stepLabels[step]}</h2>
        <p style={{ textAlign: 'center', color: '#666', marginBottom: '20px' }}>
          {pan.productName} - {pan.batchLotNumber} (Pan #{pan.panNumber})
        </p>

        {/* Target values display */}
        {(targetTemp || targetTime) && (
          <div style={{
            textAlign: 'center',
            marginBottom: '24px',
            padding: '12px',
            backgroundColor: '#f5f5f5',
            borderRadius: '8px'
          }}>
            <span style={{ color: '#666' }}>Target: </span>
            {targetTemp && <span>{targetTempMax ? `${targetTemp}–${targetTempMax}` : targetTemp}°F</span>}
            {targetTemp && targetTime && <span> / </span>}
            {targetTime && <span>{targetTime} min</span>}
          </div>
        )}

        {wizardState.error && (
          <div style={{
            backgroundColor: '#ffebee',
            border: '1px solid #f44336',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '16px',
            color: '#c62828'
          }}>
            {wizardState.error}
          </div>
        )}

        <div style={{ maxWidth: '400px', margin: '0 auto' }}>
          {/* Actual Temperature */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
              Actual Temperature (°F)
            </label>
            <input
              type="number"
              step="0.1"
              value={wizardState.processingActualTemp ?? ''}
              onChange={(e) => setWizardState(prev => ({
                ...prev,
                processingActualTemp: e.target.value ? parseFloat(e.target.value) : null
              }))}
              style={{
                width: '100%',
                padding: '12px 16px',
                fontSize: '1.1rem',
                border: '2px solid #ddd',
                borderRadius: '8px',
                boxSizing: 'border-box'
              }}
              placeholder="Enter temperature"
            />
          </div>

          {/* Actual Time */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
              Actual Time
            </label>
            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: '#666' }}>
                  Hours
                </label>
                <input
                  type="number"
                  min="0"
                  max="23"
                  value={wizardState.processingActualTimeHours ?? ''}
                  onChange={(e) => setWizardState(prev => ({
                    ...prev,
                    processingActualTimeHours: e.target.value ? Math.min(23, Math.max(0, parseInt(e.target.value))) : null
                  }))}
                  style={{ width: '100%', padding: '12px 16px', fontSize: '1.1rem', border: '2px solid #ddd', borderRadius: '8px', boxSizing: 'border-box' }}
                  placeholder="0"
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: '#666' }}>
                  Minutes
                </label>
                <input
                  type="number"
                  min="0"
                  max="59"
                  value={wizardState.processingActualTimeMinutes ?? ''}
                  onChange={(e) => setWizardState(prev => ({
                    ...prev,
                    processingActualTimeMinutes: e.target.value ? Math.min(59, Math.max(0, parseInt(e.target.value))) : null
                  }))}
                  style={{ width: '100%', padding: '12px 16px', fontSize: '1.1rem', border: '2px solid #ddd', borderRadius: '8px', boxSizing: 'border-box' }}
                  placeholder="0"
                />
              </div>
            </div>
          </div>

          {/* CCP Achieved */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
              CCP Achieved?
            </label>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setWizardState(prev => ({ ...prev, processingCcpAchieved: true }))}
                style={{
                  flex: 1,
                  padding: '12px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  border: '2px solid #ddd',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  backgroundColor: wizardState.processingCcpAchieved === true ? '#9C27B0' : 'white',
                  color: wizardState.processingCcpAchieved === true ? 'white' : '#333',
                  borderColor: wizardState.processingCcpAchieved === true ? '#9C27B0' : '#ddd'
                }}
              >
                Yes
              </button>
              <button
                onClick={() => setWizardState(prev => ({ ...prev, processingCcpAchieved: false }))}
                style={{
                  flex: 1,
                  padding: '12px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  border: '2px solid #ddd',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  backgroundColor: wizardState.processingCcpAchieved === false ? '#9C27B0' : 'white',
                  color: wizardState.processingCcpAchieved === false ? 'white' : '#333',
                  borderColor: wizardState.processingCcpAchieved === false ? '#9C27B0' : '#ddd'
                }}
              >
                No
              </button>
            </div>
          </div>

          {/* Corrective Action (optional) */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
              Corrective Action (optional)
            </label>
            <input
              type="text"
              value={wizardState.processingCorrectiveAction}
              onChange={(e) => setWizardState(prev => ({
                ...prev,
                processingCorrectiveAction: e.target.value
              }))}
              style={{
                width: '100%',
                padding: '12px 16px',
                fontSize: '1.1rem',
                border: '2px solid #ddd',
                borderRadius: '8px',
                boxSizing: 'border-box'
              }}
              placeholder="Enter if needed"
            />
          </div>

          {/* Quantity Produced (optional, baking step only) */}
          {step === 'baking' && (
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', color: '#333' }}>
                Quantity Produced (optional)
              </label>
              <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '8px' }}>
                Leave both blank for default (48 pieces / 4 dozen)
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.9rem', color: '#666' }}>
                    Whole dozens (0–3)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="3"
                    value={wizardState.processingFinishedDozens ?? ''}
                    onChange={(e) => setWizardState(prev => ({
                      ...prev,
                      processingFinishedDozens: e.target.value === ''
                        ? null
                        : Math.min(3, Math.max(0, parseInt(e.target.value)))
                    }))}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      fontSize: '1.1rem',
                      border: '2px solid #ddd',
                      borderRadius: '8px',
                      boxSizing: 'border-box'
                    }}
                    placeholder="0"
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.9rem', color: '#666' }}>
                    Individual buns (0–11)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="11"
                    value={wizardState.processingFinishedIndividualBuns ?? ''}
                    onChange={(e) => setWizardState(prev => ({
                      ...prev,
                      processingFinishedIndividualBuns: e.target.value === ''
                        ? null
                        : Math.min(11, Math.max(0, parseInt(e.target.value)))
                    }))}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      fontSize: '1.1rem',
                      border: '2px solid #ddd',
                      borderRadius: '8px',
                      boxSizing: 'border-box'
                    }}
                    placeholder="0"
                  />
                </div>
              </div>
              <div style={{ marginTop: '8px', fontSize: '0.95rem', color: '#444' }}>
                = {formatPanQuantityDisplay(computePanQuantity(
                    wizardState.processingFinishedDozens,
                    wizardState.processingFinishedIndividualBuns
                  ))}
                {wizardState.processingFinishedDozens == null
                  && wizardState.processingFinishedIndividualBuns == null
                  && ' (default)'}
              </div>
            </div>
          )}
        </div>

        {/* Checked By display */}
        <p style={{ textAlign: 'center', color: '#666', marginTop: '16px' }}>
          Checked By: <strong>{currentUser?.name || 'Unknown'}</strong>
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', marginTop: '24px' }}>
          <button
            onClick={handleSaveProcessingStep}
            disabled={!canProceed}
            style={{
              padding: '16px 48px',
              fontSize: '1.1rem',
              fontWeight: 'bold',
              backgroundColor: canProceed ? '#9C27B0' : '#ccc',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: canProceed ? 'pointer' : 'not-allowed'
            }}
          >
            SAVE & CONTINUE
          </button>
          <button
            onClick={handleSaveAndExit}
            style={{
              padding: '12px 24px',
              backgroundColor: 'transparent',
              color: '#9C27B0',
              border: '2px solid #9C27B0',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 'bold'
            }}
          >
            SAVE & EXIT
          </button>
        </div>
      </div>
    );
  };

  /**
   * Renders Sign-Off step
   */
  const renderInProgressSignOffStep = () => {
    const batch = wizardState.inProgressBatch;
    if (!batch) return null;

    // Pre-fill production name from preparedBy if not already set
    if (!wizardState.signOffProductionName && batch.preparedBy) {
      setWizardState(prev => ({
        ...prev,
        signOffProductionName: batch.preparedBy || '',
        signOffProductionDate: prev.signOffProductionDate || new Date().toISOString().split('T')[0]
      }));
    }

    const canComplete = wizardState.signOffProductionName.trim() !== '';

    return (
      <div style={styles.stepContainer}>
        <button onClick={handleInProgressBack} style={styles.backButton}>
          ← Back
        </button>

        <h2 style={styles.stepTitle}>Sign-Off</h2>
        <p style={{ textAlign: 'center', color: '#666', marginBottom: '20px' }}>
          {batch.productName} - {batch.batchLotNumber}
        </p>

        {wizardState.error && (
          <div style={{
            backgroundColor: '#ffebee',
            border: '1px solid #f44336',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '16px',
            color: '#c62828'
          }}>
            {wizardState.error}
          </div>
        )}

        <div style={{ maxWidth: '500px', margin: '0 auto' }}>
          {/* Production Staff */}
          <div style={{
            padding: '16px',
            border: '2px solid #9C27B0',
            borderRadius: '12px',
            marginBottom: '16px'
          }}>
            <h3 style={{ margin: '0 0 12px 0', color: '#9C27B0' }}>Production (Staff)</h3>
            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>Name</label>
                <input
                  type="text"
                  value={wizardState.signOffProductionName}
                  onChange={(e) => setWizardState(prev => ({
                    ...prev,
                    signOffProductionName: e.target.value
                  }))}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: '1rem',
                    border: '2px solid #ddd',
                    borderRadius: '8px',
                    boxSizing: 'border-box'
                  }}
                  placeholder="Enter name"
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>Date</label>
                <input
                  type="date"
                  value={wizardState.signOffProductionDate}
                  onChange={(e) => setWizardState(prev => ({
                    ...prev,
                    signOffProductionDate: e.target.value
                  }))}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: '1rem',
                    border: '2px solid #ddd',
                    borderRadius: '8px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>
          </div>

          {/* Supervisor QA */}
          <div style={{
            padding: '16px',
            border: '1px solid #ddd',
            borderRadius: '12px'
          }}>
            <h3 style={{ margin: '0 0 12px 0', color: '#666' }}>Supervisor (Quality Assurance)</h3>
            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>Name (optional)</label>
                <input
                  type="text"
                  value={wizardState.signOffSupervisorName}
                  onChange={(e) => setWizardState(prev => ({
                    ...prev,
                    signOffSupervisorName: e.target.value
                  }))}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: '1rem',
                    border: '2px solid #ddd',
                    borderRadius: '8px',
                    boxSizing: 'border-box'
                  }}
                  placeholder="Enter name"
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>Date</label>
                <input
                  type="date"
                  value={wizardState.signOffSupervisorDate}
                  onChange={(e) => setWizardState(prev => ({
                    ...prev,
                    signOffSupervisorDate: e.target.value
                  }))}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: '1rem',
                    border: '2px solid #ddd',
                    borderRadius: '8px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '24px' }}>
          <button
            onClick={handleSaveSignOff}
            disabled={!canComplete}
            style={{
              padding: '16px 48px',
              fontSize: '1.1rem',
              fontWeight: 'bold',
              backgroundColor: canComplete ? '#9C27B0' : '#ccc',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: canComplete ? 'pointer' : 'not-allowed'
            }}
          >
            COMPLETE BATCH
          </button>
        </div>
      </div>
    );
  };

  /**
   * Renders the In Progress section at the top of the page
   */
  const renderInProgressSection = () => {
    if (incompletePans.length === 0) {
      return null; // Don't show section if no incomplete pans
    }

    return (
      <div style={{
        backgroundColor: '#9C27B0',
        borderRadius: '12px',
        padding: '16px',
        marginBottom: '20px'
      }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer'
          }}
          onClick={() => setInProgressExpanded(!inProgressExpanded)}
        >
          <h2 style={{
            color: 'white',
            margin: 0,
            fontSize: '1.25rem',
            fontWeight: '600'
          }}>
            In Progress ({incompletePans.length})
          </h2>
          <span style={{
            color: 'white',
            fontSize: '1rem',
            transform: inProgressExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s'
          }}>
            ▼
          </span>
        </div>

        {inProgressExpanded && (
          incompletePansLoading ? (
            <p style={{ color: 'white', textAlign: 'center', marginTop: '16px' }}>Loading pans...</p>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
              gap: '12px',
              marginTop: '16px'
            }}>
              {incompletePans.map(pan => {
                const currentStep = getCurrentStepForPan(pan);
                return (
                  <div
                    key={pan.id}
                    onClick={() => handleStartInProgressWizardForPan(pan)}
                    style={{
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      padding: '16px',
                      cursor: 'pointer',
                      transition: 'transform 0.2s, box-shadow 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <h4 style={{ margin: '0 0 8px 0', color: '#333', fontSize: '1rem' }}>
                        {pan.productName}
                      </h4>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setPanToDelete(pan);
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '2px 4px',
                          fontSize: '1rem',
                          color: '#999',
                          lineHeight: 1,
                          borderRadius: '4px',
                          flexShrink: 0
                        }}
                        title="Delete pan"
                      >
                        🗑
                      </button>
                    </div>
                    <p style={{ margin: '0 0 4px 0', color: '#666', fontSize: '0.875rem' }}>
                      <span style={{ fontWeight: '600' }}>Lot:</span>{' '}
                      <span style={{ fontFamily: 'monospace' }}>{pan.batchLotNumber}</span>
                    </p>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 10px',
                        backgroundColor: '#E8E8E8',
                        color: '#333',
                        borderRadius: '20px',
                        fontSize: '0.75rem',
                        fontWeight: '600'
                      }}>
                        Pan #{pan.panNumber}
                      </span>
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 10px',
                        backgroundColor: getStepBadgeColor(currentStep),
                        color: getStepBadgeTextColor(currentStep),
                        borderRadius: '20px',
                        fontSize: '0.75rem',
                        fontWeight: '600'
                      }}>
                        {getStepLabel(currentStep)}
                      </span>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddAnotherPan(pan.batchRecordId);
                      }}
                      style={{
                        width: '100%',
                        padding: '8px',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        backgroundColor: 'transparent',
                        color: '#9C27B0',
                        border: '1.5px solid #9C27B0',
                        borderRadius: '6px',
                        cursor: 'pointer'
                      }}
                    >
                      + Add Another Pan
                    </button>
                  </div>
                );
              })}
            </div>
          )
        )}
      </div>
    );
  };

  /**
   * Renders the In Progress Deliveries section below the In Progress batches
   */
  const renderInProgressDeliveriesSection = () => {
    if (incompleteDeliveryGroups.length === 0) {
      return null; // Don't show section if no incomplete deliveries
    }

    return (
      <div style={{
        backgroundColor: '#E91E63',
        borderRadius: '12px',
        padding: '16px',
        marginBottom: '20px'
      }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer'
          }}
          onClick={() => setInProgressDeliveriesExpanded(!inProgressDeliveriesExpanded)}
        >
          <h2 style={{
            color: 'white',
            margin: 0,
            fontSize: '1.25rem',
            fontWeight: '600'
          }}>
            Pending Deliveries ({incompleteDeliveryGroups.length})
          </h2>
          <span style={{
            color: 'white',
            fontSize: '1rem',
            transform: inProgressDeliveriesExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s'
          }}>
            ▼
          </span>
        </div>

        {inProgressDeliveriesExpanded && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
            gap: '12px',
            marginTop: '16px'
          }}>
            {incompleteDeliveryGroups.map(group => (
              <div
                key={group.deliveryGroupId}
                onClick={() => handleStartInProgressDeliveryWizard(group)}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  padding: '16px',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <h4 style={{ margin: '0 0 8px 0', color: '#C2185B', fontSize: '1rem' }}>
                  {group.customerDestination}
                </h4>
                <p style={{ margin: '0 0 4px 0', color: '#666', fontSize: '0.875rem' }}>
                  <span style={{ fontWeight: '600' }}>Date:</span>{' '}
                  {new Date(group.deliveryDate).toLocaleDateString()} at {group.deliveryTime}
                </p>
                <p style={{ margin: '0 0 4px 0', color: '#666', fontSize: '0.875rem' }}>
                  <span style={{ fontWeight: '600' }}>Items:</span>{' '}
                  {group.lineItemCount} {group.lineItemCount === 1 ? 'item' : 'items'}
                  {group.firstFillingName && ` (${group.firstFillingName}${group.lineItemCount > 1 ? '...' : ''})`}
                </p>
                <p style={{ margin: '0 0 12px 0', color: '#666', fontSize: '0.875rem' }}>
                  <span style={{ fontWeight: '600' }}>Created:</span>{' '}
                  {group.createdAt?.split('T')[0]}
                </p>
                <span style={{
                  display: 'inline-block',
                  padding: '6px 12px',
                  backgroundColor: '#FCE4EC',
                  color: '#C2185B',
                  borderRadius: '20px',
                  fontSize: '0.8rem',
                  fontWeight: '600'
                }}>
                  Pending
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Render current step based on wizard state
  const renderCurrentStep = () => {
    switch (wizardState.step) {
      case 'initial':
        return renderInitialStep();
      case 'batchType':
        return renderBatchTypeStep();
      case 'selectComponent':
        return renderSelectComponentStep();
      case 'selectFilling':
        return renderSelectFillingStep();
      case 'selectDough':
        return renderSelectDoughStep();
      case 'selectProduct':
        return renderSelectProductStep();
      case 'ingredientList':
        return renderIngredientListStep();
      case 'fillingIngredientList':
        return renderFillingIngredientListStep();
      case 'doughIngredientList':
        return renderDoughIngredientListStep();
      case 'selectFillingBatches':
        return renderSelectFillingBatchesStep();
      case 'selectDoughBatches':
        return renderSelectDoughBatchesStep();
      case 'bakingDetails':
        return renderBakingDetailsStep();
      case 'selectTraceCodes':
        return renderTraceCodeSelectionStep();
      case 'selectComponentBatch':
        return renderSelectComponentBatchStep();
      case 'timesProduced':
        return renderTimesProducedStep();
      case 'creating':
        return renderCreatingStep();
      case 'summary':
        return renderSummaryStep();
      // Shipment wizard steps
      case 'shipmentSelectSupplier':
        return renderShipmentSelectSupplierStep();
      case 'shipmentSelectIngredient':
        return renderShipmentSelectIngredientStep();
      case 'shipmentLotNumber':
        return renderShipmentLotNumberStep();
      case 'shipmentQuantity':
        return renderShipmentQuantityStep();
      case 'shipmentCondition':
        return renderShipmentConditionStep();
      case 'shipmentTemperature':
        return renderShipmentTemperatureStep();
      case 'shipmentCorrectiveAction':
        return renderShipmentCorrectiveActionStep();
      case 'shipmentSaving':
        return renderCreatingStep();
      case 'shipmentSummary':
        return renderShipmentSummaryStep();
      // In Progress wizard steps
      case 'inProgressBaking':
      case 'inProgressCooling1':
      case 'inProgressCooling2':
      case 'inProgressFreezing':
        return renderInProgressProcessingStep();
      case 'inProgressSaving':
        return renderCreatingStep();
      // Delivery wizard steps
      case 'deliveryDateTime':
        return renderDeliveryDateTimeStep();
      case 'deliveryCustomer':
        return renderDeliveryCustomerStep();
      case 'deliverySelectProduct':
        return renderDeliverySelectProductStep();
      case 'deliverySelectBatch':
        return renderDeliverySelectBatchStep();
      case 'deliveryQuantity':
        return renderDeliveryQuantityStep();
      case 'deliveryReview':
        return renderDeliveryReviewStep();
      case 'deliverySaving':
        return renderCreatingStep();
      // Delivery completion wizard steps
      case 'inProgressDeliveryTemp':
        return renderInProgressDeliveryTempStep();
      case 'inProgressDeliveryInitials':
        return renderInProgressDeliveryInitialsStep();
      case 'inProgressDeliverySaving':
        return renderCreatingStep();
      // Temperature Log wizard steps
      case 'tempLogSelectType':
        return renderTempLogSelectTypeStep();
      case 'tempLogSelectEquipment':
        return renderTempLogSelectEquipmentStep();
      case 'tempLogEnterTemp':
        return renderTempLogEnterTempStep();
      case 'tempLogSaving':
        return renderCreatingStep();
      case 'tempLogSummary':
        return renderTempLogSummaryStep();
      // Sanitation Cleaning wizard steps
      case 'sanitationSelectFrequency':
        return renderSanitationSelectFrequencyStep();
      case 'sanitationSelectEquipment':
        return renderSanitationSelectEquipmentStep();
      case 'sanitationReview':
        return renderSanitationReviewStep();
      case 'sanitationSaving':
        return renderCreatingStep();
      case 'sanitationSummary':
        return renderSanitationSummaryStep();
      default:
        return renderInitialStep();
    }
  };

  return (
    <div style={styles.container}>
      {/* Header with optional logout for team users */}
      {currentUser?.role === 'team' ? (
        <div style={styles.teamHeader}>
          <h1 style={{ ...styles.pageTitle, marginBottom: 0 }}>Workflow</h1>
          <button onClick={handleLogout} style={styles.logoutButton}>
            Logout
          </button>
        </div>
      ) : (
        <h1 style={styles.pageTitle}>Workflow</h1>
      )}

      {/* In Progress Section - At top, only shown on initial step */}
      {wizardState.step === 'initial' && renderInProgressSection()}

      {/* In Progress Deliveries Section - Below in-progress batches, only shown on initial step */}
      {wizardState.step === 'initial' && renderInProgressDeliveriesSection()}

      {/* Wizard Section */}
      <div ref={wizardContainerRef} style={styles.wizardContainer}>
        {renderCurrentStep()}
      </div>

      {/* New Shipment Section - hide when in in-progress wizard or delivery wizard */}
      {!wizardState.step.startsWith('inProgress') && !wizardState.step.startsWith('delivery') && (
        <div style={styles.centeredContainer}>
          <button onClick={handleNewShipmentClick} style={styles.shipmentMainButton}>
            NEW SHIPMENT
          </button>
        </div>
      )}

      {/* New Delivery Section - hide when in in-progress wizard or delivery wizard */}
      {!wizardState.step.startsWith('inProgress') && !wizardState.step.startsWith('delivery') && (
        <div style={styles.centeredContainer}>
          <button onClick={handleNewDeliveryClick} style={styles.deliveryMainButton}>
            NEW DELIVERY
          </button>
        </div>
      )}

      {/* Temperature Log Section */}
      {!wizardState.step.startsWith('inProgress') &&
       !wizardState.step.startsWith('delivery') &&
       !wizardState.step.startsWith('tempLog') &&
       !wizardState.step.startsWith('sanitation') && (
        <div style={styles.centeredContainer}>
          <button onClick={handleNewTempLogClick} style={styles.tempLogMainButton}>
            LOG EQUIPMENT TEMP
          </button>
        </div>
      )}

      {/* Sanitation Cleaning Section */}
      {!wizardState.step.startsWith('inProgress') &&
       !wizardState.step.startsWith('delivery') &&
       !wizardState.step.startsWith('tempLog') &&
       !wizardState.step.startsWith('sanitation') && (
        <div style={styles.centeredContainer}>
          <button onClick={handleNewSanitationClick} style={styles.sanitationMainButton}>
            LOG CLEANING
          </button>
        </div>
      )}

      {/* Recent Batches Section */}
      {renderRecentBatches()}

      {/* Batch Ingredient Details Modal */}
      <BatchIngredientDetailsModal
        isOpen={ingredientModalOpen}
        onClose={() => {
          setIngredientModalOpen(false);
          setSelectedBatchForModal(null);
          setModalIngredients([]);
        }}
        batchLotNumber={selectedBatchForModal?.batchLotNumber || ''}
        productName={selectedBatchForModal?.productName || ''}
        dateOfProduction={selectedBatchForModal?.dateOfProduction || ''}
        ingredients={modalIngredients}
        isLoading={modalLoading}
        batchType={selectedBatchForModal?.type || 'component'}
        batchId={selectedBatchForModal?.id || ''}
        onIngredientAdded={handleRefreshModalIngredients}
        onDuplicate={handleDuplicateBatch}
      />

      {/* Delete Pan Confirmation Modal */}
      {panToDelete && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1100
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2rem', marginBottom: '12px' }}>🗑</div>
            <h3 style={{ margin: '0 0 12px 0', color: '#333' }}>
              Delete Pan #{panToDelete.panNumber}?
            </h3>
            <p style={{ margin: '0 0 4px 0', color: '#666', fontSize: '0.9rem' }}>
              {panToDelete.productName}
            </p>
            <p style={{ margin: '0 0 20px 0', color: '#999', fontSize: '0.85rem', fontFamily: 'monospace' }}>
              {panToDelete.batchLotNumber}
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={() => setPanToDelete(null)}
                style={{
                  padding: '10px 24px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  backgroundColor: '#f5f5f5',
                  color: '#333',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeletePan}
                style={{
                  padding: '10px 24px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  backgroundColor: '#f44336',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Styles
const styles: { [key: string]: React.CSSProperties } = {
  container: {
    padding: '20px',
    maxWidth: '1200px',
    margin: '0 auto'
  },
  pageTitle: {
    fontSize: '2rem',
    color: '#333',
    marginBottom: '20px'
  },
  teamHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px'
  },
  logoutButton: {
    padding: '10px 20px',
    backgroundColor: '#f44336',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  wizardContainer: {
    border: '2px solid #ddd',
    borderRadius: '12px',
    backgroundColor: 'white',
    minHeight: '400px',
    position: 'relative'
  },
  centeredContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '400px',
    padding: '20px'
  },
  stepContainer: {
    padding: '40px 20px'
  },
  stepContainerScrollable: {
    padding: '40px 20px',
    display: 'flex',
    flexDirection: 'column' as const,
    maxHeight: 'calc(100vh - 120px)',
    overflow: 'hidden',
  },
  scrollableListArea: {
    flex: 1,
    overflowY: 'auto' as const,
    minHeight: 0,
  },
  stepTitle: {
    textAlign: 'center',
    marginBottom: '40px',
    fontSize: '1.5rem',
    color: '#333'
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '24px'
  },
  primaryButton: {
    width: 'calc(100% - 40px)',
    height: 'calc(100% - 40px)',
    minHeight: '360px',
    fontSize: '32px',
    fontWeight: 'bold',
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '16px',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(40, 167, 69, 0.4)',
    transition: 'transform 0.2s, box-shadow 0.2s'
  },
  backButton: {
    padding: '10px 20px',
    fontSize: '16px',
    fontWeight: '500',
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    marginBottom: '20px'
  },
  buttonGrid: {
    display: 'flex',
    justifyContent: 'center',
    gap: '20px',
    flexWrap: 'wrap'
  },
  typeButton: {
    padding: '30px 60px',
    fontSize: '22px',
    fontWeight: 'bold',
    backgroundColor: '#2196F3',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)'
  },
  fillingTypeButton: {
    padding: '30px 60px',
    fontSize: '22px',
    fontWeight: 'bold',
    backgroundColor: '#FF9800',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(255, 152, 0, 0.3)'
  },
  componentGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '16px',
    maxWidth: '900px',
    margin: '0 auto'
  },
  componentButton: {
    padding: '24px',
    fontSize: '18px',
    fontWeight: '600',
    backgroundColor: 'white',
    color: '#333',
    border: '2px solid #ddd',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'border-color 0.2s, background-color 0.2s'
  },
  fillingButton: {
    padding: '24px',
    fontSize: '18px',
    fontWeight: '600',
    backgroundColor: '#fff8f0',
    color: '#333',
    border: '2px solid #FF9800',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'border-color 0.2s, background-color 0.2s'
  },
  emptyMessage: {
    textAlign: 'center',
    color: '#666',
    fontSize: '16px'
  },
  stepSubtitle: {
    textAlign: 'center',
    color: '#666',
    marginBottom: '40px',
    marginTop: '-30px',
    fontSize: '16px'
  },
  ingredientGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: '16px',
    maxWidth: '900px',
    margin: '0 auto'
  },
  ingredientButton: {
    padding: '20px',
    fontSize: '16px',
    fontWeight: '500',
    color: '#333',
    border: '2px solid #ddd',
    borderRadius: '12px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    transition: 'border-color 0.2s, background-color 0.2s'
  },
  ingredientName: {
    flex: 1,
    textAlign: 'left'
  },
  checkmark: {
    color: '#28a745',
    fontSize: '20px',
    fontWeight: 'bold'
  },
  checkmarkBlue: {
    color: '#2196F3',
    fontSize: '20px',
    fontWeight: 'bold'
  },
  fillingIngredientSections: {
    maxWidth: '900px',
    margin: '0 auto'
  },
  ingredientSection: {
    marginBottom: '30px'
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#28a745',
    marginBottom: '16px',
    paddingBottom: '8px',
    borderBottom: '2px solid #28a745'
  },
  sectionTitleBlue: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#2196F3',
    marginBottom: '16px',
    paddingBottom: '8px',
    borderBottom: '2px solid #2196F3'
  },
  continueContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '40px',
    flexShrink: 0,
  },
  continueButton: {
    padding: '20px 60px',
    fontSize: '18px',
    fontWeight: 'bold',
    color: 'white',
    border: 'none',
    borderRadius: '12px'
  },
  formContainer: {
    maxWidth: '400px',
    margin: '0 auto'
  },
  formGroup: {
    marginBottom: '24px'
  },
  formLabel: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '8px'
  },
  formInput: {
    width: '100%',
    padding: '12px 16px',
    fontSize: '16px',
    border: '2px solid #ddd',
    borderRadius: '8px',
    boxSizing: 'border-box' as const
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px'
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #28a745',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },
  loadingText: {
    marginTop: '20px',
    color: '#666',
    fontSize: '16px'
  },
  errorContainer: {
    textAlign: 'center',
    padding: '40px'
  },
  errorText: {
    color: '#dc3545',
    fontSize: '16px'
  },
  emptyContainer: {
    textAlign: 'center',
    padding: '40px'
  },
  traceCodeList: {
    maxWidth: '600px',
    margin: '0 auto',
    border: '1px solid #ddd',
    borderRadius: '12px',
    overflow: 'hidden'
  },
  traceCodeItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '16px 20px',
    cursor: 'pointer',
    borderBottom: '1px solid #eee',
    transition: 'background-color 0.2s'
  },
  checkbox: {
    width: '24px',
    height: '24px',
    marginRight: '16px',
    cursor: 'pointer'
  },
  lotNumber: {
    fontFamily: 'monospace',
    fontSize: '16px',
    flex: 1
  },
  receivingDate: {
    color: '#666',
    fontSize: '14px'
  },
  componentBatchList: {
    maxWidth: '600px',
    margin: '0 auto',
    border: '1px solid #ddd',
    borderRadius: '12px',
    overflow: 'hidden'
  },
  componentBatchItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '16px 20px',
    cursor: 'pointer',
    borderBottom: '1px solid #eee',
    transition: 'background-color 0.2s, border-color 0.2s',
    border: '2px solid transparent',
    margin: '-1px'
  },
  radio: {
    width: '20px',
    height: '20px',
    marginRight: '16px',
    cursor: 'pointer',
    accentColor: '#2196F3'
  },
  componentBatchInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  componentBatchLot: {
    fontFamily: 'monospace',
    fontSize: '16px',
    fontWeight: '600',
    color: '#333'
  },
  componentBatchMeta: {
    fontSize: '14px',
    color: '#666'
  },
  doneButton: {
    padding: '16px 50px',
    fontSize: '18px',
    fontWeight: 'bold',
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer'
  },
  timesProducedContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '40px'
  },
  timesProducedControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  },
  incrementButton: {
    width: '60px',
    height: '60px',
    fontSize: '32px',
    fontWeight: 'bold',
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  timesProducedInput: {
    width: '120px',
    height: '60px',
    fontSize: '32px',
    fontWeight: 'bold',
    textAlign: 'center',
    border: '2px solid #ddd',
    borderRadius: '12px'
  },
  createBatchButton: {
    padding: '24px 80px',
    fontSize: '22px',
    fontWeight: 'bold',
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '16px',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(40, 167, 69, 0.4)'
  },
  errorBanner: {
    backgroundColor: '#ffebee',
    color: '#c62828',
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    maxWidth: '500px',
    margin: '0 auto 30px auto'
  },
  errorDismiss: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '20px',
    color: '#c62828',
    padding: '0 4px'
  },
  spinnerLarge: {
    width: '60px',
    height: '60px',
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #28a745',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },
  creatingText: {
    marginTop: '24px',
    fontSize: '18px',
    color: '#666'
  },
  summaryCard: {
    maxWidth: '500px',
    margin: '0 auto',
    backgroundColor: '#e8f5e9',
    border: '2px solid #28a745',
    borderRadius: '16px',
    padding: '30px'
  },
  summaryIconContainer: {
    textAlign: 'center',
    marginBottom: '20px'
  },
  summaryIcon: {
    fontSize: '48px',
    color: '#28a745'
  },
  summaryTitle: {
    textAlign: 'center',
    marginBottom: '24px',
    fontSize: '1.5rem',
    color: '#333'
  },
  summaryDetails: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '16px'
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 0',
    borderBottom: '1px solid #eee'
  },
  summaryLabel: {
    color: '#666',
    fontWeight: '500'
  },
  summaryValue: {
    color: '#333',
    fontWeight: '600'
  },
  summaryValueMono: {
    color: '#333',
    fontWeight: '600',
    fontFamily: 'monospace'
  },
  createAnotherButton: {
    padding: '20px 60px',
    fontSize: '18px',
    fontWeight: 'bold',
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(40, 167, 69, 0.4)'
  },
  recentBatchesSection: {
    marginTop: '40px'
  },
  recentBatchesTitle: {
    fontSize: '1.5rem',
    color: '#2196F3',
    marginBottom: '0'
  },
  recentBatchesTitleOrange: {
    fontSize: '1.5rem',
    color: '#FF9800',
    marginBottom: '0'
  },
  collapsibleHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
    padding: '10px 0',
    marginBottom: '10px',
    borderRadius: '4px',
    transition: 'background-color 0.2s'
  } as React.CSSProperties,
  chevron: {
    fontSize: '1rem',
    transition: 'transform 0.2s',
    color: '#666'
  } as React.CSSProperties,
  recentBatchesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '16px'
  },
  batchCard: {
    border: '1px solid #ddd',
    borderLeft: '4px solid #2196F3',
    borderRadius: '8px',
    padding: '16px',
    backgroundColor: 'white'
  },
  fillingBatchCard: {
    border: '1px solid #ddd',
    borderLeft: '4px solid #FF9800',
    borderRadius: '8px',
    padding: '16px',
    backgroundColor: 'white'
  },
  batchCardTitle: {
    margin: '0 0 12px 0',
    fontSize: '16px',
    fontWeight: '600',
    color: '#333'
  },
  batchCardDetail: {
    margin: '6px 0',
    fontSize: '14px',
    color: '#666',
    display: 'flex',
    gap: '8px'
  },
  batchCardLabel: {
    color: '#999'
  },
  batchCardValueMono: {
    fontFamily: 'monospace'
  },
  // Dough-themed styles
  doughTypeButton: {
    padding: '30px 60px',
    fontSize: '22px',
    fontWeight: 'bold',
    backgroundColor: '#8B4513',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(139, 69, 19, 0.3)'
  },
  doughButton: {
    padding: '24px',
    fontSize: '18px',
    fontWeight: '600',
    backgroundColor: '#faf5f0',
    color: '#333',
    border: '2px solid #8B4513',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'border-color 0.2s, background-color 0.2s'
  },
  doughIngredientButton: {
    padding: '20px',
    fontSize: '16px',
    fontWeight: '500',
    color: '#333',
    border: '2px solid #ddd',
    borderRadius: '12px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    transition: 'border-color 0.2s, background-color 0.2s'
  },
  ingredientQuantity: {
    fontSize: '14px',
    color: '#666',
    fontWeight: '400'
  },
  checkmarkBrown: {
    color: '#8B4513',
    fontSize: '20px',
    fontWeight: 'bold'
  },
  recentBatchesTitleBrown: {
    fontSize: '1.5rem',
    color: '#8B4513',
    marginBottom: '0'
  },
  doughBatchCard: {
    border: '1px solid #ddd',
    borderLeft: '4px solid #8B4513',
    borderRadius: '8px',
    padding: '16px',
    backgroundColor: 'white'
  },
  // Product-themed styles
  productTypeButton: {
    padding: '30px 60px',
    fontSize: '22px',
    fontWeight: 'bold',
    backgroundColor: '#9C27B0',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(156, 39, 176, 0.3)'
  },
  productButton: {
    padding: '24px',
    fontSize: '18px',
    fontWeight: '600',
    backgroundColor: '#faf5fc',
    color: '#333',
    border: '2px solid #9C27B0',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'border-color 0.2s, background-color 0.2s'
  },
  batchSelectionList: {
    maxWidth: '700px',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  batchSelectionItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '16px 20px',
    cursor: 'pointer',
    border: '2px solid #ddd',
    borderRadius: '12px',
    transition: 'background-color 0.2s, border-color 0.2s'
  },
  batchSelectionInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    flex: 1
  },
  batchSelectionName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#333'
  },
  batchSelectionLot: {
    fontFamily: 'monospace',
    fontSize: '14px',
    color: '#666'
  },
  batchSelectionDate: {
    fontSize: '13px',
    color: '#999'
  },
  recentBatchesTitlePurple: {
    fontSize: '1.5rem',
    color: '#9C27B0',
    marginBottom: '0'
  },
  productBatchCard: {
    border: '1px solid #ddd',
    borderLeft: '4px solid #9C27B0',
    borderRadius: '8px',
    padding: '16px',
    backgroundColor: 'white'
  },
  // Colored card wrappers for Recent Batches sections
  recentBatchesCardCyan: {
    backgroundColor: '#2196F3',
    borderRadius: '12px',
    padding: '20px',
    marginTop: '40px'
  },
  recentBatchesCardOrange: {
    backgroundColor: '#FF9800',
    borderRadius: '12px',
    padding: '20px',
    marginTop: '20px'
  },
  recentBatchesCardBrown: {
    backgroundColor: '#8B4513',
    borderRadius: '12px',
    padding: '20px',
    marginTop: '20px'
  },
  recentBatchesCardPurple: {
    backgroundColor: '#9C27B0',
    borderRadius: '12px',
    padding: '20px',
    marginTop: '20px'
  },
  recentBatchesTitleWhite: {
    fontSize: '1.5rem',
    color: 'white',
    marginBottom: '0'
  },
  chevronWhite: {
    fontSize: '1rem',
    transition: 'transform 0.2s',
    color: 'white'
  },
  emptyMessageWhite: {
    textAlign: 'center',
    color: 'white',
    fontSize: '16px'
  },
  loadingTextWhite: {
    marginTop: '20px',
    color: 'white',
    fontSize: '16px'
  },
  // Shipment wizard styles
  shipmentMainButton: {
    width: 'calc(100% - 40px)',
    height: 'calc(100% - 40px)',
    minHeight: '360px',
    fontSize: '32px',
    fontWeight: 'bold',
    backgroundColor: '#009688',
    color: 'white',
    border: 'none',
    borderRadius: '16px',
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
    boxShadow: '0 4px 12px rgba(0, 150, 136, 0.4)'
  },
  shipmentPrimaryButton: {
    padding: '30px 60px',
    fontSize: '1.5rem',
    fontWeight: 'bold',
    backgroundColor: '#009688',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    boxShadow: '0 4px 6px rgba(0, 150, 136, 0.3)'
  },
  shipmentSupplierButton: {
    padding: '20px',
    fontSize: '1.1rem',
    fontWeight: '500',
    backgroundColor: 'white',
    color: '#333',
    border: '2px solid #009688',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    position: 'relative' as const,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px'
  },
  shipmentSearchContainer: {
    marginBottom: '20px',
    display: 'flex',
    justifyContent: 'center'
  },
  shipmentSearchInput: {
    padding: '12px 20px',
    fontSize: '1rem',
    border: '2px solid #ddd',
    borderRadius: '8px',
    width: '100%',
    maxWidth: '400px',
    outline: 'none',
    transition: 'border-color 0.2s'
  },
  shipmentSubtitle: {
    fontSize: '1rem',
    color: '#666',
    marginTop: '-10px',
    marginBottom: '20px',
    textAlign: 'center' as const
  },
  shipmentExistingLots: {
    marginBottom: '20px',
    padding: '15px',
    backgroundColor: '#f9f9f9',
    borderRadius: '8px'
  },
  shipmentExistingLotsTitle: {
    fontSize: '0.9rem',
    fontWeight: '600',
    color: '#333',
    marginBottom: '10px'
  },
  shipmentLotChipsContainer: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '8px'
  },
  shipmentLotChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    backgroundColor: '#009688',
    color: 'white',
    borderRadius: '20px',
    fontSize: '0.9rem'
  },
  shipmentLotRemoveButton: {
    background: 'none',
    border: 'none',
    color: 'white',
    fontSize: '1.2rem',
    cursor: 'pointer',
    padding: '0',
    lineHeight: '1',
    opacity: 0.8
  },
  shipmentFormContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '15px',
    maxWidth: '500px',
    margin: '0 auto 20px'
  },
  shipmentFormRow: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '5px'
  },
  shipmentFormRowInline: {
    display: 'flex',
    gap: '10px',
    alignItems: 'center'
  },
  shipmentFormLabel: {
    fontSize: '0.9rem',
    fontWeight: '500',
    color: '#333'
  },
  shipmentFormInput: {
    padding: '10px 15px',
    fontSize: '1rem',
    border: '2px solid #ddd',
    borderRadius: '6px',
    outline: 'none',
    transition: 'border-color 0.2s'
  },
  shipmentFormSelect: {
    padding: '10px 15px',
    fontSize: '1rem',
    border: '2px solid #ddd',
    borderRadius: '6px',
    outline: 'none',
    backgroundColor: 'white',
    cursor: 'pointer',
    minWidth: '100px'
  },
  shipmentTempToggle: {
    display: 'flex',
    border: '2px solid #009688',
    borderRadius: '6px',
    overflow: 'hidden'
  },
  shipmentTempButton: {
    padding: '10px 15px',
    fontSize: '1rem',
    border: 'none',
    backgroundColor: 'white',
    color: '#009688',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  shipmentTempButtonActive: {
    backgroundColor: '#009688',
    color: 'white'
  },
  shipmentButtonRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: '15px',
    marginTop: '20px'
  },
  shipmentAddButton: {
    padding: '15px 30px',
    fontSize: '1.1rem',
    fontWeight: 'bold',
    backgroundColor: 'white',
    color: '#009688',
    border: '2px solid #009688',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  // Single-field step styles
  shipmentSingleFieldContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '15px',
    padding: '30px 20px',
    maxWidth: '400px',
    margin: '0 auto'
  },
  shipmentFieldLabel: {
    fontSize: '1rem',
    color: '#666',
    textAlign: 'center' as const
  },
  shipmentLargeInput: {
    padding: '20px',
    fontSize: '1.5rem',
    border: '3px solid #009688',
    borderRadius: '12px',
    width: '100%',
    textAlign: 'center' as const,
    outline: 'none'
  },
  shipmentLargeSelect: {
    padding: '20px',
    fontSize: '1.5rem',
    border: '3px solid #009688',
    borderRadius: '12px',
    backgroundColor: 'white',
    cursor: 'pointer',
    minWidth: '120px'
  },
  shipmentQuantityRow: {
    display: 'flex',
    gap: '15px',
    alignItems: 'center',
    width: '100%'
  },
  shipmentTemperatureRow: {
    display: 'flex',
    gap: '15px',
    alignItems: 'center',
    width: '100%'
  },
  shipmentTempToggleLarge: {
    display: 'flex',
    border: '3px solid #009688',
    borderRadius: '12px',
    overflow: 'hidden'
  },
  shipmentTempButtonLarge: {
    padding: '20px 25px',
    fontSize: '1.5rem',
    border: 'none',
    backgroundColor: 'white',
    color: '#009688',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  shipmentConditionGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '15px',
    maxWidth: '400px',
    margin: '0 auto',
    padding: '20px'
  },
  shipmentConditionButton: {
    padding: '25px 20px',
    fontSize: '1.2rem',
    fontWeight: '500',
    backgroundColor: 'white',
    color: '#333',
    border: '3px solid #009688',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  shipmentConditionButtonActive: {
    backgroundColor: '#009688',
    color: 'white'
  },
  shipmentSecondaryButton: {
    padding: '15px 30px',
    fontSize: '1.1rem',
    fontWeight: 'bold',
    backgroundColor: 'white',
    color: '#009688',
    border: '2px solid #009688',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  shipmentSummaryCard: {
    backgroundColor: 'white',
    borderRadius: '16px',
    padding: '40px',
    textAlign: 'center' as const,
    boxShadow: '0 4px 12px rgba(0, 150, 136, 0.2)',
    border: '2px solid #009688',
    maxWidth: '400px'
  },
  shipmentSummaryIcon: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    backgroundColor: '#009688',
    color: 'white',
    fontSize: '3rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px'
  },
  shipmentSummaryTitle: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#009688',
    marginBottom: '10px'
  },
  shipmentSummaryText: {
    fontSize: '1.1rem',
    color: '#666',
    marginBottom: '30px'
  },
  // Delivery wizard styles
  deliveryMainButton: {
    width: 'calc(100% - 40px)',
    height: 'calc(100% - 40px)',
    minHeight: '360px',
    fontSize: '32px',
    fontWeight: 'bold',
    backgroundColor: '#E91E63',
    color: 'white',
    border: 'none',
    borderRadius: '16px',
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
    boxShadow: '0 4px 12px rgba(233, 30, 99, 0.4)'
  },
  deliveryPrimaryButton: {
    padding: '15px 40px',
    fontSize: '1.1rem',
    fontWeight: 'bold',
    backgroundColor: '#E91E63',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  deliverySecondaryButton: {
    padding: '15px 30px',
    fontSize: '1.1rem',
    fontWeight: 'bold',
    backgroundColor: 'white',
    color: '#E91E63',
    border: '2px solid #E91E63',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  deliveryProductButton: {
    padding: '30px 40px',
    fontSize: '18px',
    fontWeight: 'bold',
    backgroundColor: '#FCE4EC',
    color: '#C2185B',
    border: '3px solid #E91E63',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    minWidth: '150px'
  },
  deliveryBatchButton: {
    display: 'flex',
    alignItems: 'center',
    padding: '16px 20px',
    backgroundColor: 'white',
    border: '2px solid #E91E63',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    width: '100%',
    textAlign: 'left' as const
  },
  deliveryReviewSection: {
    backgroundColor: '#FCE4EC',
    padding: '20px',
    borderRadius: '12px',
    marginBottom: '24px'
  },
  deliveryReviewInfo: {
    marginBottom: '8px',
    fontSize: '1rem',
    color: '#333'
  },
  deliveryReviewSubtitle: {
    fontSize: '1.2rem',
    fontWeight: 'bold',
    color: '#C2185B',
    marginBottom: '16px'
  },
  deliveryLineItemsList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px',
    marginBottom: '24px'
  },
  deliveryLineItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px',
    backgroundColor: 'white',
    border: '2px solid #E91E63',
    borderRadius: '8px'
  },
  deliveryLineItemInfo: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px'
  },
  deliveryLineItemName: {
    fontWeight: 'bold',
    color: '#C2185B'
  },
  deliveryLineItemBatch: {
    fontSize: '0.9rem',
    color: '#666'
  },
  deliveryLineItemQty: {
    fontSize: '0.9rem',
    color: '#333'
  },
  deliveryRemoveButton: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: '#ffebee',
    color: '#E91E63',
    border: 'none',
    fontSize: '1.5rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1
  },
  deliveryReviewButtons: {
    display: 'flex',
    gap: '16px',
    justifyContent: 'center'
  },
  deliveryTempToggle: {
    display: 'flex',
    border: '2px solid #E91E63',
    borderRadius: '8px',
    overflow: 'hidden'
  },
  deliveryTempButton: {
    padding: '12px 16px',
    fontSize: '1rem',
    border: 'none',
    backgroundColor: 'white',
    color: '#E91E63',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  deliveryTempButtonActive: {
    backgroundColor: '#E91E63',
    color: 'white'
  },
  // Temperature Log wizard styles
  tempLogMainButton: {
    width: 'calc(100% - 40px)',
    height: 'calc(100% - 40px)',
    minHeight: '360px',
    fontSize: '32px',
    fontWeight: 'bold',
    backgroundColor: '#03A9F4',
    color: 'white',
    border: 'none',
    borderRadius: '16px',
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
    boxShadow: '0 4px 12px rgba(3, 169, 244, 0.4)'
  },
  tempLogTypeButton: {
    padding: '40px 30px',
    fontSize: '20px',
    fontWeight: 'bold',
    backgroundColor: '#E1F5FE',
    color: '#0277BD',
    border: '3px solid #03A9F4',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '10px',
    minWidth: '150px'
  },
  tempLogTypeIcon: {
    fontSize: '36px'
  },
  tempLogEquipmentButton: {
    padding: '20px',
    backgroundColor: '#E1F5FE',
    color: '#0277BD',
    border: '3px solid #03A9F4',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    textAlign: 'left' as const,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px'
  },
  tempLogEquipmentName: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#0277BD'
  },
  tempLogEquipmentLocation: {
    fontSize: '14px',
    color: '#666'
  },
  tempLogEquipmentRange: {
    fontSize: '12px',
    color: '#888',
    marginTop: '4px'
  },
  tempLogInfoCard: {
    backgroundColor: '#E1F5FE',
    padding: '20px',
    borderRadius: '12px',
    marginBottom: '24px'
  },
  tempLogInfoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '8px',
    fontSize: '1rem'
  },
  tempLogInfoLabel: {
    fontWeight: '600',
    color: '#0277BD'
  },
  tempLogInfoValue: {
    color: '#333'
  },
  tempLogWarning: {
    color: '#f44336',
    fontSize: '0.9rem',
    marginTop: '8px'
  },
  tempLogPrimaryButton: {
    padding: '15px 40px',
    fontSize: '1.1rem',
    fontWeight: 'bold',
    backgroundColor: '#03A9F4',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  tempLogSecondaryButton: {
    padding: '15px 30px',
    fontSize: '1.1rem',
    fontWeight: 'bold',
    backgroundColor: '#E1F5FE',
    color: '#0277BD',
    border: '2px solid #03A9F4',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  tempLogOutlineButton: {
    padding: '15px 30px',
    fontSize: '1.1rem',
    fontWeight: 'bold',
    backgroundColor: 'white',
    color: '#03A9F4',
    border: '2px solid #03A9F4',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  tempLogSummaryCard: {
    backgroundColor: '#E1F5FE',
    padding: '30px',
    borderRadius: '16px',
    textAlign: 'center' as const,
    marginBottom: '24px'
  },
  tempLogSummaryIcon: {
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    backgroundColor: '#03A9F4',
    color: 'white',
    fontSize: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px'
  },
  tempLogSummaryTitle: {
    fontSize: '1.5rem',
    color: '#0277BD',
    marginBottom: '20px'
  },
  tempLogSummaryDetails: {
    textAlign: 'left' as const,
    maxWidth: '400px',
    margin: '0 auto'
  },
  tempLogSummaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '8px',
    fontSize: '1rem',
    padding: '8px 0',
    borderBottom: '1px solid rgba(3, 169, 244, 0.2)'
  },
  tempLogSummaryLabel: {
    fontWeight: '600',
    color: '#0277BD'
  },
  tempLogSummaryButtons: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px',
    alignItems: 'center'
  },
  // Sanitation Cleaning wizard styles
  sanitationMainButton: {
    width: 'calc(100% - 40px)',
    height: 'calc(100% - 40px)',
    minHeight: '360px',
    fontSize: '32px',
    fontWeight: 'bold',
    backgroundColor: '#4CAF50',
    color: 'white',
    border: 'none',
    borderRadius: '16px',
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
    boxShadow: '0 4px 12px rgba(76, 175, 80, 0.4)'
  },
  sanitationFrequencyButton: {
    padding: '40px 30px',
    fontSize: '20px',
    fontWeight: 'bold',
    backgroundColor: '#E8F5E9',
    color: '#2E7D32',
    border: '3px solid #4CAF50',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '10px',
    minWidth: '150px'
  },
  sanitationFrequencyIcon: {
    fontSize: '36px'
  },
  sanitationEquipmentGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '12px',
    marginBottom: '24px'
  },
  sanitationEquipmentButton: {
    padding: '16px',
    backgroundColor: '#E8F5E9',
    color: '#2E7D32',
    border: '3px solid #4CAF50',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    textAlign: 'left' as const,
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  sanitationEquipmentButtonSelected: {
    backgroundColor: '#4CAF50',
    color: 'white'
  },
  sanitationCheckbox: {
    width: '24px',
    height: '24px',
    borderRadius: '4px',
    border: '2px solid #4CAF50',
    backgroundColor: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    fontWeight: 'bold',
    flexShrink: 0
  },
  sanitationCheckboxSelected: {
    backgroundColor: '#2E7D32',
    color: 'white',
    border: '2px solid #2E7D32'
  },
  sanitationEquipmentName: {
    fontSize: '16px',
    fontWeight: 'bold'
  },
  sanitationReviewCard: {
    backgroundColor: '#E8F5E9',
    padding: '24px',
    borderRadius: '12px',
    marginBottom: '24px'
  },
  sanitationReviewRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '12px',
    fontSize: '1rem'
  },
  sanitationReviewLabel: {
    fontWeight: '600',
    color: '#2E7D32'
  },
  sanitationReviewValue: {
    color: '#333'
  },
  sanitationEquipmentList: {
    marginTop: '8px',
    paddingLeft: '16px'
  },
  sanitationEquipmentListItem: {
    fontSize: '1rem',
    color: '#333',
    marginBottom: '4px'
  },
  sanitationPrimaryButton: {
    padding: '15px 40px',
    fontSize: '1.1rem',
    fontWeight: 'bold',
    backgroundColor: '#4CAF50',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  sanitationOutlineButton: {
    padding: '15px 30px',
    fontSize: '1.1rem',
    fontWeight: 'bold',
    backgroundColor: 'white',
    color: '#4CAF50',
    border: '2px solid #4CAF50',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  sanitationSummaryCard: {
    backgroundColor: '#E8F5E9',
    padding: '30px',
    borderRadius: '16px',
    textAlign: 'center' as const,
    marginBottom: '24px'
  },
  sanitationSummaryIcon: {
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    backgroundColor: '#4CAF50',
    color: 'white',
    fontSize: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px'
  },
  sanitationSummaryTitle: {
    fontSize: '1.5rem',
    color: '#2E7D32',
    marginBottom: '20px'
  },
  sanitationSummaryDetails: {
    textAlign: 'left' as const,
    maxWidth: '400px',
    margin: '0 auto'
  },
  sanitationSummaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '8px',
    fontSize: '1rem',
    padding: '8px 0',
    borderBottom: '1px solid rgba(76, 175, 80, 0.2)'
  },
  sanitationSummaryLabel: {
    fontWeight: '600',
    color: '#2E7D32'
  },
  sanitationSummaryButtons: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px',
    alignItems: 'center'
  }
};
